/**
 * PromiseToPayModal - Create and send Promise to Pay contracts
 * Two contract types:
 * 1. Promissory Note (Hard Money Loan) - Lender signs first, then borrower
 * 2. Credit Repair Contract
 */
/* eslint-disable */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Send, FileText, Clock, CheckCircle, Check, Eye, Trash2, Mail, DollarSign, CreditCard, ArrowLeft, Pen, ChevronDown, ChevronUp, User, Users, Download, Activity, Receipt, AlertTriangle, Copy, Link, RefreshCw, Archive, Calendar, Shield, Phone, RotateCcw, Search, List, LayoutGrid, CalendarClock, Bell, BellRing, CalendarDays, Edit2 } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import PromisedPaymentsPanel from './PromisedPaymentsPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// Saved contacts for quick selection
const SAVED_BORROWERS = [
  { id: 'norma', name: 'Norma Ramirez', email: 'norma.ramirez951@yahoo.com' },
  { id: 'nicholas', name: 'Nicholas Sibley', email: 'clearallcreditrepair@gmail.com' },
  { id: 'kendra', name: 'Kendra Sibley', email: 'clearallcreditrepair@gmail.com' },
];

const SAVED_LENDERS = [
  { id: 'clearall', name: 'Clear All Credit Repair', email: 'clearallcreditrepair@gmail.com' },
  { id: 'kendra-nicholas', name: 'Kendra & Nicholas Sibley', email: 'clearallcreditrepair@gmail.com' },
  { id: 'kendra-lender', name: 'Kendra Sibley', email: 'clearallcreditrepair@gmail.com' },
  { id: 'nicholas-lender', name: 'Nicholas Sibley', email: 'clearallcreditrepair@gmail.com' },
];

// Cursive signature font styles
const SIGNATURE_STYLES = [
  { id: 'style1', name: 'Elegant', fontFamily: "'Dancing Script', cursive", fontSize: '32px' },
  { id: 'style2', name: 'Classic', fontFamily: "'Great Vibes', cursive", fontSize: '34px' },
  { id: 'style3', name: 'Modern', fontFamily: "'Pacifico', cursive", fontSize: '28px' },
  { id: 'style4', name: 'Formal', fontFamily: "'Allura', cursive", fontSize: '36px' },
  { id: 'style5', name: 'Bold', fontFamily: "'Sacramento', cursive", fontSize: '38px' },
];

// ── Lender-based color stripe (Feb 2026) ─────────────────────────────────
// Every loan card in the Promissory Notes list gets a colored 6px left-edge
// stripe based on the lender_name so the user can visually tell whose loan
// is whose at a glance without reading the lender field.  The palette is
// intentionally saturated + accessible-contrast against the dark card bg.
const _LENDER_PALETTE = [
  { key: "emerald", color: "#10b981" },
  { key: "sky",     color: "#0ea5e9" },
  { key: "violet",  color: "#8b5cf6" },
  { key: "amber",   color: "#f59e0b" },
  { key: "rose",    color: "#f43f5e" },
  { key: "cyan",    color: "#06b6d4" },
  { key: "fuchsia", color: "#d946ef" },
  { key: "lime",    color: "#84cc16" },
  { key: "orange",  color: "#fb7185" },
  { key: "teal",    color: "#14b8a6" },
];
function lenderColorStripe(lenderName) {
  const name = String(lenderName || "").trim().toLowerCase();
  if (!name) return { key: "zinc", color: "#52525b" };
  // Deterministic hash so the same lender always gets the same swatch.
  let h = 0;
  for (let i = 0; i < name.length; i++) h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  return _LENDER_PALETTE[Math.abs(h) % _LENDER_PALETTE.length];
}

// 2026-02-08 — Live "Next $200 rolls over in Xh Ym" chip. Defined at
// module scope (NOT inside the parent) so React sees a stable component
// identity across parent re-renders — otherwise selecting a client in
// the Email Active Loans picker would remount the chip mid-render and
// blank the whole modal.
function PenaltyCountdown({ iso }) {
  const [, forceTick] = React.useState(0);
  React.useEffect(() => {
    if (!iso) return undefined;
    const id = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, [iso]);
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const now = Date.now();
  const secs = Math.max(0, Math.floor((target - now) / 1000));
  if (secs <= 60) {
    return (
      <span className="text-amber-300 font-mono text-[10px]" data-testid="penalty-countdown">
        rolls over any second
      </span>
    );
  }
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return (
    <span
      className="text-amber-300/90 font-mono text-[10px] whitespace-nowrap"
      data-testid="penalty-countdown"
      title="Next $200 late fee rolls over at this loan's 11:59 PM Pacific."
    >
      · next $200 in {parts.join(' ')}
    </span>
  );
}

export default function PromiseToPayModal({ isOpen, onClose }) {
  // 2026-02-24 — Late-payment penalty rate is now selectable ($100 OR $200
  // per calendar day).  The clause text is generated dynamically so the
  // signed PDF, the additional_terms field, and the running-tally UI all
  // stay in lockstep with the rate the user picked.
  const buildLatePenaltyClause = (perDay = 200) => (
    `LATE PAYMENT PENALTY: If full payment is not received by the Repayment Due Date, a daily late fee of $${Number(perDay).toFixed(2)} will accrue and be added to the outstanding balance for each calendar day the payment remains unpaid, and shall continue to compound daily until the loan is paid in full, including all accrued penalties.`
  );
  const LATE_PENALTY_CLAUSE = buildLatePenaltyClause(200);

  const appendLatePenalty = (existing, perDay = 200) => {
    const current = (existing || '').trim();
    const stripped = current.replace(/LATE PAYMENT PENALTY:[^]*?paid in full, including all accrued penalties\./g, '').trim();
    const clause = buildLatePenaltyClause(perDay);
    return stripped ? `${stripped}\n\n${clause}` : clause;
  };

  const [activeTab, setActiveTab] = useState('select'); // 'select', 'promissory', 'credit-repair', 'promissory-logs', 'credit-repair-logs', 'trash'
  const [isSending, setIsSending] = useState(false);
  const [isSendingReceipt, setIsSendingReceipt] = useState(false);
  const [contractSent, setContractSent] = useState(false);  // Track if contract was sent
  const [receiptSent, setReceiptSent] = useState(false);    // Track if receipt was sent
  const [signingLink, setSigningLink] = useState('');       // Store signing link to copy
  const [linkCopied, setLinkCopied] = useState(false);      // Track if link was copied
  const [contracts, setContracts] = useState([]);
  const [trashedContracts, setTrashedContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  // Signature style selection (replacing canvas)
  const [selectedSignatureStyle, setSelectedSignatureStyle] = useState('style1');
  const hasLenderSignature = selectedSignatureStyle !== null;

  // Dropdown states
  const [showBorrowerDropdown, setShowBorrowerDropdown] = useState(false);
  const [showLenderDropdown, setShowLenderDropdown] = useState(false);

  // Payment Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMethod: '',
    notes: ''
  });
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);

  // Loan Log state
  const [loans, setLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  
  // Loan Rollover state
  const [showRolloverModal, setShowRolloverModal] = useState(false);
  const [rolloverForm, setRolloverForm] = useState({
    paymentBeforeRollover: '',  // Amount paid before rolling over
    rolloverAmount: '',  // How much of the loan to roll over
    newBorrowerName: '',
    newBorrowerEmail: '',
    newBorrowerPhone: '',
    additionalAmount: '',
    newAmountReturn: '',
    newDateReturn: '',
    newDateLent: '',
    interestRate: '',
    notes: '',
    sendContract: true  // Send contract to borrower after creation
  });
  const [isProcessingRollover, setIsProcessingRollover] = useState(false);
  
  // Mark Paid in Full state
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  // Per-row "Mark paid in full" workflow (per user request 2026-02-29):
  //   1. Click the green "Paid in Full" button next to the red $ amount
  //   2. Backend zeros the balance + emails the client a paid-in-full receipt
  //   3. Confirmation modal asks "Also delete this client now?"
  //   4. If yes → soft-delete (moves to trash)
  const [markingPaidReminderId, setMarkingPaidReminderId] = useState(null);
  const [paidInFullConfirmDelete, setPaidInFullConfirmDelete] = useState(null); // reminder object after PIF

  // Missed Payment state
  const [showMissedPaymentModal, setShowMissedPaymentModal] = useState(false);
  const [missedPaymentForm, setMissedPaymentForm] = useState({
    interestAmount: '',
    newDateReturn: '',
    notes: ''
  });
  const [isProcessingMissedPayment, setIsProcessingMissedPayment] = useState(false);

  // Loan Ledger state
  // Sort loans by NEXT payment date — uses expected_payment_date if the user
  // set one (e.g. when negotiating a new pay date with a borrower in penalty),
  // otherwise falls back to date_return. Per user request 2026-02-24.
  const sortLoansByNextPayment = (list) => {
    return [...list].sort((a, b) => {
      const nextA = a.expected_payment_date || a.date_return;
      const nextB = b.expected_payment_date || b.date_return;
      const ta = nextA ? new Date(nextA).getTime() : Infinity;
      const tb = nextB ? new Date(nextB).getTime() : Infinity;
      const tav = Number.isNaN(ta) ? Infinity : ta;
      const tbv = Number.isNaN(tb) ? Infinity : tb;
      if (tav !== tbv) return tav - tbv;
      const ra = Number(a.amount_return ?? a.amount_lent ?? 0) || 0;
      const rb = Number(b.amount_return ?? b.amount_lent ?? 0) || 0;
      return rb - ra;
    });
  };

  const [ledgerData, setLedgerData] = useState(null);
  // Loan ledger view mode — per user request 2026-02-24:
  //  • 'detailed' (default): full card per loan (current view)
  //  • 'compact': one row per loan (single line across) for at-a-glance scanning
  //  • 'calendar': month grid keyed by next payment date
  const [loanLedgerView, setLoanLedgerView] = useState(() => localStorage.getItem('p2p_loan_view') || 'detailed');
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  // Per user request 2026-02-26: track the currently-selected day on the
  // calendar so we can show full loan details below the grid. Defaults to
  // today on first render.
  const [calendarSelectedDay, setCalendarSelectedDay] = useState(() => new Date().toISOString().slice(0, 10));
  // Per user request 2026-02-27: tapping a day pops a fullscreen modal with
  // every loan due that day (so they don't have to scroll the page).
  const [calendarDayModal, setCalendarDayModal] = useState(null); // ISO date string or null
  // Per user request 2026-02-27: real calendar with Month + Week views so they
  // can see every loan coming up that week with full details inline.
  const [calendarMode, setCalendarMode] = useState(() => localStorage.getItem('p2p_cal_mode') || 'month'); // 'month' | 'week'
  const [calendarWeekAnchor, setCalendarWeekAnchor] = useState(() => {
    const d = new Date();
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() - d.getDay()); // Sunday of current week
    return d;
  });
  // Persist the chosen view across modal opens
  useEffect(() => { localStorage.setItem('p2p_loan_view', loanLedgerView); }, [loanLedgerView]);
  useEffect(() => { localStorage.setItem('p2p_cal_mode', calendarMode); }, [calendarMode]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [isSavingLedger, setIsSavingLedger] = useState(false);
  const [showAddLoanModal, setShowAddLoanModal] = useState(false);
  const [showClosedLoans, setShowClosedLoans] = useState(false);
  const [selectedLoansForDelete, setSelectedLoansForDelete] = useState([]);
  const [isMultiDeleteMode, setIsMultiDeleteMode] = useState(false);

  // Email Active Loans state (Feb 2026)
  const [showEmailActiveLoansModal, setShowEmailActiveLoansModal] = useState(false);
  // Per-loan "promise to pay" dates keyed by loan_number (YYYY-MM-DD).
  // When set, the backend computes the projected $200/day penalty for that
  // loan up to the chosen date and includes it in the email summary.
  const [emailActivePromiseDates, setEmailActivePromiseDates] = useState({});
  const [actionMenuOpen, setActionMenuOpen] = useState(null); // reminder.id whose action menu is open
  const [reminderFrequencySubmenuOpen, setReminderFrequencySubmenuOpen] = useState(null); // reminder.id whose frequency submenu is expanded
  // Per-card expansion for the new "Promised Payments" panel — keyed by reminder.id
  const [paymentPlanOpen, setPaymentPlanOpen] = useState({});
  // Close the Actions dropdown reliably when the user clicks outside of it.
  // Per user report 2026-06-05 & 2026-06-12: dropdown intermittently failed
  // to open on production. Root causes were:
  //   • mousedown listener firing BEFORE the toggle's click handler could
  //     register, sometimes closing the menu in the same tick it opened
  //   • absent type="button" allowed accidental form submission
  //   • bubble-up from the dropdown body re-triggered the outside-click
  // Fix: bind on `click` (post-bubble), require both the toggle and the
  // menu body to short-circuit it via stopPropagation, and add Escape.
  useEffect(() => {
    if (!actionMenuOpen) return undefined;
    const handler = (e) => {
      const tgt = e.target;
      if (tgt && tgt.closest && tgt.closest(`[data-testid="actions-menu-${actionMenuOpen}"], [data-testid="actions-toggle-${actionMenuOpen}"]`)) {
        return; // click was inside the menu or on its toggle — keep open
      }
      setActionMenuOpen(null);
      setReminderFrequencySubmenuOpen(null);
    };
    const escHandler = (e) => { if (e.key === "Escape") { setActionMenuOpen(null); setReminderFrequencySubmenuOpen(null); } };
    document.addEventListener("click", handler);
    document.addEventListener("touchend", handler);
    document.addEventListener("keydown", escHandler);
    return () => {
      document.removeEventListener("click", handler);
      document.removeEventListener("touchend", handler);
      document.removeEventListener("keydown", escHandler);
    };
  }, [actionMenuOpen]);
  const [activeLoanClients, setActiveLoanClients] = useState([]);
  const [loadingActiveLoanClients, setLoadingActiveLoanClients] = useState(false);
  const [selectedClientEmail, setSelectedClientEmail] = useState('');
  const [selectedLoanNumbers, setSelectedLoanNumbers] = useState([]); // empty = all for that client
  const [emailActiveNote, setEmailActiveNote] = useState('');
  const [sendingActiveLoansEmail, setSendingActiveLoansEmail] = useState(false);
  const [manualLoanForm, setManualLoanForm] = useState({
    borrowerName: '',
    borrowerEmail: '',
    amountLent: '',
    amountReturn: '',
    dateLent: '',
    dateReturn: '',
    notes: ''
  });

  // Promissory Note Form
  const [promissoryForm, setPromissoryForm] = useState({
    borrowerName: '',
    borrowerEmail: '',
    lenderName: 'Clear All Credit Repair',
    lenderEmail: '',
    dateLent: '',
    dateReturn: '',
    amountLent: '',
    amountReturn: '',
    additionalTerms: '',
    // 2026-02-24 — Late-penalty rate is per-loan selectable ($100 or $200
    // per day).  Defaults to 200 so historical behaviour is preserved
    // when the user just clicks the amber button once.
    latePenaltyPerDay: 200,
  });

  // Credit Repair Contract Form
  const [creditRepairForm, setCreditRepairForm] = useState({
    clientName: '',
    clientEmail: '',
    downPayment: '',
    remainingBalance: '',
    totalAmount: '',
    paymentDueDate: '',
    additionalTerms: '',
    paymentMethod: ''  // For receipt
  });

  // Credit Payment Plan Form
  const [paymentPlanForm, setPaymentPlanForm] = useState({
    clientName: '',
    clientEmail: '',
    totalBalance: '',
    downPayment: '',
    paymentAmount: '',
    paymentFrequency: 'biweekly', // weekly, biweekly, monthly
    startDate: '',
    notes: ''
  });
  
  // Request Payment Form (one-time)
  const [requestPaymentForm, setRequestPaymentForm] = useState({
    clientName: '',
    clientEmail: '',
    amount: '',
    dueDate: '',
    notes: '',
    reminderFrequency: 'once' // once, weekly, biweekly, monthly
  });
  const [isCreatingRequest, setIsCreatingRequest] = useState(false);
  
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [loadingPaymentPlans, setLoadingPaymentPlans] = useState(false);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState(null);
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [aiScheduleInput, setAiScheduleInput] = useState('');
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [sendingInstantReminder, setSendingInstantReminder] = useState(null);
  const [loggingPayment, setLoggingPayment] = useState(null);
  const [paymentLogAmount, setPaymentLogAmount] = useState('');
  const [copiedLink, setCopiedLink] = useState(null);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(null);
  const [paymentConfirmData, setPaymentConfirmData] = useState(null);
  const [nextPaymentDate, setNextPaymentDate] = useState('');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [showAddClientMenu, setShowAddClientMenu] = useState(false);

  // Quick Reminder state
  const [quickReminderForm, setQuickReminderForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    balance: '',
    pastedInfo: ''
  });
  const [quickReminderSendMethod, setQuickReminderSendMethod] = useState('email'); // 'email', 'sms', 'both'
  const [quickReminders, setQuickReminders] = useState([]);
  const [quickRemindersTrash, setQuickRemindersTrash] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [sendingQuickReminder, setSendingQuickReminder] = useState(false);
  const [resendingReminder, setResendingReminder] = useState(null);
  const [editingBalance, setEditingBalance] = useState(null);
  const [editBalanceValue, setEditBalanceValue] = useState('');
  // Contact-info inline editor (email + phone) per Quick Reminder row.
  // Per user request 2026-02-22: "make sure when the clients are added to this list,
  // their phone numbers and emails are added to their contact information inside the
  // quick reminder." This lets the owner backfill missing contact details.
  const [editingContact, setEditingContact] = useState(null);
  const [editContactForm, setEditContactForm] = useState({ email: '', phone: '' });
  const [savingContact, setSavingContact] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [restoringClient, setRestoringClient] = useState(null);
  const [deletingPermanently, setDeletingPermanently] = useState(null);
  const [emptyingTrash, setEmptyingTrash] = useState(false);
  const [quickReminderSubTab, setQuickReminderSubTab] = useState('clients'); // 'clients' or 'trash'
  const [recordingPayment, setRecordingPayment] = useState(null); // ID of client receiving payment
  const [paymentAmount, setPaymentAmount] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState(''); // Search by name or phone
  const [clientSortBy, setClientSortBy] = useState('newest'); // 'newest', 'balance' or 'name'
  const [sendingEmailTo, setSendingEmailTo] = useState(null); // ID of client being sent email
  const [sendingSmsTo, setSendingSmsTo] = useState(null); // ID of client being sent SMS
  const [clientViewMode, setClientViewMode] = useState('card'); // 'card' or 'list'
  const [settingPromiseDate, setSettingPromiseDate] = useState(null); // ID of client setting promise date
  const [promiseDateValue, setPromiseDateValue] = useState('');
  const [savingPromiseDate, setSavingPromiseDate] = useState(false);
  const [togglingAutoFriday, setTogglingAutoFriday] = useState(null); // ID of client toggling auto friday
  const [sendingContractTo, setSendingContractTo] = useState(null); // ID of client being sent contract
  const [contractModalClient, setContractModalClient] = useState(null); // Client for contract modal
  const [showQuickContractModal, setShowQuickContractModal] = useState(false); // Show contract modal
  const [togglingPriority, setTogglingPriority] = useState(null); // ID of client toggling priority
  const [showPriorityOnly, setShowPriorityOnly] = useState(false); // Filter to show only high priority
  
  // Step 3 Auto-Email Controls (Simplified - ALL active clients get emails)
  const [sendingStep3Now, setSendingStep3Now] = useState(false); // Loading state for send Friday email now
  const [sendingThursdayNow, setSendingThursdayNow] = useState(false); // Loading state for send Thursday email now
  const [sendingPaymentBlast, setSendingPaymentBlast] = useState(false); // Loading state for Payment-Options blast
  const [showStep3Preview, setShowStep3Preview] = useState(false); // Show Friday email preview modal
  const [showThursdayPreview, setShowThursdayPreview] = useState(false); // Show Thursday email preview modal
  
  const [quickContractForm, setQuickContractForm] = useState({
    clientName: '',
    clientEmail: '',
    downPayment: '',
    remainingBalance: '',
    totalAmount: '',
    paymentDueDate: '',
    additionalTerms: ''
  });
  
  // Ref to track scroll timeouts and prevent stacking
  const scrollTimeoutRef = React.useRef(null);
  const scrollBackTimeoutRef = React.useRef(null);

  // Payment options constant
  const PAYMENT_OPTIONS_MESSAGE = `
You can pay FREE through Apple Pay, Zelle or Venmo

Zelle: srsworldLLC@gmail.com
PayPal: mrhunbun1@gmail.com
Cash App: $CLEARALLCR
Venmo: Clearall-Creditrepairs-1
Apple Pay: 619-253-9785

When sending with Cash App and PayPal add $3 per $100 you send.
  `.trim();

  // Paid in Full toggle for Credit Repair contracts
  const [isPaidInFull, setIsPaidInFull] = useState(false);

  // Payment methods for receipt
  const PAYMENT_METHODS = [
    { value: 'paypal', label: 'PayPal' },
    { value: 'venmo', label: 'Venmo' },
    { value: 'zelle', label: 'Zelle' },
    { value: 'apple_pay', label: 'Apple Pay' },
    { value: 'cash_app', label: 'Cash App' },
    { value: 'cash', label: 'Cash Deposit' }
  ];

  useEffect(() => {
    if (isOpen && activeTab === 'history') {
      fetchContracts();
    }
    if (isOpen && activeTab === 'loans') {
      fetchLoans();
    }
    if (isOpen && activeTab === 'quick-reminder') {
      fetchQuickReminders();
    }
  }, [isOpen, activeTab]);

  // Auto-refresh Quick Reminder data every 30 seconds when tab is active
  // This ensures data is always in sync with the database
  useEffect(() => {
    let refreshInterval;
    if (isOpen && activeTab === 'quick-reminder') {
      // Initial fetch
      fetchQuickReminders();
      fetchQuickRemindersTrash();
      
      // Refresh every 30 seconds to keep data synced
      refreshInterval = setInterval(() => {
        fetchQuickReminders();
        fetchQuickRemindersTrash();
      }, 30000);
    }
    
    // Cleanup and final sync when closing or switching tabs
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [isOpen, activeTab]);

  useEffect(() => {
    // Auto-calculate total for credit repair (whole numbers only)
    const down = parseInt(creditRepairForm.downPayment) || 0;
    const remaining = parseInt(creditRepairForm.remainingBalance) || 0;
    setCreditRepairForm(prev => ({
      ...prev,
      totalAmount: String(down + remaining)
    }));
  }, [creditRepairForm.downPayment, creditRepairForm.remainingBalance]);

  // Handle "Paid in Full" toggle for Credit Repair contracts
  useEffect(() => {
    if (isPaidInFull) {
      const total = parseInt(creditRepairForm.totalAmount) || 0;
      // When toggled ON: set down payment = total, remaining = 0
      if (total > 0) {
        setCreditRepairForm(prev => ({
          ...prev,
          downPayment: String(total),
          remainingBalance: '0'
        }));
      }
    }
  }, [isPaidInFull]);

  // When user manually enters a total and "Paid in Full" is on, update down payment
  const handlePaidInFullTotalChange = (newTotal) => {
    const totalValue = parseInt(newTotal) || 0;
    if (isPaidInFull) {
      setCreditRepairForm(prev => ({
        ...prev,
        totalAmount: newTotal,
        downPayment: String(totalValue),
        remainingBalance: '0'
      }));
    } else {
      setCreditRepairForm(prev => ({
        ...prev,
        totalAmount: newTotal
      }));
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowBorrowerDropdown(false);
      setShowLenderDropdown(false);
    };
    if (showBorrowerDropdown || showLenderDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showBorrowerDropdown, showLenderDropdown]);

  // Select a saved borrower
  const selectBorrower = (borrower) => {
    setPromissoryForm(prev => ({
      ...prev,
      borrowerName: borrower.name,
      borrowerEmail: borrower.email || prev.borrowerEmail
    }));
    setShowBorrowerDropdown(false);
  };

  // Select a saved lender
  const selectLender = (lender) => {
    setPromissoryForm(prev => ({
      ...prev,
      lenderName: lender.name,
      lenderEmail: lender.email || prev.lenderEmail
    }));
    setShowLenderDropdown(false);
  };

  // Generate signature image from text using canvas
  const generateSignatureImage = (name, style) => {
    const canvas = document.createElement('canvas');
    canvas.width = 500;
    canvas.height = 120;
    const ctx = canvas.getContext('2d');
    
    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw signature text
    ctx.fillStyle = '#000000';
    ctx.font = `${style.fontSize} ${style.fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, canvas.width / 2, canvas.height / 2);
    
    return canvas.toDataURL('image/png');
  };

  const getLenderSignatureData = () => {
    if (!selectedSignatureStyle) return null;
    const style = SIGNATURE_STYLES.find(s => s.id === selectedSignatureStyle);
    if (!style) return null;
    return generateSignatureImage(promissoryForm.lenderName, style);
  };

  const fetchContracts = async () => {
    try {
      setLoadingContracts(true);
      const response = await axios.get(`${API}/api/promise-to-pay/list`);
      if (response.data.success) {
        const allContracts = response.data.contracts || [];
        // Separate active and trashed contracts
        setContracts(allContracts.filter(c => !c.is_trashed));
        setTrashedContracts(allContracts.filter(c => c.is_trashed));
      }
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
      toast.error('Failed to load contracts');
    } finally {
      setLoadingContracts(false);
    }
  };

  // Move contract to trash
  const handleMoveToTrash = async (contractId) => {
    try {
      const response = await axios.post(`${API}/api/promise-to-pay/trash/${contractId}`);
      if (response.data.success) {
        toast.success('Moved to trash');
        fetchContracts();
      }
    } catch (error) {
      console.error('Failed to trash contract:', error);
      toast.error('Failed to move to trash');
    }
  };

  // Restore contract from trash
  const handleRestoreFromTrash = async (contractId) => {
    try {
      const response = await axios.post(`${API}/api/promise-to-pay/restore/${contractId}`);
      if (response.data.success) {
        toast.success('Restored from trash');
        fetchContracts();
      }
    } catch (error) {
      console.error('Failed to restore contract:', error);
      toast.error('Failed to restore');
    }
  };

  // Permanently delete contract
  const handlePermanentDelete = async (contractId) => {
    try {
      const response = await axios.delete(`${API}/api/promise-to-pay/delete/${contractId}`);
      if (response.data.success) {
        toast.success('Permanently deleted');
        fetchContracts();
      }
    } catch (error) {
      console.error('Failed to delete contract:', error);
      toast.error('Failed to delete');
    }
  };

  // Empty all trash
  const handleEmptyTrash = async () => {
    if (!window.confirm('Are you sure you want to permanently delete all items in trash? This cannot be undone.')) {
      return;
    }
    try {
      const response = await axios.post(`${API}/api/promise-to-pay/empty-trash`);
      if (response.data.success) {
        toast.success(`Permanently deleted ${response.data.deleted_count} items`);
        fetchContracts();
      }
    } catch (error) {
      console.error('Failed to empty trash:', error);
      toast.error('Failed to empty trash');
    }
  };

  // Fetch all loans for Loan Log
  const fetchLoans = async () => {
    try {
      setLoadingLoans(true);
      const response = await axios.get(`${API}/api/loans/list`);
      if (response.data.success) {
        setLoans(response.data.loans || []);
      }
    } catch (error) {
      console.error('Failed to fetch loans:', error);
      toast.error('Failed to load loans');
    } finally {
      setLoadingLoans(false);
    }
  };

  // Quick Reminder Functions
  const fetchQuickReminders = async () => {
    try {
      setLoadingReminders(true);
      const response = await axios.get(`${API}/api/quick-reminders`);
      if (response.data.success) {
        setQuickReminders(response.data.reminders || []);
      }
    } catch (error) {
      console.error('Failed to fetch quick reminders:', error);
    } finally {
      setLoadingReminders(false);
    }
  };

  const fetchQuickRemindersTrash = async () => {
    try {
      setLoadingTrash(true);
      const response = await axios.get(`${API}/api/quick-reminders/trash`);
      if (response.data.success) {
        setQuickRemindersTrash(response.data.reminders || []);
      }
    } catch (error) {
      console.error('Failed to fetch trash:', error);
    } finally {
      setLoadingTrash(false);
    }
  };

  const handleSendQuickReminder = async () => {
    // Validate based on send method
    if (!quickReminderForm.clientName || !quickReminderForm.balance) {
      toast.error('Please fill in client name and balance');
      return;
    }
    
    // Check required fields based on send method
    if ((quickReminderSendMethod === 'email' || quickReminderSendMethod === 'both') && !quickReminderForm.clientEmail) {
      toast.error('Email address is required for email delivery');
      return;
    }
    if ((quickReminderSendMethod === 'sms' || quickReminderSendMethod === 'both') && !quickReminderForm.clientPhone) {
      toast.error('Phone number is required for SMS delivery');
      return;
    }
    
    try {
      setSendingQuickReminder(true);
      const response = await axios.post(`${API}/api/quick-reminder/send`, {
        clientName: quickReminderForm.clientName,
        clientEmail: quickReminderForm.clientEmail || null,
        clientPhone: quickReminderForm.clientPhone || null,
        balance: quickReminderForm.balance,
        sendMethod: quickReminderSendMethod
      });
      
      if (response.data.success) {
        let successMsg = 'Client saved!';
        if (response.data.email_sent && response.data.sms_sent) {
          successMsg = 'Email + SMS sent & client saved!';
        } else if (response.data.email_sent) {
          successMsg = 'Email sent & client saved!';
        } else if (response.data.sms_sent) {
          successMsg = 'SMS sent & client saved!';
        }
        toast.success(successMsg);
        
        // Clear all form fields for next client FIRST
        setQuickReminderForm({ clientName: '', clientEmail: '', clientPhone: '', balance: '', pastedInfo: '' });
        setQuickReminderSendMethod('email');
        
        // Fetch updated list
        fetchQuickReminders();
        
        // Clear any existing scroll timeouts to prevent stacking
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        if (scrollBackTimeoutRef.current) {
          clearTimeout(scrollBackTimeoutRef.current);
        }
        
        // Auto-scroll to show the saved client, then scroll back up for next entry
        scrollTimeoutRef.current = setTimeout(() => {
          const clientsSection = document.getElementById('quick-reminder-clients-section');
          if (clientsSection) {
            clientsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          
          // After showing the log briefly, scroll back to top for next client entry
          scrollBackTimeoutRef.current = setTimeout(() => {
            const formSection = document.getElementById('quick-reminder-form-section');
            if (formSection) {
              formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 1500);
        }, 300);
      } else {
        toast.error(response.data.error || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Failed to send quick reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setSendingQuickReminder(false);
    }
  };

  const handleResendReminder = async (reminderId, method = 'both') => {
    // method: 'email', 'sms', or 'both'
    try {
      if (method === 'email') {
        setSendingEmailTo(reminderId);
      } else if (method === 'sms') {
        setSendingSmsTo(reminderId);
      } else {
        setResendingReminder(reminderId);
      }
      
      const response = await axios.post(`${API}/api/quick-reminder/resend/${reminderId}`, {
        sendMethod: method
      });
      
      if (response.data.success) {
        let msg = 'Reminder sent!';
        if (response.data.email_sent && response.data.sms_sent) {
          msg = 'Email + SMS sent!';
        } else if (response.data.email_sent) {
          msg = 'Email sent!';
        } else if (response.data.sms_sent) {
          msg = 'SMS sent!';
        }
        toast.success(msg);
        fetchQuickReminders();
      } else {
        toast.error(response.data.error || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Failed to resend reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setResendingReminder(null);
      setSendingEmailTo(null);
      setSendingSmsTo(null);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    const reminder = quickReminders.find(r => r.id === reminderId);
    const clientName = reminder?.client_name || 'this client';
    const balance = reminder?.balance ? `$${parseFloat(reminder.balance).toFixed(2)}` : '';
    
    if (!window.confirm(`🗑️ Move to Trash\n\nMove "${clientName}" (${balance}) to trash?\n\nYou can restore this client from the trash at any time.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/api/quick-reminder/${reminderId}`);
      if (response.data.success) {
        toast.success(`${clientName} moved to trash`);
        fetchQuickReminders();
        fetchQuickRemindersTrash();
      }
    } catch (error) {
      console.error('Failed to move to trash:', error);
      toast.error('Failed to move to trash');
    }
  };

  /**
   * Mark a Quick Reminder client as PAID IN FULL.
   * Workflow per user request 2026-02-29:
   *  1. Zero out the balance via PUT /quick-reminder/{id}/balance
   *  2. Fire POST /quick-reminder/{id}/send-payment-confirmation with the
   *     remaining balance amount as the "payment" so the email already
   *     formats the "🎉 PAID IN FULL!" banner block.
   *  3. Pop the "Also delete this client?" confirmation modal so the owner
   *     can clean up the active-clients list in one tap.
   */
  const handleMarkClientPaidInFull = async (reminder) => {
    if (!reminder?.id) return;
    const balance = parseFloat(reminder.balance) || 0;
    try {
      setMarkingPaidReminderId(reminder.id);
      // Step 1 — zero the balance
      await axios.put(`${API}/api/quick-reminder/${reminder.id}/balance`, { balance: '0' });
      // Step 2 — email the client a paid-in-full receipt (only if we have an email)
      if (reminder.client_email && balance > 0) {
        try {
          await axios.post(`${API}/api/quick-reminder/${reminder.id}/send-payment-confirmation`, {
            payment_amount: balance.toFixed(2),
            new_balance: '0.00',
            client_name: reminder.client_name,
            client_email: reminder.client_email,
          });
          toast.success(`🎉 ${reminder.client_name} marked PAID IN FULL · email sent to ${reminder.client_email}`);
        } catch (emailErr) {
          console.warn('[PaidInFull] email failed', emailErr);
          toast.success(`🎉 ${reminder.client_name} marked PAID IN FULL · email failed to send`);
        }
      } else {
        toast.success(`🎉 ${reminder.client_name} marked PAID IN FULL${reminder.client_email ? '' : ' (no email on file)'}`);
      }
      // Step 3 — pop the delete-confirmation modal
      setPaidInFullConfirmDelete(reminder);
      // Refresh the list so the red $0 shows up
      fetchQuickReminders();
    } catch (e) {
      console.error('[PaidInFull] failed', e);
      toast.error('Failed to mark paid in full');
    } finally {
      setMarkingPaidReminderId(null);
    }
  };

  /**
   * Soft-delete the client we just marked PAID IN FULL.  Backend moves the
   * record to the trash sub-tab so it can be restored if needed.
   */
  const confirmDeletePaidInFullClient = async () => {
    const reminder = paidInFullConfirmDelete;
    if (!reminder?.id) { setPaidInFullConfirmDelete(null); return; }
    try {
      await axios.delete(`${API}/api/quick-reminder/${reminder.id}`);
      toast.success(`${reminder.client_name} moved to Trash`);
      fetchQuickReminders();
      fetchQuickRemindersTrash();
    } catch (e) {
      console.error('[PaidInFull→delete] failed', e);
      toast.error('Failed to move to trash');
    } finally {
      setPaidInFullConfirmDelete(null);
    }
  };



  const [sendPaymentConfirmation, setSendPaymentConfirmation] = useState(true); // Default to sending confirmation
  
  const handleQuickReminderPayment = async (reminderId) => {
    const reminder = quickReminders.find(r => r.id === reminderId);
    if (!reminder) return;
    
    const payment = parseFloat(paymentAmount);
    if (isNaN(payment) || payment <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    const currentBalance = parseFloat(reminder.balance) || 0;
    const newBalance = Math.max(0, currentBalance - payment);
    
    try {
      setSavingPayment(true);
      const response = await axios.put(`${API}/api/quick-reminder/${reminderId}/balance`, {
        balance: newBalance.toString()
      });
      
      if (response.data.success) {
        // Send payment confirmation email if checkbox is checked
        if (sendPaymentConfirmation && reminder.client_email) {
          try {
            await axios.post(`${API}/api/quick-reminder/${reminderId}/send-payment-confirmation`, {
              payment_amount: payment.toFixed(2),
              new_balance: newBalance.toFixed(2),
              client_name: reminder.client_name,
              client_email: reminder.client_email
            });
            toast.success(`💰 Payment of $${payment.toFixed(2)} recorded & confirmation sent to ${reminder.client_email}!`);
          } catch (emailError) {
            console.error('Failed to send confirmation email:', emailError);
            toast.success(`💰 Payment recorded but email failed to send`);
          }
        } else {
          if (newBalance === 0) {
            toast.success(`💰 Payment of $${payment.toFixed(2)} recorded - ${reminder.client_name} is PAID IN FULL!`);
          } else {
            toast.success(`💰 Payment of $${payment.toFixed(2)} recorded - New balance: $${newBalance.toFixed(2)}`);
          }
        }
        setRecordingPayment(null);
        setPaymentAmount('');
        fetchQuickReminders();
      } else {
        toast.error(response.data.error || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast.error('Failed to record payment');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleSetPromiseDate = async (reminderId) => {
    if (!promiseDateValue) {
      toast.error('Please select a date');
      return;
    }
    
    try {
      setSavingPromiseDate(true);
      const response = await axios.put(`${API}/api/quick-reminder/${reminderId}/promise-pay-date`, {
        promise_date: promiseDateValue
      });
      
      if (response.data.success) {
        toast.success(`📅 Promise to pay date set! Automatic reminder will be sent on that day.`);
        setSettingPromiseDate(null);
        setPromiseDateValue('');
        fetchQuickReminders();
      } else {
        toast.error(response.data.error || 'Failed to set date');
      }
    } catch (error) {
      console.error('Failed to set promise date:', error);
      toast.error('Failed to set promise date');
    } finally {
      setSavingPromiseDate(false);
    }
  };

  const handleClearPromiseDate = async (reminderId) => {
    try {
      const response = await axios.delete(`${API}/api/quick-reminder/${reminderId}/promise-pay-date`);
      if (response.data.success) {
        toast.success('Promise date cleared');
        fetchQuickReminders();
      }
    } catch (error) {
      console.error('Failed to clear promise date:', error);
      toast.error('Failed to clear date');
    }
  };

  const handleToggleAutoFriday = async (reminderId, currentValue) => {
    try {
      setTogglingAutoFriday(reminderId);
      const newValue = !currentValue;
      const response = await axios.put(`${API}/api/quick-reminder/${reminderId}/auto-friday`, {
        enabled: newValue
      });
      
      if (response.data.success) {
        if (newValue) {
          toast.success('🔔 Auto Friday reminders ON - Client will receive automatic reminders every Friday');
        } else {
          toast.success('Auto Friday reminders OFF');
        }
        fetchQuickReminders();
      } else {
        toast.error(response.data.error || 'Failed to toggle');
      }
    } catch (error) {
      console.error('Failed to toggle auto Friday:', error);
      toast.error('Failed to update setting');
    } finally {
      setTogglingAutoFriday(null);
    }
  };

  // Set the automated payment-reminder cadence for a Quick Reminder client.
  // Allowed values: 'off' | 'weekly' | 'biweekly' | 'monthly'.
  const handleSetReminderFrequency = async (reminderId, frequency) => {
    try {
      setTogglingAutoFriday(reminderId);
      const response = await axios.put(
        `${API}/api/quick-reminder/${reminderId}/reminder-frequency`,
        { frequency }
      );
      if (response.data.success) {
        const labels = {
          off: 'Auto reminders OFF',
          weekly: '🔔 Weekly reminders ON — every Friday at 9 AM Pacific',
          biweekly: '🔔 Biweekly reminders ON — every other Friday',
          monthly: '🔔 Monthly reminders ON — first Friday of each month',
        };
        toast.success(labels[frequency] || `Reminder frequency: ${frequency}`);
        fetchQuickReminders();
      } else {
        toast.error(response.data.error || 'Failed to update reminder frequency');
      }
    } catch (error) {
      console.error('Failed to set reminder frequency:', error);
      toast.error('Failed to update reminder frequency');
    } finally {
      setTogglingAutoFriday(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 3 AUTO-EMAILS (Simplified - ALL active clients get emails automatically)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Send Friday Step 3 emails NOW (manual trigger for testing)
  const handleSendStep3Now = async () => {
    if (quickReminders.length === 0) {
      toast.error('No active clients in Quick Reminder folder');
      return;
    }
    
    if (!window.confirm(`Send Step 3 FRIDAY emails NOW to all ${quickReminders.length} active client(s)?\n\nThis will send the Friday morning reminder immediately.`)) {
      return;
    }
    
    try {
      setSendingStep3Now(true);
      const response = await axios.post(`${API}/api/quick-reminders/send-step3-now`);
      if (response.data.success) {
        toast.success(`📞 Friday Step 3 emails sent to ${response.data.emails_sent} client(s)!`);
      } else {
        toast.error('Failed to send Friday emails');
      }
    } catch (error) {
      console.error('Failed to send Friday Step 3:', error);
      toast.error('Failed to send Friday emails');
    } finally {
      setSendingStep3Now(false);
    }
  };
  
  // Send Thursday heads-up emails NOW (manual trigger for testing)
  const handleSendThursdayNow = async () => {
    if (quickReminders.length === 0) {
      toast.error('No active clients in Quick Reminder folder');
      return;
    }
    
    if (!window.confirm(`Send Step 3 THURSDAY heads-up emails NOW to all ${quickReminders.length} active client(s)?\n\nThis will send the Thursday night reminder immediately.`)) {
      return;
    }
    
    try {
      setSendingThursdayNow(true);
      const response = await axios.post(`${API}/api/quick-reminders/send-thursday-headsup-now`);
      if (response.data.success) {
        toast.success(`🌙 Thursday heads-up emails sent to ${response.data.emails_sent} client(s)!`);
      } else {
        toast.error('Failed to send Thursday emails');
      }
    } catch (error) {
      console.error('Failed to send Thursday heads-up:', error);
      toast.error('Failed to send Thursday emails');
    } finally {
      setSendingThursdayNow(false);
    }
  };

  // ═════════════════════════════════════════════════════════════════
  // PAYMENT OPTIONS BLAST — Feb 2026
  // One-tap email to every Quick Reminder client with balance > 0:
  //   • Shows their current balance
  //   • Lists Zelle / PayPal / Cash App / Venmo / Apple Pay handles
  //   • Cash App & PayPal auto-show +$3 per $100 fee
  //   • Reminds them to send a payment screenshot
  //   • Reply-with-date CTA so they can tell you when they'll pay
  // After the blast finishes the ADMIN gets ONE consolidated summary
  // email listing everyone the blast was sent to + their balances —
  // per user request (no per-client admin notifications).
  // ═════════════════════════════════════════════════════════════════
  const handleSendPaymentOptionsBlast = async () => {
    const activeWithBalance = quickReminders.filter(r => {
      const b = parseFloat(r.balance) || 0;
      return b > 0 && r.client_email;
    }).length;
    if (activeWithBalance === 0) {
      toast.error('No active clients with a positive balance to email');
      return;
    }
    if (!window.confirm(
      `Send a Payment-Options email to all ${activeWithBalance} client(s) with a balance?\n\n` +
      `Each client sees their balance + Zelle / PayPal / Cash App / Venmo / Apple Pay handles ` +
      `(Cash App & PayPal show the +$3 per $100 fee).\n\n` +
      `You'll receive ONE summary email at the end — not one per client.`
    )) {
      return;
    }
    try {
      setSendingPaymentBlast(true);
      const resp = await axios.post(`${API}/api/quick-reminders/send-payment-options-blast`);
      if (resp.data.success) {
        toast.success(
          resp.data.message || `💰 Payment blast queued for ${resp.data.will_send_to} client(s). Summary email incoming.`,
          { duration: 6000 }
        );
      } else {
        toast.error('Failed to queue payment blast');
      }
    } catch (error) {
      console.error('Payment-Options blast failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to send payment blast');
    } finally {
      setSendingPaymentBlast(false);
    }
  };

  // Toggle High Priority for a Quick Reminder client
  const handleTogglePriority = async (reminderId, currentValue) => {
    try {
      setTogglingPriority(reminderId);
      const newValue = !currentValue;
      const response = await axios.put(`${API}/api/quick-reminder/${reminderId}/priority`, {
        high_priority: newValue
      });
      
      if (response.data.success) {
        if (newValue) {
          toast.success('⭐ High Priority ON - Client marked for priority attention');
        } else {
          toast.success('Priority OFF');
        }
        fetchQuickReminders();
      } else {
        toast.error(response.data.error || 'Failed to toggle priority');
      }
    } catch (error) {
      console.error('Failed to toggle priority:', error);
      toast.error('Failed to update priority');
    } finally {
      setTogglingPriority(null);
    }
  };

  // Open contract modal for a Quick Reminder client
  const openContractForClient = (reminder) => {
    setContractModalClient(reminder);
    setQuickContractForm({
      clientName: reminder.client_name || '',
      clientEmail: reminder.client_email || '',
      downPayment: reminder.balance || '',
      remainingBalance: '0',
      totalAmount: reminder.balance || '',
      paymentDueDate: '',
      additionalTerms: ''
    });
    setShowQuickContractModal(true);
  };

  // Generate the Credit Repair Contract text (reusing existing logic)
  const generateQuickCreditRepairContract = () => {
    const { clientName, downPayment, remainingBalance, totalAmount, paymentDueDate, additionalTerms } = quickContractForm;
    const formattedDownPayment = parseInt(downPayment) || 0;
    const formattedRemainingBalance = parseInt(remainingBalance) || 0;
    const formattedTotal = parseInt(totalAmount) || formattedDownPayment + formattedRemainingBalance;
    const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    
    let paymentScheduleText = '';
    if (formattedRemainingBalance > 0) {
      if (paymentDueDate) {
        const dueDate = new Date(paymentDueDate + 'T00:00:00');
        const formattedDueDate = dueDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        paymentScheduleText = `
PAYMENT SCHEDULE:
• Down Payment: $${formattedDownPayment.toLocaleString()} (Due upon signing)
• Remaining Balance: $${formattedRemainingBalance.toLocaleString()} (Due by ${formattedDueDate})`;
      } else {
        paymentScheduleText = `
PAYMENT SCHEDULE:
• Down Payment: $${formattedDownPayment.toLocaleString()} (Due upon signing)
• Remaining Balance: $${formattedRemainingBalance.toLocaleString()} (Due as agreed)`;
      }
    } else {
      paymentScheduleText = `
PAYMENT: $${formattedTotal.toLocaleString()} PAID IN FULL`;
    }

    return `CREDIT REPAIR SERVICE AGREEMENT

Date: ${today}

This Agreement is entered into between:

SERVICE PROVIDER:
Clear All Credit Repair
clearallcreditrepair@gmail.com

CLIENT:
${clientName}

SERVICES:
Clear All Credit Repair agrees to provide credit repair consulting services including:
• Analysis of credit reports from all three bureaus
• Identification of inaccurate, outdated, or unverifiable information
• Preparation and submission of dispute letters to credit bureaus
• Follow-up on disputes and creditor responses
• Guidance on credit improvement strategies

TOTAL SERVICE FEE: $${formattedTotal.toLocaleString()}
${paymentScheduleText}

CLIENT ACKNOWLEDGES:

• Payment is required as outlined above
• Services begin upon receipt of down payment

CLIENT RESPONSIBILITIES AND COMPLIANCE:

The Client agrees to the following terms and conditions:

1. COMMUNICATION COMPLIANCE: The Client MUST follow all email instructions and respond to all communications from Clear All Credit Repair within 48 hours of receipt.

2. DOCUMENT SUBMISSION: The Client must provide all requested documents (ID, proof of address, credit reports, etc.) in a timely manner as instructed.

3. FOLLOW-UP ACTIONS: The Client must complete any action items assigned via email, including but not limited to:
   • Reviewing dispute letters before submission
   • Providing updated credit reports when requested
   • Responding to creditor communications as directed
   • Maintaining open lines of communication

4. PAYMENT COMPLIANCE: All payments must be made on time according to the payment schedule outlined above.

FORFEITURE CLAUSE:

⚠️ IMPORTANT: If the Client fails to comply with the credit repair process, including but not limited to:
   • Not responding to emails within 48 hours
   • Failing to provide requested documents
   • Not following through with assigned action items
   • Missing scheduled payments

The Client's fees are NON-REFUNDABLE and considered FORFEITED due to non-compliance with the credit repair process. Clear All Credit Repair cannot guarantee results if the Client does not actively participate in the process.

${additionalTerms ? `ADDITIONAL TERMS:\n${additionalTerms}\n` : ''}
ACKNOWLEDGMENT:

By signing below, I, ${clientName}, acknowledge that I have read, understand, and agree to all terms and conditions of this Credit Repair Service Agreement. I understand that my compliance is required and that failure to comply may result in forfeiture of all fees paid.`;
  };

  // Send contract to Quick Reminder client
  const handleSendQuickContract = async () => {
    const { clientName, clientEmail, downPayment } = quickContractForm;
    
    if (!clientName || !clientEmail || !downPayment) {
      toast.error('Please fill in client name, email, and down payment');
      return;
    }
    
    try {
      setSendingContractTo(contractModalClient?.id);
      const contractText = generateQuickCreditRepairContract();
      
      const response = await axios.post(`${API}/api/promise-to-pay/create`, {
        client_email: clientEmail,
        client_name: clientName,
        contract_text: contractText,
        contract_type: 'credit_repair',
        metadata: {
          down_payment: quickContractForm.downPayment,
          remaining_balance: quickContractForm.remainingBalance,
          total_amount: quickContractForm.totalAmount,
          payment_due_date: quickContractForm.paymentDueDate,
          source: 'quick_reminder',
          quick_reminder_id: contractModalClient?.id
        }
      });

      if (response.data.success) {
        toast.success(`Contract sent to ${clientEmail}! They can sign it digitally.`);
        setShowQuickContractModal(false);
        setContractModalClient(null);
        setQuickContractForm({
          clientName: '',
          clientEmail: '',
          downPayment: '',
          remainingBalance: '',
          totalAmount: '',
          paymentDueDate: '',
          additionalTerms: ''
        });
      }
    } catch (error) {
      console.error('Failed to send contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to send contract');
    } finally {
      setSendingContractTo(null);
    }
  };

  const handleRestoreClient = async (reminderId) => {
    const reminder = quickRemindersTrash.find(r => r.id === reminderId);
    const clientName = reminder?.client_name || 'this client';
    
    try {
      setRestoringClient(reminderId);
      const response = await axios.post(`${API}/api/quick-reminder/${reminderId}/restore`);
      if (response.data.success) {
        toast.success(`${clientName} restored to Quick Reminder Clients!`);
        fetchQuickReminders();
        fetchQuickRemindersTrash();
      }
    } catch (error) {
      console.error('Failed to restore client:', error);
      toast.error('Failed to restore client');
    } finally {
      setRestoringClient(null);
    }
  };

  const handleQuickReminderPermanentDelete = async (reminderId) => {
    const reminder = quickRemindersTrash.find(r => r.id === reminderId);
    const clientName = reminder?.client_name || 'this client';
    const balance = reminder?.balance ? `$${parseFloat(reminder.balance).toFixed(2)}` : '';
    
    if (!window.confirm(`⚠️ PERMANENT DELETE\n\nAre you absolutely sure you want to permanently delete "${clientName}" (${balance})?\n\nThis action CANNOT be undone!`)) {
      return;
    }
    
    try {
      setDeletingPermanently(reminderId);
      const response = await axios.delete(`${API}/api/quick-reminder/${reminderId}/permanent`);
      if (response.data.success) {
        toast.success(`${clientName} permanently deleted`);
        fetchQuickRemindersTrash();
      }
    } catch (error) {
      console.error('Failed to permanently delete:', error);
      toast.error('Failed to delete permanently');
    } finally {
      setDeletingPermanently(null);
    }
  };

  const handleQuickReminderEmptyTrash = async () => {
    if (quickRemindersTrash.length === 0) {
      toast.error('Trash is already empty');
      return;
    }
    
    if (!window.confirm(`⚠️ EMPTY TRASH\n\nThis will permanently delete ALL ${quickRemindersTrash.length} clients in trash.\n\nThis action CANNOT be undone! Are you sure?`)) {
      return;
    }
    
    try {
      setEmptyingTrash(true);
      const response = await axios.delete(`${API}/api/quick-reminders/trash/empty`);
      if (response.data.success) {
        toast.success(`Trash emptied - ${response.data.count} clients permanently deleted`);
        fetchQuickRemindersTrash();
      }
    } catch (error) {
      console.error('Failed to empty trash:', error);
      toast.error('Failed to empty trash');
    } finally {
      setEmptyingTrash(false);
    }
  };

  // Confirmation modal state (per user request 2026-03-04): after a balance
  // is updated successfully, ask whether to email the client the new balance.
  const [balanceUpdateEmailPrompt, setBalanceUpdateEmailPrompt] = useState(null); // {reminder, oldBalance, newBalance}
  const [sendingBalanceUpdateEmail, setSendingBalanceUpdateEmail] = useState(false);

  const handleUpdateBalance = async (reminderId) => {
    if (!editBalanceValue || isNaN(parseFloat(editBalanceValue))) {
      toast.error('Please enter a valid amount');
      return;
    }
    // Capture the old balance BEFORE the PUT so we can compute the delta.
    const oldReminder = quickReminders.find(r => r.id === reminderId);
    const oldBalance = parseFloat(oldReminder?.balance) || 0;
    const newBalance = parseFloat(editBalanceValue) || 0;

    try {
      const response = await axios.put(`${API}/api/quick-reminder/${reminderId}/balance`, {
        balance: editBalanceValue
      });

      if (response.data.success) {
        toast.success('Balance updated!');
        setEditingBalance(null);
        setEditBalanceValue('');
        fetchQuickReminders();
        // Only prompt for an email when the balance actually changed AND
        // the client has an email on file.  No point asking otherwise.
        if (Math.abs(oldBalance - newBalance) > 0.001 && (oldReminder?.client_email || '').trim()) {
          setBalanceUpdateEmailPrompt({
            reminder: oldReminder,
            oldBalance,
            newBalance,
          });
        }
      } else {
        toast.error(response.data.error || 'Failed to update balance');
      }
    } catch (error) {
      console.error('Failed to update balance:', error);
      toast.error('Failed to update balance');
    }
  };

  // Helper invoked from the confirmation modal — fires the new
  // /send-balance-update endpoint, then closes the modal.
  const handleConfirmSendBalanceUpdateEmail = async () => {
    if (!balanceUpdateEmailPrompt) return;
    const { reminder, oldBalance, newBalance } = balanceUpdateEmailPrompt;
    try {
      setSendingBalanceUpdateEmail(true);
      const { data } = await axios.post(
        `${API}/api/quick-reminder/${reminder.id}/send-balance-update`,
        {
          old_balance: String(oldBalance),
          new_balance: String(newBalance),
          client_name: reminder.client_name,
          client_email: reminder.client_email,
        },
      );
      if (data?.success) {
        toast.success(`✉️  Updated balance emailed to ${data.sent_to}`);
      } else {
        toast.error(data?.error || 'Email failed');
      }
    } catch (err) {
      console.error('[Balance Update Email] failed', err);
      toast.error('Email failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSendingBalanceUpdateEmail(false);
      setBalanceUpdateEmailPrompt(null);
    }
  };

  const handleSaveContact = async (reminderId) => {
    const email = (editContactForm.email || '').trim();
    const phone = (editContactForm.phone || '').trim();
    if (!email && !phone) {
      toast.error('Enter an email or phone first');
      return;
    }
    try {
      setSavingContact(true);
      const { data } = await axios.put(`${API}/api/quick-reminder/${reminderId}/contact`, {
        client_email: email || null,
        client_phone: phone || null,
      });
      if (data.success) {
        toast.success('Contact info saved');
        setEditingContact(null);
        setEditContactForm({ email: '', phone: '' });
        fetchQuickReminders();
      } else {
        toast.error(data.error || 'Failed to save');
      }
    } catch (err) {
      console.error('Failed to save contact:', err);
      toast.error('Failed to save contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleSelectClient = (clientId) => {
    setSelectedClients(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedClients.length === quickReminders.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(quickReminders.map(r => r.id));
    }
  };

  const handleBulkSend = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select at least one client');
      return;
    }
    
    setSendingBulk(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const clientId of selectedClients) {
      try {
        const response = await axios.post(`${API}/api/quick-reminder/resend/${clientId}`);
        if (response.data.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
      // Small delay between sends to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setSendingBulk(false);
    setSelectedClients([]);
    fetchQuickReminders();
    
    if (failCount === 0) {
      toast.success(`✅ Sent ${successCount} reminder${successCount > 1 ? 's' : ''} successfully!`);
    } else {
      toast.warning(`Sent ${successCount}, failed ${failCount}`);
    }
  };

  // Fetch Loan Ledger summary
  const fetchLoanLedger = async () => {
    try {
      setLoadingLedger(true);
      const response = await axios.get(`${API}/api/loan-ledger/summary`);
      if (response.data.success) {
        setLedgerData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch loan ledger:', error);
      toast.error('Failed to load loan ledger');
    } finally {
      setLoadingLedger(false);
    }
  };

  // Email Active Loans — fetch clients with active loans + send consolidated email
  const fetchActiveLoanClients = async () => {
    try {
      setLoadingActiveLoanClients(true);
      const r = await axios.get(`${API}/api/loans/active-clients`);
      if (r.data.success) {
        setActiveLoanClients(r.data.clients || []);
        if ((r.data.clients || []).length === 0) {
          toast.info('No clients currently have active loans');
        }
      }
    } catch (e) {
      toast.error('Failed to load active-loan clients');
    } finally {
      setLoadingActiveLoanClients(false);
    }
  };

  const openEmailActiveLoansModal = () => {
    setSelectedClientEmail('');
    setSelectedLoanNumbers([]);
    setEmailActiveNote('');
    fetchActiveLoanClients();
  };

  useEffect(() => {
    if (showEmailActiveLoansModal) openEmailActiveLoansModal();
  }, [showEmailActiveLoansModal]);

  const sendActiveLoansEmail = async () => {
    if (!selectedClientEmail) { toast.error('Pick a client'); return; }
    try {
      setSendingActiveLoansEmail(true);
      const r = await axios.post(`${API}/api/loans/email-active-loans`, {
        borrower_email: selectedClientEmail,
        loan_numbers: selectedLoanNumbers,
        custom_note: emailActiveNote || null,
        promise_dates: emailActivePromiseDates,
      });
      if (r.data.success) {
        toast.success(`Sent ${r.data.loan_count} loan(s) summary to ${r.data.borrower_email}`);
        setShowEmailActiveLoansModal(false);
        setEmailActivePromiseDates({});
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Unknown error';
      toast.error(`Failed to send: ${detail}`);
    } finally {
      setSendingActiveLoansEmail(false);
    }
  };

  // Save loan ledger
  const handleSaveLedger = async () => {
    try {
      setIsSavingLedger(true);
      const response = await axios.post(`${API}/api/loan-ledger/save`);
      if (response.data.success) {
        toast.success(`Ledger saved! ${response.data.loan_count} loans synced`);
      }
    } catch (error) {
      console.error('Failed to save ledger:', error);
      toast.error('Failed to save ledger');
    } finally {
      setIsSavingLedger(false);
    }
  };

  // Close a loan (keep in records)
  const handleCloseLoan = async (loanNumber) => {
    if (!window.confirm(`Close Loan #${loanNumber}? It will be moved to closed loans but kept in records.`)) {
      return;
    }
    try {
      const response = await axios.post(`${API}/api/loan-ledger/close/${loanNumber}`);
      if (response.data.success) {
        toast.success(`Loan #${loanNumber} closed`);
        fetchLoanLedger();
      }
    } catch (error) {
      console.error('Failed to close loan:', error);
      toast.error('Failed to close loan');
    }
  };
  
  // Edit Loan modal state
  const [showEditLoanModal, setShowEditLoanModal] = useState(false);
  const [editLoanForm, setEditLoanForm] = useState(null);
  const [savingEditLoan, setSavingEditLoan] = useState(false);

  const openEditLoanModal = (loan) => {
    setEditLoanForm({
      loan_number: loan.loan_number,
      borrower_name: loan.borrower_name || '',
      borrower_email: loan.borrower_email || '',
      borrower_phone: loan.borrower_phone || '',
      date_lent: (loan.date_lent || '').slice(0, 10),
      date_return: (loan.date_return || '').slice(0, 10),
      amount_lent: loan.amount_lent || '',
      amount_return: loan.amount_return || '',
      notes: loan.notes || '',
      additional_terms: loan.additional_terms || '',
      expected_payment_date: (loan.expected_payment_date || '').slice(0, 10),
      // Tracks the ORIGINAL expected_payment_date when the modal was opened so
      // we can decide whether to send a shift_reason on save. The reason field
      // is optional — only collected if the user changes the expected date.
      _original_expected_payment_date: (loan.expected_payment_date || '').slice(0, 10),
      shift_reason: '',
      // Split payments (Feb 2026) — user can break the repayment into multiple
      // scheduled dates (half on Aug 15, half on Sep 1, etc.).  Each split
      // fires its own day-before reminder.  Empty array = single lump-sum.
      split_payments: Array.isArray(loan.split_payments)
        ? loan.split_payments.map((s) => ({ ...s }))
        : [],
    });
    setShowEditLoanModal(true);
  };

  const handleSaveLoanEdit = async () => {
    if (!editLoanForm) return;
    try {
      setSavingEditLoan(true);
      const payload = { ...editLoanForm };
      const loanNumber = editLoanForm.loan_number;
      const borrowerEmail = editLoanForm.borrower_email;
      delete payload.loan_number;
      // Only send shift_reason if the expected_payment_date actually changed.
      // Backend will append a payment_shifts history entry whenever the date
      // differs from what's on file — the reason is optional free-text.
      const origExpected = payload._original_expected_payment_date || '';
      delete payload._original_expected_payment_date;
      if (origExpected === (payload.expected_payment_date || '')) {
        delete payload.shift_reason;
      }
      const resp = await axios.put(`${API}/api/loans/${loanNumber}/edit`, payload);
      if (resp.data.success) {
        toast.success(`Loan #${loanNumber} updated`);
        setShowEditLoanModal(false);
        setEditLoanForm(null);
        fetchLoanLedger();

        // Feb 2026 — after ANY edit, prompt the admin to email a fresh
        // contract for the borrower to sign.  The latest emailed copy is
        // auto-saved to the loan's Signed Notes section (replacing prior).
        if (borrowerEmail) {
          const shouldResend = window.confirm(
            `Email a new contract to be signed to ${borrowerEmail}?\n\n` +
            `The latest contract PDF will be saved to Loan #${loanNumber}'s ` +
            `Signed Notes section, replacing any prior sent-contract copy.`
          );
          if (shouldResend) {
            try {
              toast.loading('Emailing new contract…', { id: 'edit-resend-contract' });
              const rc = await axios.post(`${API}/api/loans/${loanNumber}/resend-contract`);
              if (rc.data?.success) {
                toast.success(`Contract emailed to ${borrowerEmail} & saved to loan`, { id: 'edit-resend-contract' });
              } else {
                toast.error('Contract email failed', { id: 'edit-resend-contract' });
              }
            } catch (rcErr) {
              toast.error(rcErr.response?.data?.detail || 'Contract email failed', { id: 'edit-resend-contract' });
            }
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update loan');
    } finally {
      setSavingEditLoan(false);
    }
  };

  // One-tap: enable the $200/day late penalty meter on a loan. Per user request
  // 2026-02-24 — when the button is clicked the penalty accrues $200/day from
  // the day AFTER the due date (computed server-side, independent of when the
  // button was actually clicked). Also appends the human-readable clause to the
  // additional_terms so the borrower-facing PDF shows it.
  const handleAddLatePenalty = async (loan, perDay = 200) => {
    try {
      // 1. Rebuild the additional_terms clause with the picked rate (100 or 200).
      const newTerms = appendLatePenalty(loan.additional_terms, perDay);
      if (newTerms !== loan.additional_terms) {
        await axios.put(`${API}/api/loans/${loan.loan_number}/edit`, { additional_terms: newTerms });
      }
      // 2. Turn on the meter at the selected per-day rate.
      const resp = await axios.post(`${API}/api/loans/${loan.loan_number}/penalty`, { enabled: true, per_day: perDay });
      if (resp.data.success) {
        const p = resp.data.loan || {};
        const days = p.penalty_days || 0;
        const amt = p.penalty_amount || 0;
        if (days > 0) {
          toast.success(`Penalty enabled at $${perDay}/day · ${days} day(s) past due = $${amt.toLocaleString('en-US', { minimumFractionDigits: 2 })} accrued so far`);
        } else {
          toast.success(`Penalty meter enabled at $${perDay}/day — will start accruing the day after the due date`);
        }
        fetchLoanLedger();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable late penalty');
    }
  };

  // 2026-02-24 — Change the per-day rate on an ALREADY-enabled penalty
  // meter (100 ↔ 200).  Rewrites the clause in additional_terms so a
  // fresh contract PDF prints the correct number, and posts to the
  // penalty endpoint so `_annotate_loan_penalty` recomputes on read.
  const handleChangePenaltyRate = async (loan, newPerDay) => {
    try {
      const newTerms = appendLatePenalty(loan.additional_terms, newPerDay);
      if (newTerms !== loan.additional_terms) {
        await axios.put(`${API}/api/loans/${loan.loan_number}/edit`, { additional_terms: newTerms });
      }
      const resp = await axios.post(`${API}/api/loans/${loan.loan_number}/penalty`, { enabled: true, per_day: newPerDay });
      if (resp.data.success) {
        const amt = resp.data.loan?.penalty_amount || 0;
        toast.success(`Rate changed to $${newPerDay}/day · new penalty: $${amt.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
        fetchLoanLedger();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change penalty rate');
    }
  };

  // 2026-02-24 — Bulk switch every ACTIVE loan from one rate to another
  // (e.g. every $200/day loan → $100/day).  Behind a confirm() so it's
  // never accidental.  Only touches loans whose meter is turned ON.
  const handleBulkChangePenaltyRate = async (fromPerDay, toPerDay) => {
    if (!window.confirm(`Change ALL active loans currently at $${fromPerDay}/day → $${toPerDay}/day?\n\nThis rewrites the penalty clause on every affected loan and recalculates the running total.`)) return;
    try {
      const resp = await axios.post(`${API}/api/loans/bulk-update-penalty-rate`, {
        from_per_day: fromPerDay,
        to_per_day: toPerDay,
        only_enabled: true,
      });
      if (resp.data?.success) {
        toast.success(`Updated ${resp.data.modified} loan(s) from $${fromPerDay}/day → $${toPerDay}/day`);
        fetchLoanLedger();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk update failed');
    }
  };

  // Clear the penalty meter (e.g. if you turned it on by mistake or the
  // borrower paid the original due amount and you're removing the penalty
  // as a courtesy).
  const handleClearLatePenalty = async (loan) => {
    if (!window.confirm(`Clear the $200/day penalty on Loan #${loan.loan_number}?\nThe running tally will reset to $0.`)) return;
    try {
      const resp = await axios.post(`${API}/api/loans/${loan.loan_number}/penalty`, { enabled: false });
      if (resp.data.success) {
        toast.success('Penalty meter cleared');
        fetchLoanLedger();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear penalty');
    }
  };
  
  // Send Partial Payment Notice
  const handleSendPartialPaymentNotice = async (loan) => {
    if (loan.remaining_balance <= 0) {
      toast.message('Loan is fully paid — no partial notice needed');
      return;
    }
    if (!loan.borrower_email) {
      toast.error('No borrower email on file');
      return;
    }
    if (!window.confirm(`Email ${loan.borrower_name} a partial-payment reminder showing the remaining $${(loan.remaining_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}?`)) {
      return;
    }
    try {
      const resp = await axios.post(`${API}/api/loans/${loan.loan_number}/send-partial-payment-notice`, {});
      if (resp.data.success && resp.data.sent) {
        toast.success(`Partial-payment notice emailed to ${loan.borrower_email}`);
      } else if (resp.data.success) {
        toast.message('Notice recorded but email may have failed — check email config');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notice');
    }
  };

  // Resend promissory note for a loan
  // State for signing link modal
  const [showSigningLinkModal, setShowSigningLinkModal] = useState(false);
  const [currentSigningLink, setCurrentSigningLink] = useState('');
  const [currentLoanInfo, setCurrentLoanInfo] = useState({ loanNumber: '', borrowerName: '' });

  const handleResendNote = async (loanNumber, borrowerName) => {
    if (!window.confirm(`Send promissory note to ${borrowerName} for Loan #${loanNumber}?\n\nBoth you and the client will receive an email.`)) {
      return;
    }
    try {
      toast.info('Sending promissory note...');
      const response = await axios.post(`${API}/api/loan-ledger/resend-note/${loanNumber}`);
      if (response.data.success) {
        toast.success(response.data.message);
        // Show modal with signing link
        if (response.data.signing_link) {
          setCurrentSigningLink(response.data.signing_link);
          setCurrentLoanInfo({ loanNumber, borrowerName });
          setShowSigningLinkModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to resend note:', error);
      toast.error(error.response?.data?.detail || 'Failed to send promissory note');
    }
  };

  const copySigningLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentSigningLink);
      toast.success('Signing link copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = currentSigningLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Signing link copied!');
    }
  };

  // Reopen a closed loan
  const handleReopenLoan = async (loanNumber) => {
    try {
      const response = await axios.post(`${API}/api/loan-ledger/reopen/${loanNumber}`);
      if (response.data.success) {
        toast.success(`Loan #${loanNumber} reopened`);
        fetchLoanLedger();
      }
    } catch (error) {
      console.error('Failed to reopen loan:', error);
      toast.error('Failed to reopen loan');
    }
  };

  // Permanently delete a loan
  const handleDeleteLoanPermanently = async (loanNumber) => {
    if (!window.confirm(`PERMANENTLY DELETE Loan #${loanNumber}? This cannot be undone!`)) {
      return;
    }
    try {
      const response = await axios.delete(`${API}/api/loan-ledger/delete/${loanNumber}`);
      if (response.data.success) {
        toast.success(`Loan #${loanNumber} permanently deleted`);
        fetchLoanLedger();
      }
    } catch (error) {
      console.error('Failed to delete loan:', error);
      toast.error('Failed to delete loan');
    }
  };

  // Toggle loan selection for multi-delete
  const toggleLoanSelection = (loanNumber) => {
    setSelectedLoansForDelete(prev => 
      prev.includes(loanNumber) 
        ? prev.filter(n => n !== loanNumber)
        : [...prev, loanNumber]
    );
  };

  // Delete multiple loans at once
  const handleDeleteMultipleLoans = async () => {
    if (selectedLoansForDelete.length === 0) {
      toast.error('No loans selected');
      return;
    }
    if (!window.confirm(`PERMANENTLY DELETE ${selectedLoansForDelete.length} selected loan(s)? This cannot be undone!`)) {
      return;
    }
    try {
      let deleted = 0;
      for (const loanNumber of selectedLoansForDelete) {
        const response = await axios.delete(`${API}/api/loan-ledger/delete/${loanNumber}`);
        if (response.data.success) deleted++;
      }
      toast.success(`${deleted} loan(s) permanently deleted`);
      setSelectedLoansForDelete([]);
      setIsMultiDeleteMode(false);
      fetchLoanLedger();
    } catch (error) {
      console.error('Failed to delete loans:', error);
      toast.error('Failed to delete some loans');
      fetchLoanLedger();
    }
  };

  // Record a payment for a loan from the Loan Ledger
  const handleRecordLoanPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (!selectedLoan?.loan_number) {
      toast.error('No loan selected');
      return;
    }

    try {
      setIsRecordingPayment(true);
      const response = await axios.post(`${API}/api/loans/${selectedLoan.loan_number}/payment`, {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.paymentMethod || 'Not specified',
        notes: paymentForm.notes || ''
      });

      if (response.data.success) {
        toast.success(`Payment of $${parseFloat(paymentForm.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })} recorded!`);
        if (response.data.is_paid_in_full) {
          toast.success('🎉 Loan is now PAID IN FULL!', { duration: 5000 });
        }
        setPaymentForm({ amount: '', paymentMethod: '', notes: '' });
        setShowPaymentModal(false);
        setSelectedLoan(null);
        // Refresh the ledger to show updated balances
        fetchLoanLedger();
      }
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setIsRecordingPayment(false);
    }
  };

  // Mark a loan as Paid in Full
  const handleMarkPaidInFull = async (loan) => {
    if (!window.confirm(`Mark loan #${loan.loan_number} for ${loan.borrower_name} as PAID IN FULL?\n\nRemaining balance of $${loan.remaining_balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })} will be recorded as paid.`)) {
      return;
    }

    try {
      setIsMarkingPaid(true);
      const response = await axios.post(`${API}/api/loans/${loan.loan_number}/mark-paid-full`, {
        notes: 'Marked as paid in full manually'
      });

      if (response.data.success) {
        toast.success(`🎉 Loan #${loan.loan_number} marked as PAID IN FULL!`, { duration: 5000 });
        fetchLoanLedger();
      }
    } catch (error) {
      console.error('Failed to mark loan as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark loan as paid');
    } finally {
      setIsMarkingPaid(false);
    }
  };

  // Resend promissory note/contract for a specific loan
  const handleResendContract = async (loan) => {
    if (!loan.borrower_email) {
      toast.error('No email address on file for this borrower');
      return;
    }

    try {
      toast.loading('Resending contract...', { id: 'resend-contract' });
      const response = await axios.post(`${API}/api/loans/${loan.loan_number}/resend-contract`);
      
      if (response.data.success) {
        toast.success(`Contract resent to ${loan.borrower_email}!`, { id: 'resend-contract' });
      }
    } catch (error) {
      console.error('Failed to resend contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend contract', { id: 'resend-contract' });
    }
  };

  // Cancel a loan due to a contract error — voids the promissory note and
  // emails the borrower a "no payment due" notice.  Requested by user
  // 2026-02-27 after they had to void a loan where the payback amount had
  // been typed incorrectly.
  const handleCancelLoanContractError = async (loan) => {
    if (!window.confirm(
      `Cancel Loan #${loan.loan_number} for ${loan.borrower_name}?\n\n` +
      `This will:\n` +
      `  • Void the promissory note\n` +
      `  • Zero the balance owed\n` +
      `  • Email ${loan.borrower_email || 'the borrower'} confirming they owe nothing\n\n` +
      `Continue?`
    )) return;

    const reason = window.prompt(
      'Optional — brief reason to include in the email (e.g. "Payback amount was entered incorrectly"). Leave blank to skip:',
      ''
    );
    // window.prompt returns null on cancel — treat that as abort.
    if (reason === null) return;

    try {
      toast.loading('Cancelling loan…', { id: 'cancel-loan' });
      const response = await axios.post(
        `${API}/api/loans/${loan.loan_number}/cancel-contract-error`,
        { reason: (reason || '').trim() },
      );
      if (response.data.success) {
        toast.success(response.data.message || 'Loan cancelled', { id: 'cancel-loan' });
        fetchLoanLedger();
      }
    } catch (error) {
      console.error('Failed to cancel loan:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel loan', { id: 'cancel-loan' });
    }
  };

  // Rollover a loan - transfer remaining balance to a new loan
  const handleRolloverLoan = async () => {
    if (!selectedLoan) {
      toast.error('No loan selected for rollover');
      return;
    }

    const paymentBefore = parseFloat(rolloverForm.paymentBeforeRollover || 0);
    const rolloverAmt = parseFloat(rolloverForm.rolloverAmount || 0);
    
    // Calculate what the remaining balance should be after payment
    const expectedRemaining = (selectedLoan.remaining_balance || 0) - paymentBefore;
    
    if (rolloverAmt <= 0) {
      toast.error('Please enter a valid rollover amount');
      return;
    }

    if (rolloverAmt > expectedRemaining) {
      toast.error('Rollover amount cannot exceed remaining balance after payment');
      return;
    }

    if (!rolloverForm.newAmountReturn || parseFloat(rolloverForm.newAmountReturn) <= 0) {
      toast.error('Please enter the new payback amount');
      return;
    }

    if (!rolloverForm.newDateReturn) {
      toast.error('Please enter the new due date');
      return;
    }

    if (!rolloverForm.newBorrowerEmail && rolloverForm.sendContract) {
      toast.error('Please enter borrower email to send the contract');
      return;
    }

    try {
      setIsProcessingRollover(true);
      const response = await axios.post(`${API}/api/loans/${selectedLoan.loan_number}/rollover`, {
        payment_before_rollover: paymentBefore,
        rollover_amount: rolloverAmt,
        new_borrower_name: rolloverForm.newBorrowerName || selectedLoan.borrower_name,
        new_borrower_email: rolloverForm.newBorrowerEmail || selectedLoan.borrower_email,
        new_borrower_phone: rolloverForm.newBorrowerPhone || '',
        additional_amount: parseFloat(rolloverForm.additionalAmount || 0),
        new_amount_return: parseFloat(rolloverForm.newAmountReturn),
        new_date_lent: rolloverForm.newDateLent || new Date().toISOString().split('T')[0],
        new_date_return: rolloverForm.newDateReturn,
        interest_rate: rolloverForm.interestRate || '',
        notes: rolloverForm.notes || '',
        send_contract: rolloverForm.sendContract
      });

      if (response.data.success) {
        toast.success(`Loan rolled over! New loan #${response.data.new_loan_number} created.`, { duration: 5000 });
        if (response.data.contract_sent) {
          toast.success(`Contract sent to ${rolloverForm.newBorrowerEmail || selectedLoan.borrower_email}`, { duration: 4000 });
        }
        setShowRolloverModal(false);
        setSelectedLoan(null);
        setRolloverForm({
          paymentBeforeRollover: '',
          rolloverAmount: '',
          newBorrowerName: '',
          newBorrowerEmail: '',
          newBorrowerPhone: '',
          additionalAmount: '',
          newAmountReturn: '',
          newDateReturn: '',
          newDateLent: '',
          interestRate: '',
          notes: '',
          sendContract: true
        });
        fetchLoanLedger();
      }
    } catch (error) {
      console.error('Failed to rollover loan:', error);
      toast.error(error.response?.data?.detail || 'Failed to rollover loan');
    } finally {
      setIsProcessingRollover(false);
    }
  };

  // Handle Missed Payment - simple flow: interest + new date → send signable promissory note
  const handleMissedPayment = async () => {
    if (!selectedLoan) {
      toast.error('No loan selected');
      return;
    }
    
    const interestAmt = parseFloat(missedPaymentForm.interestAmount || 0);
    if (interestAmt < 0) {
      toast.error('Interest cannot be negative');
      return;
    }
    
    if (!missedPaymentForm.newDateReturn) {
      toast.error('Please enter the new payment date');
      return;
    }
    
    const remainingBalance = selectedLoan.remaining_balance || selectedLoan.amount_return || 0;
    const newTotal = remainingBalance + interestAmt;
    
    try {
      setIsProcessingMissedPayment(true);
      
      const response = await axios.post(`${API}/api/loans/${selectedLoan.loan_number}/rollover`, {
        payment_before_rollover: 0,
        rollover_amount: remainingBalance,
        new_borrower_name: selectedLoan.borrower_name,
        new_borrower_email: selectedLoan.borrower_email || '',
        new_borrower_phone: selectedLoan.borrower_phone || '',
        additional_amount: 0,
        new_amount_return: newTotal,
        new_date_return: missedPaymentForm.newDateReturn,
        new_date_lent: new Date().toISOString().split('T')[0],
        interest_rate: interestAmt > 0 ? `$${interestAmt.toFixed(2)} late fee` : '',
        notes: missedPaymentForm.notes || `Missed payment on original due date. Interest of $${interestAmt.toFixed(2)} charged.`,
        send_contract: true
      });
      
      if (response.data.success) {
        toast.success(`Missed payment processed! New promissory note #${response.data.new_loan_number} sent to ${selectedLoan.borrower_name} for signing.`, { duration: 6000 });
        setShowMissedPaymentModal(false);
        setMissedPaymentForm({ interestAmount: '', newDateReturn: '', notes: '' });
        setSelectedLoan(null);
        fetchLoanLedger();
        fetchLoans();
      }
    } catch (error) {
      console.error('Failed to process missed payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to process missed payment');
    } finally {
      setIsProcessingMissedPayment(false);
    }
  };

  // Cleanup all test loans - keep only Norma Ramirez
  const handleCleanupTestLoans = async () => {
    if (!window.confirm('Delete ALL test loans and keep ONLY Norma Ramirez loans? This cannot be undone!')) {
      return;
    }
    try {
      const response = await axios.post(`${API}/api/loan-ledger/cleanup-test-loans`);
      if (response.data.success) {
        toast.success(`Cleaned up ${response.data.deleted_count} test loans. ${response.data.remaining_loans} loans remaining.`);
        fetchLoanLedger();
      }
    } catch (error) {
      console.error('Failed to cleanup test loans:', error);
      toast.error('Failed to cleanup test loans');
    }
  };

  // Add a manual loan entry
  const handleAddManualLoan = async () => {
    if (!manualLoanForm.borrowerName || !manualLoanForm.amountLent || !manualLoanForm.amountReturn) {
      toast.error('Please fill in borrower name, amount lent, and payback amount');
      return;
    }
    try {
      const response = await axios.post(`${API}/api/loan-ledger/add-manual`, {
        borrower_name: manualLoanForm.borrowerName,
        borrower_email: manualLoanForm.borrowerEmail,
        amount_lent: parseFloat(manualLoanForm.amountLent),
        amount_return: parseFloat(manualLoanForm.amountReturn),
        date_lent: manualLoanForm.dateLent,
        date_return: manualLoanForm.dateReturn,
        notes: manualLoanForm.notes
      });
      if (response.data.success) {
        toast.success(`Loan #${response.data.loan_number} added to ledger`);
        setShowAddLoanModal(false);
        setManualLoanForm({
          borrowerName: '',
          borrowerEmail: '',
          amountLent: '',
          amountReturn: '',
          dateLent: '',
          dateReturn: '',
          notes: ''
        });
        fetchLoanLedger();
      }
    } catch (error) {
      console.error('Failed to add loan:', error);
      toast.error('Failed to add loan');
    }
  };

  // Get detailed loan info
  const viewLoanDetail = async (loanNumber) => {
    try {
      const response = await axios.get(`${API}/api/loans/${loanNumber}`);
      if (response.data.success) {
        setSelectedLoan(response.data.loan);
      }
    } catch (error) {
      console.error('Failed to get loan detail:', error);
      toast.error('Failed to load loan details');
    }
  };

  // Mark loan as paid and send confirmation
  const handleMarkAsPaidAndNotify = async () => {
    if (!selectedLoan) return;
    
    try {
      setIsSendingConfirmation(true);
      
      // Update loan status to paid
      await axios.patch(`${API}/api/loans/${selectedLoan.loan_number}/status`, {
        status: 'paid',
        notes: 'Loan marked as paid in full'
      });
      
      // Send the paid confirmation email
      const response = await axios.post(`${API}/api/loans/${selectedLoan.loan_number}/send-paid-confirmation`);
      
      if (response.data.success) {
        toast.success(`Paid confirmation sent to you and ${selectedLoan.borrower_name}!`);
        // Refresh loan details
        viewLoanDetail(selectedLoan.loan_number);
        fetchLoans();
      }
    } catch (error) {
      console.error('Failed to send confirmation:', error);
      toast.error(error.response?.data?.detail || 'Failed to send confirmation');
    } finally {
      setIsSendingConfirmation(false);
    }
  };

  // Send Paid Confirmation Email for a specific loan (when loan is already paid)
  const handleSendPaidConfirmationEmail = async (loan) => {
    if (!loan?.loan_number) {
      toast.error('No loan selected');
      return;
    }
    
    if (!loan.borrower_email) {
      toast.error('No borrower email on file for this loan');
      return;
    }
    
    try {
      setIsSendingConfirmation(true);
      
      const response = await axios.post(`${API}/api/loans/${loan.loan_number}/send-paid-confirmation`);
      
      if (response.data.success) {
        toast.success(`Paid confirmation email sent to ${loan.borrower_name} (${loan.borrower_email})!`);
      }
    } catch (error) {
      console.error('Failed to send paid confirmation:', error);
      toast.error(error.response?.data?.detail || 'Failed to send paid confirmation email');
    } finally {
      setIsSendingConfirmation(false);
    }
  };

  // Send payment reminder to borrower (manual trigger)
  const handleSendPaymentReminder = async (loan) => {
    if (!loan?.loan_number) {
      toast.error('No loan selected');
      return;
    }
    
    if (!loan.borrower_email) {
      toast.error('No borrower email on file for this loan');
      return;
    }
    
    try {
      setIsSendingConfirmation(true);
      
      const response = await axios.post(`${API}/api/loan-ledger/send-single-reminder/${loan.loan_number}`);
      
      if (response.data.success) {
        toast.success(`Payment reminder sent to ${loan.borrower_name} (${loan.borrower_email})!`);
      }
    } catch (error) {
      console.error('Failed to send reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to send payment reminder');
    } finally {
      setIsSendingConfirmation(false);
    }
  };

  // Send payment confirmation without changing status
  const handleSendPaymentConfirmation = async () => {
    if (!selectedLoan) return;
    
    try {
      setIsSendingConfirmation(true);
      
      const response = await axios.post(`${API}/api/loans/${selectedLoan.loan_number}/send-payment-summary`);
      
      if (response.data.success) {
        toast.success(`Payment summary sent to you and ${selectedLoan.borrower_name}!`);
      }
    } catch (error) {
      console.error('Failed to send summary:', error);
      toast.error(error.response?.data?.detail || 'Failed to send payment summary');
    } finally {
      setIsSendingConfirmation(false);
    }
  };

  // Send Credit Repair Paid Receipt
  const handleSendCreditRepairReceipt = async (contract) => {
    try {
      setIsSendingConfirmation(true);
      
      const response = await axios.post(`${API}/api/contracts/${contract.id}/send-paid-receipt`);
      
      if (response.data.success) {
        toast.success(`Paid receipt sent to you and ${contract.client_name}!`);
        // Refresh contract details
        viewContractDetail(contract.id);
      }
    } catch (error) {
      console.error('Failed to send receipt:', error);
      toast.error(error.response?.data?.detail || 'Failed to send receipt');
    } finally {
      setIsSendingConfirmation(false);
    }
  };

  // Record payment for any contract (not just loans)
  const handleRecordContractPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (!selectedContract) {
      toast.error('No contract selected');
      return;
    }

    try {
      setIsRecordingPayment(true);
      
      const response = await axios.post(`${API}/api/contracts/${selectedContract.id}/payment`, {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.paymentMethod || 'Not specified',
        notes: paymentForm.notes
      });

      if (response.data.success) {
        toast.success(`Payment of $${paymentForm.amount} recorded!`);
        setPaymentForm({ amount: '', paymentMethod: '', notes: '' });
        setShowPaymentModal(false);
        // Refresh contract details
        viewContractDetail(selectedContract.id);
        fetchContracts();
      }
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setIsRecordingPayment(false);
    }
  };

  const generatePromissoryNote = () => {
    const { borrowerName, lenderName, dateLent, dateReturn, amountLent, amountReturn, additionalTerms } = promissoryForm;
    
    return `PROMISSORY NOTE

Date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}

FOR VALUE RECEIVED, the undersigned borrower promises to pay to the order of:

LENDER: ${lenderName}
BORROWER: ${borrowerName}

PRINCIPAL AMOUNT: $${parseFloat(amountLent).toLocaleString('en-US', { minimumFractionDigits: 2 })}

The Borrower agrees to repay the Lender the total sum of:

AMOUNT TO BE REPAID: $${parseFloat(amountReturn).toLocaleString('en-US', { minimumFractionDigits: 2 })}

LOAN DATE: ${dateLent}
REPAYMENT DUE DATE: ${dateReturn}

TERMS AND CONDITIONS:

1. The Borrower promises to pay the above amount to the Lender on or before the Repayment Due Date.

2. If the Borrower fails to make payment by the due date, the Borrower shall be in default and the entire unpaid balance shall become immediately due and payable.

3. The Borrower agrees to pay all costs of collection, including reasonable attorney's fees, in the event of default.

4. This Note shall be governed by and construed in accordance with the laws of the state in which the Lender resides.

5. Any amendment to this Note must be in writing and signed by both parties.

${additionalTerms ? `ADDITIONAL TERMS:\n${additionalTerms}\n` : ''}
ACKNOWLEDGMENT:

By signing below, I, ${borrowerName}, acknowledge that I have read, understand, and agree to all terms and conditions of this Promissory Note. I understand this is a legally binding document.

This Promissory Note is executed as of the date first written above.`;
  };

  const generateCreditRepairContract = () => {
    const { clientName, downPayment, remainingBalance, totalAmount, paymentDueDate, additionalTerms } = creditRepairForm;
    
    // Handle empty/undefined values - default to 0, display as whole numbers
    const downPaymentNum = parseInt(downPayment) || 0;
    const remainingBalanceNum = parseInt(remainingBalance) || 0;
    const totalAmountNum = parseInt(totalAmount) || 0;
    
    return `CREDIT REPAIR SERVICE AGREEMENT

Date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}

This Credit Repair Service Agreement ("Agreement") is entered into between:

SERVICE PROVIDER: Clear All Credit Repair
CLIENT: ${clientName}

PAYMENT TERMS:

Down Payment: $${downPaymentNum.toLocaleString('en-US')}
Remaining Balance: $${remainingBalanceNum.toLocaleString('en-US')}
Total Service Fee: $${totalAmountNum.toLocaleString('en-US')}
${paymentDueDate ? `Remaining Balance Due By: ${paymentDueDate}` : ''}

SERVICES INCLUDE:

1. Credit report analysis
2. Dispute letter preparation and submission
3. Upon step three Friday task explanation on status and results

CLIENT ACKNOWLEDGES:

• Payment is required as outlined above
• Services begin upon receipt of down payment

CLIENT RESPONSIBILITIES AND COMPLIANCE:

The Client agrees to the following terms and conditions:

1. COMMUNICATION COMPLIANCE: The Client MUST follow all email instructions and respond to all communications from Clear All Credit Repair within 48 hours of receipt.

2. DOCUMENT SUBMISSION: The Client must provide all requested documents (ID, proof of address, credit reports, etc.) in a timely manner as instructed.

3. FOLLOW-UP ACTIONS: The Client must complete any action items assigned via email, including but not limited to:
   • Reviewing dispute letters before submission
   • Providing updated credit reports when requested
   • Responding to creditor communications as directed
   • Maintaining open lines of communication

4. PAYMENT COMPLIANCE: All payments must be made on time according to the payment schedule outlined above.

FORFEITURE CLAUSE:

⚠️ IMPORTANT: If the Client fails to comply with the credit repair process, including but not limited to:
   • Not responding to emails within 48 hours
   • Failing to provide requested documents
   • Not following through with assigned action items
   • Missing scheduled payments

The Client's fees are NON-REFUNDABLE and considered FORFEITED due to non-compliance with the credit repair process. Clear All Credit Repair cannot guarantee results if the Client does not actively participate in the process.

${additionalTerms ? `ADDITIONAL TERMS:\n${additionalTerms}\n` : ''}
ACKNOWLEDGMENT:

By signing below, I, ${clientName}, acknowledge that I have read, understand, and agree to all terms and conditions of this Credit Repair Service Agreement. I understand that my compliance is required and that failure to comply may result in forfeiture of all fees paid.`;
  };

  const handleSendPromissoryNote = async () => {
    const { borrowerName, borrowerEmail, dateLent, dateReturn, amountLent, amountReturn } = promissoryForm;
    
    if (!borrowerName || !borrowerEmail || !dateLent || !dateReturn || !amountLent || !amountReturn) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate signature style is selected
    if (!selectedSignatureStyle) {
      toast.error('Please select a signature style for the contract');
      return;
    }

    try {
      setIsSending(true);
      const contractText = generatePromissoryNote();
      const lenderSignature = getLenderSignatureData();
      
      const response = await axios.post(`${API}/api/promise-to-pay/create`, {
        client_email: borrowerEmail,
        client_name: borrowerName,
        contract_text: contractText,
        contract_type: 'promissory_note',
        lender_signature: lenderSignature,
        lender_email: promissoryForm.lenderEmail,  // Send lender email for receiving signed copy
        metadata: {
          lender_name: promissoryForm.lenderName,
          lender_email: promissoryForm.lenderEmail,
          amount_lent: promissoryForm.amountLent,
          amount_return: promissoryForm.amountReturn,
          date_lent: dateLent,
          date_return: dateReturn,
          additional_terms: promissoryForm.additionalTerms || null,
          // 2026-02-24 — Pass the picked per-day rate so the backend stores
          // it on the loan doc for `_annotate_loan_penalty` to read.  Falls
          // back to 200 when the clause was never added, but the backend
          // ignores this value unless the additional_terms actually
          // contain the "LATE PAYMENT PENALTY" clause.
          late_penalty_per_day: promissoryForm.latePenaltyPerDay || 200,
        }
      });

      if (response.data.success) {
        const loanNumber = response.data.loan_number;
        toast.success(`Promissory Note sent to ${borrowerEmail}${loanNumber ? ` - Loan #${loanNumber}` : ''}`);
        // Store the signing link so user can copy it
        if (response.data.signing_link) {
          setSigningLink(response.data.signing_link);
        }
        setLinkCopied(false);
        setContractSent(true);
        // DON'T reset form or navigate away - let user copy the link first!
        // The "Done" button will handle cleanup when user is ready
      }
    } catch (error) {
      console.error('Failed to send contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to send contract');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendCreditRepairContract = async () => {
    const { clientName, clientEmail, downPayment, remainingBalance } = creditRepairForm;
    
    if (!clientName || !clientEmail || !downPayment) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setIsSending(true);
      const contractText = generateCreditRepairContract();
      
      const response = await axios.post(`${API}/api/promise-to-pay/create`, {
        client_email: clientEmail,
        client_name: clientName,
        contract_text: contractText,
        contract_type: 'credit_repair',
        metadata: {
          down_payment: creditRepairForm.downPayment,
          remaining_balance: creditRepairForm.remainingBalance,
          total_amount: creditRepairForm.totalAmount,
          payment_due_date: creditRepairForm.paymentDueDate
        }
      });

      if (response.data.success) {
        toast.success(`Credit Repair Contract sent to ${clientEmail}`);
        setContractSent(true);  // Mark contract as sent but DON'T close the form
        // Store the signing link so user can copy it
        if (response.data.signing_link) {
          setSigningLink(response.data.signing_link);
        }
        setLinkCopied(false);
        // Don't reset - let user send receipt if needed
      }
    } catch (error) {
      console.error('Failed to send contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to send contract');
    } finally {
      setIsSending(false);
    }
  };

  // Send receipt only (without contract for signing)
  const handleSendReceipt = async () => {
    const { clientName, clientEmail, downPayment, remainingBalance, totalAmount, paymentDueDate, paymentMethod } = creditRepairForm;
    
    if (!clientName || !clientEmail || !downPayment) {
      toast.error('Please fill in client name, email, and down payment');
      return;
    }
    
    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    try {
      setIsSendingReceipt(true);
      
      const response = await axios.post(`${API}/api/credit-repair/send-receipt`, {
        client_name: clientName,
        client_email: clientEmail,
        down_payment: parseInt(downPayment) || 0,
        remaining_balance: parseInt(remainingBalance) || 0,
        total_amount: parseInt(totalAmount) || parseInt(downPayment) || 0,
        is_paid_in_full: isPaidInFull,
        payment_due_date: paymentDueDate || null,
        payment_method: paymentMethod
      });

      if (response.data.success) {
        toast.success(`Receipt sent to ${clientEmail}! Receipt #${response.data.receipt_number}`);
        setReceiptSent(true);  // Mark receipt as sent
      }
    } catch (error) {
      console.error('Failed to send receipt:', error);
      toast.error(error.response?.data?.detail || 'Failed to send receipt');
    } finally {
      setIsSendingReceipt(false);
    }
  };

  // Reset form and go back to selection
  const handleDoneWithCreditRepair = () => {
    setCreditRepairForm({
      clientName: '',
      clientEmail: '',
      downPayment: '',
      remainingBalance: '',
      totalAmount: '',
      paymentDueDate: '',
      additionalTerms: '',
      paymentMethod: ''
    });
    setIsPaidInFull(false);
    setContractSent(false);
    setReceiptSent(false);
    setSigningLink('');
    setLinkCopied(false);
    setActiveTab('select');
    fetchContracts();
  };

  // ============================================
  // PAYMENT PLAN HANDLERS
  // ============================================
  
  // Fetch existing payment plans
  const fetchPaymentPlans = async () => {
    setLoadingPaymentPlans(true);
    try {
      const response = await axios.get(`${API}/api/payment-plans`);
      setPaymentPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching payment plans:', error);
      toast.error('Failed to load payment plans');
    } finally {
      setLoadingPaymentPlans(false);
    }
  };

  // Create a new payment plan
  const handleCreatePaymentPlan = async () => {
    if (!paymentPlanForm.clientName || !paymentPlanForm.clientEmail || !paymentPlanForm.totalBalance || !paymentPlanForm.paymentAmount || !paymentPlanForm.startDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsCreatingPlan(true);
    try {
      const response = await axios.post(`${API}/api/payment-plans/create`, {
        client_name: paymentPlanForm.clientName,
        client_email: paymentPlanForm.clientEmail,
        total_balance: parseInt(paymentPlanForm.totalBalance),
        down_payment: parseInt(paymentPlanForm.downPayment || 0),
        payment_amount: parseInt(paymentPlanForm.paymentAmount),
        payment_frequency: paymentPlanForm.paymentFrequency,
        start_date: paymentPlanForm.startDate,
        notes: paymentPlanForm.notes
      });

      if (response.data.success) {
        toast.success(`Payment plan created! First reminder sent to ${paymentPlanForm.clientEmail}`);
        // Reset form
        setPaymentPlanForm({
          clientName: '',
          clientEmail: '',
          totalBalance: '',
          downPayment: '',
          paymentAmount: '',
          paymentFrequency: 'biweekly',
          startDate: '',
          notes: ''
        });
        // Switch to logs view
        setActiveTab('clients-ledger');
        fetchPaymentPlans();
      } else {
        throw new Error(response.data.error || 'Failed to create payment plan');
      }
    } catch (error) {
      console.error('Error creating payment plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to create payment plan');
    } finally {
      setIsCreatingPlan(false);
    }
  };

  // Handle Request Payment (one-time payment request)
  const handleCreateRequestPayment = async () => {
    if (!requestPaymentForm.clientName || !requestPaymentForm.clientEmail || !requestPaymentForm.amount) {
      toast.error('Please fill in client name, email, and amount');
      return;
    }

    setIsCreatingRequest(true);
    try {
      const response = await axios.post(`${API}/api/payment-plans/create`, {
        client_name: requestPaymentForm.clientName,
        client_email: requestPaymentForm.clientEmail,
        total_balance: parseInt(requestPaymentForm.amount),
        down_payment: 0,
        payment_amount: parseInt(requestPaymentForm.amount),
        payment_frequency: requestPaymentForm.reminderFrequency === 'once' ? 'monthly' : requestPaymentForm.reminderFrequency,
        start_date: requestPaymentForm.dueDate || new Date().toISOString().split('T')[0],
        notes: requestPaymentForm.notes || 'One-time payment request',
        is_one_time: true
      });

      if (response.data.success) {
        toast.success(`Payment request sent to ${requestPaymentForm.clientEmail}!`);
        // Reset form
        setRequestPaymentForm({
          clientName: '',
          clientEmail: '',
          amount: '',
          dueDate: '',
          notes: '',
          reminderFrequency: 'once'
        });
        // Switch to clients ledger
        setActiveTab('clients-ledger');
        fetchPaymentPlans();
      } else {
        throw new Error(response.data.error || 'Failed to create payment request');
      }
    } catch (error) {
      console.error('Error creating payment request:', error);
      toast.error(error.response?.data?.detail || 'Failed to send payment request');
    } finally {
      setIsCreatingRequest(false);
    }
  };

  // AI Schedule Parser
  const handleAISchedule = async () => {
    if (!aiScheduleInput.trim()) return;

    setIsProcessingAI(true);
    try {
      const response = await axios.post(`${API}/api/payment-plans/parse-schedule`, {
        text: aiScheduleInput
      });

      if (response.data.success) {
        const parsed = response.data.parsed;
        setPaymentPlanForm({
          ...paymentPlanForm,
          clientName: parsed.client_name || paymentPlanForm.clientName,
          totalBalance: parsed.total_balance?.toString() || paymentPlanForm.totalBalance,
          downPayment: parsed.down_payment?.toString() || paymentPlanForm.downPayment,
          paymentAmount: parsed.payment_amount?.toString() || paymentPlanForm.paymentAmount,
          paymentFrequency: parsed.payment_frequency || paymentPlanForm.paymentFrequency,
          startDate: parsed.start_date || paymentPlanForm.startDate
        });
        toast.success('Schedule parsed! Review and adjust if needed.');
        setAiScheduleInput('');
      } else {
        throw new Error(response.data.error || 'Failed to parse schedule');
      }
    } catch (error) {
      console.error('Error parsing schedule:', error);
      toast.error('Could not parse schedule. Please enter details manually.');
    } finally {
      setIsProcessingAI(false);
    }
  };

  // Load payment plans when tab changes
  useEffect(() => {
    if (isOpen && activeTab === 'payment-plan-logs') {
      fetchPaymentPlans();
    }
  }, [isOpen, activeTab]);

  // Send instant reminder
  const handleSendInstantReminder = async (plan) => {
    setSendingInstantReminder(plan.plan_id);
    try {
      const response = await axios.post(`${API}/api/payment-plans/send-instant-reminder/${plan.plan_id}`);
      if (response.data.success) {
        toast.success(`Reminder sent to ${plan.client_name}!`);
      } else {
        throw new Error(response.data.error || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending instant reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setSendingInstantReminder(null);
    }
  };

  // Copy payment link for texting
  const handleCopyPaymentLink = async (plan) => {
    const message = `Hi ${plan.client_name}, this is a reminder that your payment of $${plan.payment_amount} is due. Please send a screenshot of your payment so we can update your balance.\n\nPayment Options (FREE):\n- Apple Pay: 619-253-9785\n- Zelle: srsworldLLC@gmail.com\n- Venmo: Clearall-Creditrepairs-1\n\nPayment Options (+$3 per $100):\n- PayPal: mrhunbun1@gmail.com\n- Cash App: $CLEARALLCR\n\nRemaining Balance: $${plan.remaining_balance}`;
    
    try {
      await navigator.clipboard.writeText(message);
      setCopiedLink(plan.plan_id);
      toast.success('Message copied! You can paste it in your text app.');
      setTimeout(() => setCopiedLink(null), 3000);
    } catch (error) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = message;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLink(plan.plan_id);
      toast.success('Message copied!');
      setTimeout(() => setCopiedLink(null), 3000);
    }
  };

  // Log a payment - show confirmation modal
  const handleLogPayment = async (plan) => {
    if (!paymentLogAmount || parseInt(paymentLogAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    // Show confirmation modal
    setPaymentConfirmData({
      plan: plan,
      amount: parseInt(paymentLogAmount),
      newRemaining: Math.max(0, (plan.remaining_balance || 0) - parseInt(paymentLogAmount)),
      newTotalPaid: (plan.total_paid || 0) + parseInt(paymentLogAmount)
    });
    setShowPaymentConfirm(plan.plan_id);
    setNextPaymentDate('');
  };

  // Confirm and send payment receipt
  const handleConfirmPayment = async (sendReceipt = true) => {
    if (!paymentConfirmData) return;
    
    setSendingReceipt(true);
    try {
      const response = await axios.post(`${API}/api/payment-plans/record-payment/${paymentConfirmData.plan.plan_id}`, {
        amount: paymentConfirmData.amount,
        send_receipt: sendReceipt,
        next_payment_date: nextPaymentDate || null
      });
      
      if (response.data.success) {
        toast.success(`Payment of $${paymentConfirmData.amount} recorded! ${sendReceipt ? 'Receipt sent to client.' : ''}`);
        setPaymentLogAmount('');
        setShowPaymentConfirm(null);
        setPaymentConfirmData(null);
        setNextPaymentDate('');
        fetchPaymentPlans();
      } else {
        throw new Error(response.data.error || 'Failed to log payment');
      }
    } catch (error) {
      console.error('Error logging payment:', error);
      toast.error('Failed to log payment');
    } finally {
      setSendingReceipt(false);
      setLoggingPayment(null);
    }
  };

  // Copy signing link to clipboard
  const handleCopyLink = async () => {
    if (!signingLink) return;
    try {
      await navigator.clipboard.writeText(signingLink);
      setLinkCopied(true);
      toast.success('Link copied! You can text it to the client.');
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = signingLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      toast.success('Link copied! You can text it to the client.');
      setTimeout(() => setLinkCopied(false), 3000);
    }
  };

  const handleDeleteContract = async (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;

    try {
      await axios.delete(`${API}/api/promise-to-pay/${contractId}`);
      toast.success('Contract deleted');
      fetchContracts();
      setSelectedContract(null);
    } catch (error) {
      console.error('Failed to delete contract:', error);
      toast.error('Failed to delete contract');
    }
  };

  const viewContractDetail = async (contractId) => {
    try {
      const response = await axios.get(`${API}/api/promise-to-pay/detail/${contractId}`);
      if (response.data.success) {
        setSelectedContract(response.data.contract);
      }
    } catch (error) {
      console.error('Failed to get contract detail:', error);
      toast.error('Failed to load contract details');
    }
  };

  // Record a payment against a loan
  const handleRecordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (!selectedContract?.loan_number) {
      toast.error('No loan number found');
      return;
    }

    try {
      setIsRecordingPayment(true);
      const response = await axios.post(`${API}/api/loans/${selectedContract.loan_number}/payment`, {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.paymentMethod || 'Not specified',
        notes: paymentForm.notes
      });

      if (response.data.success) {
        toast.success(`Payment recorded! Confirmation: ${response.data.confirmation_number}`);
        if (response.data.is_paid_in_full) {
          toast.success('Loan is now PAID IN FULL!');
        }
        setPaymentForm({ amount: '', paymentMethod: '', notes: '' });
        setShowPaymentModal(false);
        // Refresh contract details
        viewContractDetail(selectedContract.id);
      }
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setIsRecordingPayment(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Format date as MM/DD/YY
  const formatDateShort = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      // Handle different date formats
      let date;
      
      // If already in MM/DD/YY or M/D/YY format
      if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{2,4}$/)) {
        const parts = dateStr.split('/');
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        let year = parts[2];
        if (year.length === 2) {
          year = '20' + year;
        }
        return `${month}/${day}/${year.slice(-2)}`;
      }
      
      // If in YYYY-MM-DD format
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
        date = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
      } else {
        date = new Date(dateStr);
      }
      
      if (isNaN(date.getTime())) {
        return dateStr; // Return original if can't parse
      }
      
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${month}/${day}/${year}`;
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/20 text-gray-300',
      sent: 'bg-blue-500/20 text-blue-300',
      viewed: 'bg-yellow-500/20 text-yellow-300',
      signed: 'bg-green-500/20 text-green-300'
    };
    const labels = {
      draft: 'Draft',
      sent: 'Sent',
      viewed: 'Viewed',
      signed: 'Signed'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Promissory Note signature badge — RED when SENT (awaiting signature),
  // GREEN once the borrower has signed and returned the note. Drives the
  // "Promissory Note Sent / Signed" indicator the user wants to see in
  // the active-loan rows and the full loan-detail page.
  const getPromissoryNoteBadge = (loan, size = 'sm') => {
    if (!loan) return null;
    const signed = !!loan.signed_at || ['signed', 'paid', 'paid_in_full'].includes(loan.status);
    const padding = size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2 py-1 text-[11px]';
    if (signed) {
      const signedLabel = loan.signed_at
        ? `Promissory Note Signed · ${formatDateShort(loan.signed_at)}`
        : 'Promissory Note Signed';
      return (
        <span
          data-testid={`promissory-note-badge-signed-${loan.loan_number || ''}`}
          className={`inline-flex items-center gap-1.5 rounded-full font-semibold bg-green-500/15 text-green-400 border border-green-500/40 ${padding}`}
          title={signedLabel}
        >
          <CheckCircle className="w-3.5 h-3.5" />
          {signedLabel}
        </span>
      );
    }
    return (
      <span
        data-testid={`promissory-note-badge-sent-${loan.loan_number || ''}`}
        className={`inline-flex items-center gap-1.5 rounded-full font-semibold bg-red-500/15 text-red-400 border border-red-500/40 ${padding}`}
        title="Promissory note sent — awaiting borrower signature"
      >
        <Send className="w-3.5 h-3.5" />
        Promissory Note Sent
      </span>
    );
  };

  const getContractTypeBadge = (type) => {
    if (type === 'promissory_note') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300">Promissory Note</span>;
    } else if (type === 'credit_repair') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300">Credit Repair</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-300">Contract</span>;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-1 sm:p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: 'calc(100dvh - 1rem)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {/* Header */}
        <div className="p-6 border-b border-zinc-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Contracts & Agreements</h2>
              <p className="text-sm text-zinc-400">Create promissory notes and credit repair contracts</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap border-b border-zinc-700 text-xs">
          <button
            onClick={() => setActiveTab('select')}
            className={`flex-1 min-w-[60px] px-2 py-3 font-medium transition-colors ${
              activeTab === 'select'
                ? 'text-green-400 border-b-2 border-green-400 bg-green-500/10'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <Send className="w-4 h-4 inline mr-1" />
            New
          </button>
          <button
            onClick={() => { setActiveTab('ledger'); fetchLoanLedger(); }}
            className={`flex-1 min-w-[60px] px-2 py-3 font-medium transition-colors ${
              activeTab === 'ledger'
                ? 'text-amber-400 border-b-2 border-amber-400 bg-amber-500/10'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <DollarSign className="w-4 h-4 inline mr-1" />
            Loans
          </button>
          <button
            onClick={() => { setActiveTab('promissory-logs'); fetchContracts(); }}
            className={`flex-1 min-w-[60px] px-2 py-3 font-medium transition-colors ${
              activeTab === 'promissory-logs'
                ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-500/10'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-1" />
            SIGNED CONTRACTS
          </button>
          <button
            onClick={() => { setActiveTab('quick-reminder'); fetchQuickReminders(); }}
            className={`flex-1 min-w-[60px] px-2 py-3 font-medium transition-colors ${
              activeTab === 'quick-reminder'
                ? 'text-orange-400 border-b-2 border-orange-400 bg-orange-500/10'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-1" />
            Quick Reminder
          </button>
          <button
            onClick={() => { setActiveTab('trash'); fetchContracts(); }}
            className={`flex-1 min-w-[60px] px-2 py-3 font-medium transition-colors ${
              activeTab === 'trash'
                ? 'text-red-400 border-b-2 border-red-400 bg-red-500/10'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <Trash2 className="w-4 h-4 inline mr-1" />
            Trash
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Contract Type Selection */}
          {activeTab === 'select' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white text-center mb-6">Select Contract Type</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Promissory Note Option */}
                <button
                  onClick={() => setActiveTab('promissory')}
                  className="group p-6 bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-500/30 rounded-xl hover:border-purple-400/50 transition-all text-left"
                >
                  <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <DollarSign className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">Promissory Note</h4>
                  <p className="text-purple-300 text-sm mb-4">Hard Money Loan Agreement</p>
                  <ul className="text-zinc-400 text-xs space-y-1">
                    <li>• Borrower & Lender information</li>
                    <li>• Loan amount & repayment amount</li>
                    <li>• Loan date & due date</li>
                    <li>• Legally binding terms</li>
                    <li>• Requires signature & ID</li>
                  </ul>
                </button>

                {/* Credit Repair Contract Option */}
                <button
                  onClick={() => setActiveTab('credit-repair')}
                  className="group p-6 bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-500/30 rounded-xl hover:border-blue-400/50 transition-all text-left"
                >
                  <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <CreditCard className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">Credit Repair Contract</h4>
                  <p className="text-blue-300 text-sm mb-4">Service Agreement for New Clients</p>
                  <ul className="text-zinc-400 text-xs space-y-1">
                    <li>• Down payment & remaining balance</li>
                    <li>• Service terms & conditions</li>
                    <li>• Compliance requirements</li>
                    <li>• Forfeiture clause for non-compliance</li>
                    <li>• Requires signature & ID</li>
                  </ul>
                </button>

                {/* Credit Payment Plan Reminders Option */}
                <button
                  onClick={() => setActiveTab('payment-plan')}
                  className="group p-6 bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-500/30 rounded-xl hover:border-green-400/50 transition-all text-left"
                >
                  <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Calendar className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">Payment Plan Reminders</h4>
                  <p className="text-green-300 text-sm mb-4">Automated Payment Scheduling</p>
                  <ul className="text-zinc-400 text-xs space-y-1">
                    <li>• Set up payment schedules</li>
                    <li>• Automatic email reminders</li>
                    <li>• Track remaining balance</li>
                    <li>• AI-assisted scheduling</li>
                    <li>• Payment options included</li>
                  </ul>
                </button>
              </div>
            </div>
          )}

          {/* Promissory Note Form */}
          {activeTab === 'promissory' && (
            <div className="space-y-6">
              <button 
                onClick={() => setActiveTab('select')}
                className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 mb-4"
              >
                <ArrowLeft className="w-4 h-4" /> Back to selection
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Promissory Note</h3>
                  <p className="text-sm text-zinc-400">Hard Money Loan Agreement</p>
                </div>
              </div>

              {/* Borrower Info with Dropdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-sm text-zinc-400 mb-2 flex items-center justify-between">
                    <span>Borrower Name *</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowBorrowerDropdown(!showBorrowerDropdown); setShowLenderDropdown(false); }}
                      className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                    >
                      <Users className="w-3 h-3" /> Saved Borrowers <ChevronDown className="w-3 h-3" />
                    </button>
                  </label>
                  <input
                    type="text"
                    value={promissoryForm.borrowerName}
                    onChange={(e) => setPromissoryForm({...promissoryForm, borrowerName: e.target.value})}
                    placeholder="John Smith"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  />
                  
                  {/* Borrower Dropdown */}
                  {showBorrowerDropdown && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-zinc-800 border border-zinc-600 rounded-lg shadow-xl overflow-hidden">
                      <div className="py-1">
                        {SAVED_BORROWERS.map(borrower => (
                          <button
                            key={borrower.id}
                            onClick={(e) => { e.stopPropagation(); selectBorrower(borrower); }}
                            className="w-full px-4 py-3 text-left hover:bg-purple-600/30 transition-colors flex items-center gap-3"
                          >
                            <div className="w-8 h-8 bg-purple-600/30 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-purple-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{borrower.name}</p>
                              {borrower.email && <p className="text-xs text-zinc-400">{borrower.email}</p>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Borrower Email *</label>
                  <input
                    type="email"
                    value={promissoryForm.borrowerEmail}
                    onChange={(e) => setPromissoryForm({...promissoryForm, borrowerEmail: e.target.value})}
                    placeholder="borrower@email.com"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  />
                </div>
              </div>

              {/* Lender Info with Dropdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-sm text-zinc-400 mb-2 flex items-center justify-between">
                    <span>Lender Name</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowLenderDropdown(!showLenderDropdown); setShowBorrowerDropdown(false); }}
                      className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                    >
                      <Users className="w-3 h-3" /> Saved Lenders <ChevronDown className="w-3 h-3" />
                    </button>
                  </label>
                  <input
                    type="text"
                    value={promissoryForm.lenderName}
                    onChange={(e) => setPromissoryForm({...promissoryForm, lenderName: e.target.value})}
                    placeholder="Clear All Credit Repair"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  />
                  
                  {/* Lender Dropdown */}
                  {showLenderDropdown && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-zinc-800 border border-zinc-600 rounded-lg shadow-xl overflow-hidden">
                      <div className="py-1">
                        {SAVED_LENDERS.map(lender => (
                          <button
                            key={lender.id}
                            onClick={(e) => { e.stopPropagation(); selectLender(lender); }}
                            className="w-full px-4 py-3 text-left hover:bg-purple-600/30 transition-colors flex items-center gap-3"
                          >
                            <div className="w-8 h-8 bg-green-600/30 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-green-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{lender.name}</p>
                              {lender.email && <p className="text-xs text-zinc-400">{lender.email}</p>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Lender Email (for copy)</label>
                  <input
                    type="email"
                    value={promissoryForm.lenderEmail}
                    onChange={(e) => setPromissoryForm({...promissoryForm, lenderEmail: e.target.value})}
                    placeholder="lender@email.com"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Date Money Lent *</label>
                  <input
                    type="date"
                    value={promissoryForm.dateLent}
                    onChange={(e) => setPromissoryForm({...promissoryForm, dateLent: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Repayment Due Date *</label>
                  <input
                    type="date"
                    value={promissoryForm.dateReturn}
                    onChange={(e) => setPromissoryForm({...promissoryForm, dateReturn: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  />
                </div>
              </div>

              {/* Amounts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Amount Lent *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={promissoryForm.amountLent}
                      onChange={(e) => setPromissoryForm({...promissoryForm, amountLent: e.target.value})}
                      placeholder="0.00"
                      className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Amount to be Returned *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={promissoryForm.amountReturn}
                      onChange={(e) => setPromissoryForm({...promissoryForm, amountReturn: e.target.value})}
                      placeholder="0.00"
                      className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Terms */}
              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <label className="block text-sm text-zinc-400">Additional Terms (Optional)</label>
                  {/* 2026-02-24 — Two-button late-penalty picker so the user
                      can lock a new promissory note at $100/day OR $200/day
                      before the borrower signs. */}
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setPromissoryForm({...promissoryForm, additionalTerms: appendLatePenalty(promissoryForm.additionalTerms, 200), latePenaltyPerDay: 200})}
                      className={`px-2 py-1 rounded-l-md text-[11px] font-semibold border border-amber-500/40 transition-colors flex items-center gap-1 ${promissoryForm.latePenaltyPerDay === 200 ? 'bg-amber-500 text-white' : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300'}`}
                      data-testid="promissory-add-late-penalty-200"
                      title="Append the $200/day late payment penalty clause"
                    >
                      <AlertTriangle className="w-3 h-3" />
                      $200/day
                    </button>
                    <button
                      type="button"
                      onClick={() => setPromissoryForm({...promissoryForm, additionalTerms: appendLatePenalty(promissoryForm.additionalTerms, 100), latePenaltyPerDay: 100})}
                      className={`px-2 py-1 rounded-r-md text-[11px] font-semibold border border-l-0 border-amber-500/40 transition-colors flex items-center gap-1 ${promissoryForm.latePenaltyPerDay === 100 ? 'bg-amber-500 text-white' : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300'}`}
                      data-testid="promissory-add-late-penalty-100"
                      title="Append the $100/day late payment penalty clause"
                    >
                      $100/day
                    </button>
                  </div>
                </div>
                <textarea
                  value={promissoryForm.additionalTerms}
                  onChange={(e) => setPromissoryForm({...promissoryForm, additionalTerms: e.target.value})}
                  placeholder="Any additional terms or conditions..."
                  rows={4}
                  className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                />
              </div>

              {/* Lender Signature Style Selector */}
              <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4">
                <h4 className="text-purple-300 font-medium mb-3 flex items-center gap-2">
                  <Pen className="w-4 h-4" />
                  Lender Signature Style (Required)
                </h4>
                <p className="text-sm text-purple-200/70 mb-4">
                  Select a signature style for "{promissoryForm.lenderName || 'Lender Name'}". This will appear on the contract.
                </p>
                
                {/* Signature Style Options */}
                <div className="grid grid-cols-1 gap-3">
                  {SIGNATURE_STYLES.map((style) => (
                    <button
                      key={style.id}
                      type="button"
                      onClick={() => setSelectedSignatureStyle(style.id)}
                      className={`w-full p-4 rounded-lg border-2 transition-all flex items-center justify-between ${
                        selectedSignatureStyle === style.id 
                          ? 'border-purple-500 bg-purple-500/20' 
                          : 'border-zinc-600 bg-zinc-800/50 hover:border-zinc-500'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedSignatureStyle === style.id 
                            ? 'border-purple-500 bg-purple-500' 
                            : 'border-zinc-500'
                        }`}>
                          {selectedSignatureStyle === style.id && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className="text-zinc-400 text-sm">{style.name}</span>
                      </div>
                      <div 
                        className="bg-white rounded px-4 py-2 min-w-[200px] text-center"
                        style={{ fontFamily: style.fontFamily, fontSize: style.fontSize, color: '#000' }}
                      >
                        {promissoryForm.lenderName || 'Your Name'}
                      </div>
                    </button>
                  ))}
                </div>
                
                <div className="mt-4 flex items-center gap-2">
                  {selectedSignatureStyle ? (
                    <span className="text-green-400 text-xs flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Signature style selected
                    </span>
                  ) : (
                    <span className="text-amber-400 text-xs">Please select a signature style</span>
                  )}
                </div>
              </div>

              {/* Preview Box */}
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <h4 className="text-purple-300 font-medium mb-2">What the borrower will receive:</h4>
                <ol className="text-sm text-purple-200/80 space-y-1 list-decimal list-inside">
                  <li>Email with the Promissory Note (with your signature)</li>
                  <li>Must check boxes to acknowledge each term</li>
                  <li>Sign digitally to complete the agreement</li>
                  <li>Both parties receive the fully signed contract via email</li>
                </ol>
              </div>

              {/* Send Button */}
              <Button
                onClick={handleSendPromissoryNote}
                disabled={isSending || !selectedSignatureStyle || contractSent}
                className={`w-full py-5 text-lg font-bold ${
                  contractSent 
                    ? 'bg-green-700 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-600 disabled:to-gray-700'
                }`}
              >
                {isSending ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </>
                ) : contractSent ? (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Promissory Note Sent!
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    {selectedSignatureStyle ? 'Send Promissory Note' : 'Select Signature Style'}
                  </>
                )}
              </Button>

              {/* Copy Link Section - Shows after contract is sent */}
              {contractSent && signingLink && (
                <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Link className="w-4 h-4 text-purple-400" />
                    <span className="text-purple-300 font-medium">Signing Link Ready</span>
                  </div>
                  <p className="text-xs text-purple-200/70 mb-3">Copy this link to text it to your client:</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={signingLink}
                      readOnly
                      className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white truncate"
                    />
                    <Button
                      onClick={handleCopyLink}
                      className={`px-4 py-2 ${
                        linkCopied 
                          ? 'bg-green-600 hover:bg-green-600' 
                          : 'bg-purple-600 hover:bg-purple-700'
                      }`}
                    >
                      {linkCopied ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Back/Done Buttons */}
              <div className="flex gap-4 pt-4 border-t border-zinc-700">
                <Button
                  onClick={() => setActiveTab('select')}
                  variant="outline"
                  className="flex-1 py-4 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => {
                    setPromissoryForm({
                      borrowerName: '',
                      borrowerEmail: '',
                      lenderName: 'Clear All Credit Repair',
                      lenderEmail: '',
                      dateLent: '',
                      dateReturn: '',
                      amountLent: '',
                      amountReturn: '',
                      additionalTerms: ''
                    });
                    setSelectedSignatureStyle('style1');
                    setContractSent(false);
                    setSigningLink('');
                    setLinkCopied(false);
                    setActiveTab('select');
                    fetchContracts();
                  }}
                  className={`flex-1 py-4 ${
                    contractSent 
                      ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800' 
                      : 'bg-zinc-700 hover:bg-zinc-600'
                  }`}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Done - Start New
                </Button>
              </div>
            </div>
          )}

          {/* Credit Repair Contract Form */}
          {activeTab === 'credit-repair' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Credit Repair Contract</h3>
                  <p className="text-sm text-zinc-400">Service Agreement for New Clients</p>
                </div>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Client Name *</label>
                  <input
                    type="text"
                    value={creditRepairForm.clientName}
                    onChange={(e) => setCreditRepairForm({...creditRepairForm, clientName: e.target.value})}
                    placeholder="John Smith"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Client Email *</label>
                  <input
                    type="email"
                    value={creditRepairForm.clientEmail}
                    onChange={(e) => setCreditRepairForm({...creditRepairForm, clientEmail: e.target.value})}
                    placeholder="client@email.com"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* Paid in Full Toggle */}
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-green-300 font-medium">Paid in Full</h4>
                      <p className="text-sm text-green-200/70">Toggle on if client pays the full amount upfront</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsPaidInFull(!isPaidInFull)}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-zinc-900 ${
                      isPaidInFull ? 'bg-green-600' : 'bg-zinc-600'
                    }`}
                    data-testid="paid-in-full-toggle"
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        isPaidInFull ? 'translate-x-8' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Payment Info */}
              {isPaidInFull ? (
                // Simplified view when Paid in Full is ON
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Total Service Fee *</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={creditRepairForm.totalAmount}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          handlePaidInFullTotalChange(val);
                        }}
                        placeholder="Enter amount"
                        className="w-full md:w-1/2 bg-zinc-800 border border-green-500/50 rounded-lg pl-8 pr-4 py-3 text-green-400 font-bold placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                        data-testid="paid-in-full-total-input"
                      />
                    </div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-300 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      <span>Down Payment: <strong>${creditRepairForm.downPayment || '0'}</strong> (Full Amount)</span>
                    </div>
                    <div className="flex items-center gap-2 text-green-300 text-sm mt-1">
                      <CheckCircle className="w-4 h-4" />
                      <span>Remaining Balance: <strong>$0</strong></span>
                    </div>
                  </div>
                </div>
              ) : (
                // Standard view when Paid in Full is OFF
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Down Payment *</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={creditRepairForm.downPayment}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setCreditRepairForm({...creditRepairForm, downPayment: val});
                        }}
                        placeholder="0"
                        className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white placeholder-zinc-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                        data-testid="down-payment-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Remaining Balance</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={creditRepairForm.remainingBalance}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setCreditRepairForm({...creditRepairForm, remainingBalance: val});
                        }}
                        placeholder="0"
                        className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white placeholder-zinc-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                        data-testid="remaining-balance-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Total Amount</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="text"
                        value={creditRepairForm.totalAmount}
                        readOnly
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-green-400 font-bold outline-none"
                        data-testid="total-amount-display"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Due Date - Only show when NOT paid in full */}
              {!isPaidInFull && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Remaining Balance Due Date (Optional)</label>
                  <input
                    type="date"
                    value={creditRepairForm.paymentDueDate}
                    onChange={(e) => setCreditRepairForm({...creditRepairForm, paymentDueDate: e.target.value})}
                    className="w-full md:w-1/2 bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    data-testid="payment-due-date-input"
                  />
                </div>
              )}

              {/* Additional Terms */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Additional Terms (Optional)</label>
                <textarea
                  value={creditRepairForm.additionalTerms}
                  onChange={(e) => setCreditRepairForm({...creditRepairForm, additionalTerms: e.target.value})}
                  placeholder="Any additional terms or conditions specific to this client..."
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Warning Box */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h4 className="text-amber-300 font-medium mb-2 flex items-center gap-2">
                  ⚠️ Contract Includes Forfeiture Clause
                </h4>
                <p className="text-sm text-amber-200/80">
                  This contract states that if the client fails to follow email instructions, respond to communications, 
                  or comply with the credit repair process, their fees are non-refundable and forfeited.
                </p>
              </div>

              {/* Preview Box */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h4 className="text-blue-300 font-medium mb-2">What the client will receive:</h4>
                <ol className="text-sm text-blue-200/80 space-y-1 list-decimal list-inside">
                  <li>Email with the Credit Repair Service Agreement</li>
                  <li>Link to review all terms and conditions</li>
                  <li>They review and sign digitally</li>
                  <li>Both parties receive a signed copy via email</li>
                </ol>
              </div>

              {/* Payment Method Dropdown - For Receipt Only */}
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                <h4 className="text-green-300 font-medium mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  Payment Method (Required for Receipt)
                </h4>
                <select
                  value={creditRepairForm.paymentMethod}
                  onChange={(e) => setCreditRepairForm({...creditRepairForm, paymentMethod: e.target.value})}
                  className="w-full md:w-1/2 bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                  data-testid="payment-method-select"
                >
                  <option value="">Select how client paid...</option>
                  {PAYMENT_METHODS.map(method => (
                    <option key={method.value} value={method.value}>{method.label}</option>
                  ))}
                </select>
                <p className="text-xs text-zinc-500 mt-2">This will be shown on the payment receipt</p>
              </div>

              {/* Send Buttons - Contract and Receipt */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Send Contract Button */}
                <Button
                  onClick={handleSendCreditRepairContract}
                  disabled={isSending || isSendingReceipt || contractSent}
                  className={`w-full py-5 text-base font-bold ${
                    contractSent 
                      ? 'bg-green-700 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                  }`}
                  data-testid="send-contract-btn"
                >
                  {isSending ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </>
                  ) : contractSent ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Contract Sent!
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 mr-2" />
                      Send Contract
                    </>
                  )}
                </Button>

                {/* Send Receipt Button */}
                <Button
                  onClick={handleSendReceipt}
                  disabled={isSending || isSendingReceipt || receiptSent}
                  className={`w-full py-5 text-base font-bold ${
                    receiptSent 
                      ? 'bg-green-700 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                  }`}
                  data-testid="send-receipt-btn"
                >
                  {isSendingReceipt ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </>
                  ) : receiptSent ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Receipt Sent!
                    </>
                  ) : (
                    <>
                      <Receipt className="w-5 h-5 mr-2" />
                      Send Receipt
                    </>
                  )}
                </Button>
              </div>

              {/* Button descriptions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-zinc-500">
                <p className="text-center">{contractSent ? 'Contract sent to client' : 'Sends contract for client to sign'}</p>
                <p className="text-center">{receiptSent ? 'Receipt sent to client' : 'Sends payment receipt only'}</p>
              </div>

              {/* Copy Link Section - Shows after contract is sent */}
              {contractSent && signingLink && (
                <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Link className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-300 font-medium">Signing Link Ready</span>
                  </div>
                  <p className="text-xs text-blue-200/70 mb-3">Copy this link to text it to your client:</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={signingLink}
                      readOnly
                      className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white truncate"
                    />
                    <Button
                      onClick={handleCopyLink}
                      className={`px-4 py-2 ${
                        linkCopied 
                          ? 'bg-green-600 hover:bg-green-600' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {linkCopied ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Done Button - Always visible */}
              <div className="flex gap-4 pt-4 border-t border-zinc-700">
                <Button
                  onClick={() => setActiveTab('select')}
                  variant="outline"
                  className="flex-1 py-4 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleDoneWithCreditRepair}
                  className={`flex-1 py-4 ${
                    (contractSent || receiptSent) 
                      ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800' 
                      : 'bg-zinc-700 hover:bg-zinc-600'
                  }`}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Done - Start New
                </Button>
              </div>
            </div>
          )}

          {/* Payment Plan Reminders Form */}
          {activeTab === 'payment-plan' && (
            <div className="space-y-6">
              <button 
                onClick={() => setActiveTab('select')}
                className="text-sm text-green-400 hover:text-green-300 flex items-center gap-1 mb-4"
              >
                <ArrowLeft className="w-4 h-4" /> Back to selection
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Credit Payment Plan Reminders</h3>
                  <p className="text-sm text-zinc-400">Set up automated payment schedule reminders</p>
                </div>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Client Name *</label>
                  <input
                    type="text"
                    value={paymentPlanForm.clientName}
                    onChange={(e) => setPaymentPlanForm({...paymentPlanForm, clientName: e.target.value})}
                    placeholder="John Smith"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                    data-testid="payment-plan-client-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Client Email *</label>
                  <input
                    type="email"
                    value={paymentPlanForm.clientEmail}
                    onChange={(e) => setPaymentPlanForm({...paymentPlanForm, clientEmail: e.target.value})}
                    placeholder="client@email.com"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                    data-testid="payment-plan-client-email"
                  />
                </div>
              </div>

              {/* Balance Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Total Credit Balance *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={paymentPlanForm.totalBalance}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        setPaymentPlanForm({...paymentPlanForm, totalBalance: val});
                      }}
                      placeholder="1500"
                      className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                      data-testid="payment-plan-total-balance"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Down Payment (if any)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={paymentPlanForm.downPayment}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        setPaymentPlanForm({...paymentPlanForm, downPayment: val});
                      }}
                      placeholder="500"
                      className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                      data-testid="payment-plan-down-payment"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Schedule */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Payment Amount *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={paymentPlanForm.paymentAmount}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        setPaymentPlanForm({...paymentPlanForm, paymentAmount: val});
                      }}
                      placeholder="500"
                      className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                      data-testid="payment-plan-payment-amount"
                    />
                  </div>
                </div>

                {/* Quick Frequency Buttons */}
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Payment Frequency *</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentPlanForm({...paymentPlanForm, paymentFrequency: 'weekly'})}
                      className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                        paymentPlanForm.paymentFrequency === 'weekly'
                          ? 'bg-green-600 border-green-500 text-white'
                          : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-green-500/50'
                      }`}
                    >
                      <span className="block text-lg">Weekly</span>
                      <span className="text-xs opacity-75">Every 7 days</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentPlanForm({...paymentPlanForm, paymentFrequency: 'biweekly'})}
                      className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                        paymentPlanForm.paymentFrequency === 'biweekly'
                          ? 'bg-green-600 border-green-500 text-white'
                          : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-green-500/50'
                      }`}
                    >
                      <span className="block text-lg">Bi-Weekly</span>
                      <span className="text-xs opacity-75">Every 2 weeks</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentPlanForm({...paymentPlanForm, paymentFrequency: 'monthly'})}
                      className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                        paymentPlanForm.paymentFrequency === 'monthly'
                          ? 'bg-green-600 border-green-500 text-white'
                          : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-green-500/50'
                      }`}
                    >
                      <span className="block text-lg">Monthly</span>
                      <span className="text-xs opacity-75">Every 30 days</span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">First Payment Date *</label>
                  <input
                    type="date"
                    value={paymentPlanForm.startDate}
                    onChange={(e) => setPaymentPlanForm({...paymentPlanForm, startDate: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                    data-testid="payment-plan-start-date"
                  />
                </div>
              </div>

              {/* Calculated Summary */}
              {paymentPlanForm.totalBalance && paymentPlanForm.paymentAmount && (
                <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                  <h4 className="text-green-400 font-medium mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Payment Plan Summary
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-zinc-500">Total Balance</p>
                      <p className="text-white font-bold">${parseInt(paymentPlanForm.totalBalance || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Down Payment</p>
                      <p className="text-white font-bold">${parseInt(paymentPlanForm.downPayment || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Remaining</p>
                      <p className="text-green-400 font-bold">
                        ${Math.max(0, parseInt(paymentPlanForm.totalBalance || 0) - parseInt(paymentPlanForm.downPayment || 0)).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-zinc-500"># of Payments</p>
                      <p className="text-white font-bold">
                        {Math.ceil(Math.max(0, parseInt(paymentPlanForm.totalBalance || 0) - parseInt(paymentPlanForm.downPayment || 0)) / parseInt(paymentPlanForm.paymentAmount || 1))}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Schedule Assistant */}
              <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                <h4 className="text-purple-400 font-medium mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  AI Schedule Assistant (Optional)
                </h4>
                <p className="text-xs text-zinc-500 mb-3">
                  Describe the payment schedule in plain English and let AI fill in the form for you.
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={aiScheduleInput}
                    onChange={(e) => setAiScheduleInput(e.target.value)}
                    placeholder="e.g., Client owes $1500, paid $500 down, wants to pay $500 every 2 weeks starting April 1st"
                    className="flex-1 bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white text-sm placeholder-zinc-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    data-testid="ai-schedule-input"
                  />
                  <Button
                    onClick={handleAISchedule}
                    disabled={isProcessingAI || !aiScheduleInput.trim()}
                    className="px-4 bg-purple-600 hover:bg-purple-700"
                    data-testid="ai-schedule-btn"
                  >
                    {isProcessingAI ? (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      'Parse'
                    )}
                  </Button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Notes (Optional)</label>
                <textarea
                  value={paymentPlanForm.notes}
                  onChange={(e) => setPaymentPlanForm({...paymentPlanForm, notes: e.target.value})}
                  placeholder="Any additional notes about this payment plan..."
                  rows={2}
                  className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none resize-none"
                  data-testid="payment-plan-notes"
                />
              </div>

              {/* Payment Options Preview */}
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                <h4 className="text-zinc-300 font-medium mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  Payment Options (Included in Reminders)
                </h4>
                <div className="text-xs text-zinc-400 space-y-1 font-mono whitespace-pre-line">
                  {PAYMENT_OPTIONS_MESSAGE}
                </div>
              </div>

              {/* Create Plan Button */}
              <Button
                onClick={handleCreatePaymentPlan}
                disabled={isCreatingPlan || !paymentPlanForm.clientName || !paymentPlanForm.clientEmail || !paymentPlanForm.totalBalance || !paymentPlanForm.paymentAmount || !paymentPlanForm.startDate}
                className="w-full py-5 text-base font-bold bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                data-testid="create-payment-plan-btn"
              >
                {isCreatingPlan ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating Plan...
                  </>
                ) : (
                  <>
                    <Calendar className="w-5 h-5 mr-2" />
                    Create Payment Plan & Send First Reminder
                  </>
                )}
              </Button>

              {/* View Payment Plans Link */}
              <div className="text-center">
                <button
                  onClick={() => setActiveTab('clients-ledger')}
                  className="text-sm text-green-400 hover:text-green-300 flex items-center gap-1 mx-auto"
                >
                  <Clock className="w-4 h-4" /> View CACR Payments Ledger
                </button>
              </div>
            </div>
          )}

          {/* Request Payment Tab (One-Time Payment Request) */}
          {activeTab === 'request-payment' && (
            <div className="space-y-6">
              <button 
                onClick={() => setActiveTab('clients-ledger')}
                className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1 mb-4"
              >
                <ArrowLeft className="w-4 h-4" /> Back to CACR Payments
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Request Payment</h3>
                  <p className="text-sm text-zinc-400">Send a one-time payment request to a client</p>
                </div>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Client Name *</label>
                  <input
                    type="text"
                    value={requestPaymentForm.clientName}
                    onChange={(e) => setRequestPaymentForm({...requestPaymentForm, clientName: e.target.value})}
                    placeholder="John Smith"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                    data-testid="request-payment-client-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Client Email *</label>
                  <input
                    type="email"
                    value={requestPaymentForm.clientEmail}
                    onChange={(e) => setRequestPaymentForm({...requestPaymentForm, clientEmail: e.target.value})}
                    placeholder="client@email.com"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                    data-testid="request-payment-client-email"
                  />
                </div>
              </div>

              {/* Payment Amount */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Payment Amount *</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={requestPaymentForm.amount}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setRequestPaymentForm({...requestPaymentForm, amount: val});
                    }}
                    placeholder="500"
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white placeholder-zinc-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none text-xl font-bold"
                    data-testid="request-payment-amount"
                  />
                </div>
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Due Date (Optional)</label>
                <input
                  type="date"
                  value={requestPaymentForm.dueDate}
                  onChange={(e) => setRequestPaymentForm({...requestPaymentForm, dueDate: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                  data-testid="request-payment-due-date"
                />
              </div>

              {/* Reminder Frequency */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Reminder Frequency</label>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => setRequestPaymentForm({...requestPaymentForm, reminderFrequency: 'once'})}
                    className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      requestPaymentForm.reminderFrequency === 'once'
                        ? 'bg-amber-600 border-amber-500 text-white'
                        : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-amber-500/50'
                    }`}
                  >
                    <span className="block text-sm">One-Time</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequestPaymentForm({...requestPaymentForm, reminderFrequency: 'weekly'})}
                    className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      requestPaymentForm.reminderFrequency === 'weekly'
                        ? 'bg-amber-600 border-amber-500 text-white'
                        : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-amber-500/50'
                    }`}
                  >
                    <span className="block text-sm">Weekly</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequestPaymentForm({...requestPaymentForm, reminderFrequency: 'biweekly'})}
                    className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      requestPaymentForm.reminderFrequency === 'biweekly'
                        ? 'bg-amber-600 border-amber-500 text-white'
                        : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-amber-500/50'
                    }`}
                  >
                    <span className="block text-sm">Bi-Weekly</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequestPaymentForm({...requestPaymentForm, reminderFrequency: 'monthly'})}
                    className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      requestPaymentForm.reminderFrequency === 'monthly'
                        ? 'bg-amber-600 border-amber-500 text-white'
                        : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-amber-500/50'
                    }`}
                  >
                    <span className="block text-sm">Monthly</span>
                  </button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  {requestPaymentForm.reminderFrequency === 'once' 
                    ? 'A single payment request email will be sent immediately'
                    : `Automatic reminders will be sent ${requestPaymentForm.reminderFrequency} until paid`
                  }
                </p>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Notes (Optional)</label>
                <textarea
                  value={requestPaymentForm.notes}
                  onChange={(e) => setRequestPaymentForm({...requestPaymentForm, notes: e.target.value})}
                  placeholder="Reason for payment request, service details, etc..."
                  rows={2}
                  className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none resize-none"
                  data-testid="request-payment-notes"
                />
              </div>

              {/* Payment Options Preview */}
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                <h4 className="text-zinc-300 font-medium mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-amber-400" />
                  Payment Options (Included in Request)
                </h4>
                <div className="text-xs text-zinc-400 space-y-1 font-mono whitespace-pre-line">
                  {PAYMENT_OPTIONS_MESSAGE}
                </div>
              </div>

              {/* Send Request Button */}
              <Button
                onClick={handleCreateRequestPayment}
                disabled={isCreatingRequest || !requestPaymentForm.clientName || !requestPaymentForm.clientEmail || !requestPaymentForm.amount}
                className="w-full py-5 text-base font-bold bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800"
                data-testid="send-request-payment-btn"
              >
                {isCreatingRequest ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending Request...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Payment Request
                  </>
                )}
              </Button>

              {/* View Ledger Link */}
              <div className="text-center">
                <button
                  onClick={() => setActiveTab('clients-ledger')}
                  className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1 mx-auto"
                >
                  <Clock className="w-4 h-4" /> View CACR Payments Ledger
                </button>
              </div>
            </div>
          )}

          {/* Payment Plan Logs */}
          {activeTab === 'payment-plan-logs' && (
            <div className="space-y-6">
              <button 
                onClick={() => setActiveTab('payment-plan')}
                className="text-sm text-green-400 hover:text-green-300 flex items-center gap-1 mb-4"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Payment Plan
              </button>

              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Payment Plans</h3>
                    <p className="text-sm text-zinc-400">Manage client payment schedules</p>
                  </div>
                </div>
                <Button
                  onClick={fetchPaymentPlans}
                  disabled={loadingPaymentPlans}
                  variant="outline"
                  className="border-zinc-600 text-zinc-300"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingPaymentPlans ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {loadingPaymentPlans ? (
                <div className="flex items-center justify-center py-12">
                  <svg className="animate-spin h-8 w-8 text-green-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              ) : paymentPlans.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No payment plans yet</p>
                  <p className="text-sm">Create your first payment plan to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {paymentPlans.map((plan) => (
                    <div
                      key={plan.plan_id}
                      className={`bg-zinc-800/50 border rounded-lg p-4 transition-all ${
                        plan.status === 'completed' ? 'border-green-500/30' : 
                        plan.status === 'cancelled' ? 'border-red-500/30' : 'border-zinc-700'
                      }`}
                    >
                      {/* Header with Client Name and Actions */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div>
                            <h4 className="text-white font-medium text-lg">{plan.client_name}</h4>
                            <p className="text-sm text-zinc-500">{plan.client_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Copy Text Link Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyPaymentLink(plan);
                            }}
                            className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1 transition-all ${
                              copiedLink === plan.plan_id
                                ? 'bg-green-600 text-white'
                                : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30'
                            }`}
                            title="Copy message to text client"
                          >
                            {copiedLink === plan.plan_id ? (
                              <>
                                <Check className="w-3 h-3" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                Copy Text
                              </>
                            )}
                          </button>
                          
                          {/* Send Instant Reminder Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendInstantReminder(plan);
                            }}
                            disabled={sendingInstantReminder === plan.plan_id || plan.status !== 'active'}
                            className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1 transition-all ${
                              plan.status !== 'active'
                                ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                                : 'bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 border border-amber-500/30'
                            }`}
                            title="Send instant payment reminder"
                          >
                            {sendingInstantReminder === plan.plan_id ? (
                              <>
                                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="w-3 h-3" />
                                Send Reminder
                              </>
                            )}
                          </button>
                          
                          {/* Status Badge */}
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            plan.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            plan.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {plan.status}
                          </span>
                        </div>
                      </div>

                      {/* Balance Info */}
                      <div className="grid grid-cols-5 gap-4 text-sm bg-zinc-900/50 rounded-lg p-3 mb-3">
                        <div>
                          <p className="text-zinc-500">Total</p>
                          <p className="text-white font-bold">${plan.total_balance?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Down</p>
                          <p className="text-white">${plan.down_payment?.toLocaleString() || 0}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Paid</p>
                          <p className="text-green-400 font-bold">${plan.total_paid?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Remaining</p>
                          <p className="text-amber-400 font-bold">${plan.remaining_balance?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Schedule</p>
                          <p className="text-white">${plan.payment_amount} / {plan.payment_frequency}</p>
                        </div>
                      </div>

                      {/* Next Payment & Log Payment */}
                      {plan.status === 'active' && (
                        <div className="flex items-center gap-4 bg-green-900/20 border border-green-500/30 rounded-lg p-3">
                          <div className="flex-1">
                            <p className="text-xs text-zinc-500">Next Payment Due</p>
                            <p className="text-green-400 font-bold">{formatDateShort(plan.next_payment_date)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={loggingPayment === plan.plan_id ? paymentLogAmount : ''}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^0-9]/g, '');
                                  setPaymentLogAmount(val);
                                  setLoggingPayment(plan.plan_id);
                                }}
                                onFocus={() => setLoggingPayment(plan.plan_id)}
                                placeholder={plan.payment_amount?.toString()}
                                className="w-24 bg-zinc-800 border border-zinc-600 rounded-lg pl-7 pr-2 py-2 text-white text-sm placeholder-zinc-500 focus:border-green-500 outline-none"
                              />
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (loggingPayment !== plan.plan_id) {
                                  setLoggingPayment(plan.plan_id);
                                  setPaymentLogAmount(plan.payment_amount?.toString() || '');
                                } else {
                                  handleLogPayment(plan);
                                }
                              }}
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg flex items-center gap-1 transition-all"
                            >
                              <DollarSign className="w-3 h-3" />
                              Log Payment
                            </button>
                          </div>
                        </div>
                      )}

                      {plan.status === 'completed' && (
                        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 text-center">
                          <p className="text-blue-400 font-medium">Payment Plan Completed!</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Data Backup Notice */}
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 text-center">
                <p className="text-xs text-zinc-500">
                  <Shield className="w-3 h-3 inline mr-1" />
                  All payment plans are automatically saved to the database and persist across deployments
                </p>
              </div>
            </div>
          )}

          {/* Clients Ledger Tab - Credit Repair Payment Plans */}
          {activeTab === 'clients-ledger' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-400" />
                  CACR Payments Ledger
                </h3>
                <div className="flex gap-2">
                  {/* Add New Client Dropdown */}
                  <div className="relative">
                    <Button
                      onClick={() => setShowAddClientMenu(!showAddClientMenu)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Add New Client
                      <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showAddClientMenu ? 'rotate-180' : ''}`} />
                    </Button>
                    
                    {showAddClientMenu && (
                      <div className="absolute right-0 mt-2 w-56 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden">
                        <button
                          onClick={() => {
                            setActiveTab('payment-plan');
                            setShowAddClientMenu(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-green-600/20 flex items-center gap-3 border-b border-zinc-700"
                        >
                          <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <Calendar className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-medium">Start Payment Plan</p>
                            <p className="text-xs text-zinc-400">Schedule recurring payments</p>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setActiveTab('request-payment');
                            setShowAddClientMenu(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-amber-600/20 flex items-center gap-3"
                        >
                          <div className="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center">
                            <DollarSign className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-medium">Request Payment</p>
                            <p className="text-xs text-zinc-400">One-time payment request</p>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={fetchPaymentPlans}
                    disabled={loadingPaymentPlans}
                    variant="outline"
                    className="border-zinc-600"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingPaymentPlans ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {loadingPaymentPlans ? (
                <div className="flex items-center justify-center py-12">
                  <svg className="animate-spin h-8 w-8 text-green-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              ) : paymentPlans.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No credit repair clients yet</p>
                  <p className="text-sm">Add your first client payment plan</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-400">{paymentPlans.filter(p => p.status === 'active').length}</p>
                      <p className="text-xs text-zinc-500">Active Plans</p>
                    </div>
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-400">{paymentPlans.filter(p => p.status === 'completed').length}</p>
                      <p className="text-xs text-zinc-500">Completed</p>
                    </div>
                    <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-amber-400">${paymentPlans.reduce((sum, p) => sum + (p.remaining_balance || 0), 0).toLocaleString()}</p>
                      <p className="text-xs text-zinc-500">Outstanding</p>
                    </div>
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-400">${paymentPlans.reduce((sum, p) => sum + (p.total_paid || 0), 0).toLocaleString()}</p>
                      <p className="text-xs text-zinc-500">Collected</p>
                    </div>
                  </div>

                  {/* Client List */}
                  {paymentPlans.map((plan) => (
                    <div
                      key={plan.plan_id}
                      className={`bg-zinc-800/50 border rounded-lg p-4 ${
                        plan.status === 'completed' ? 'border-blue-500/30' : 
                        plan.status === 'cancelled' ? 'border-red-500/30' : 'border-zinc-700'
                      }`}
                    >
                      {/* Client Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold">
                            {plan.client_name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <h4 className="text-white font-medium">{plan.client_name}</h4>
                            <p className="text-xs text-zinc-500">{plan.client_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Copy Text Message */}
                          <button
                            onClick={() => handleCopyPaymentLink(plan)}
                            className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                              copiedLink === plan.plan_id
                                ? 'bg-green-600 text-white'
                                : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                            }`}
                            title="Copy text message"
                          >
                            {copiedLink === plan.plan_id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            {copiedLink === plan.plan_id ? 'Copied!' : 'Text'}
                          </button>
                          
                          {/* Send Instant Reminder */}
                          <button
                            onClick={() => handleSendInstantReminder(plan)}
                            disabled={sendingInstantReminder === plan.plan_id || plan.status !== 'active'}
                            className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                              plan.status !== 'active' ? 'bg-zinc-700 text-zinc-500' : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                            }`}
                            title="Send instant email reminder"
                          >
                            {sendingInstantReminder === plan.plan_id ? (
                              <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /></svg>
                            ) : (
                              <Send className="w-3 h-3" />
                            )}
                            Email
                          </button>
                          
                          {/* Status Badge */}
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            plan.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            plan.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {plan.status}
                          </span>
                        </div>
                      </div>

                      {/* Balance Row */}
                      <div className="grid grid-cols-5 gap-2 text-sm bg-zinc-900/50 rounded p-2 mb-3">
                        <div className="text-center">
                          <p className="text-zinc-500 text-xs">Total</p>
                          <p className="text-white font-bold">${plan.total_balance?.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-zinc-500 text-xs">Down</p>
                          <p className="text-white">${plan.down_payment?.toLocaleString() || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-zinc-500 text-xs">Paid</p>
                          <p className="text-green-400 font-bold">${plan.total_paid?.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-zinc-500 text-xs">Remaining</p>
                          <p className="text-amber-400 font-bold">${plan.remaining_balance?.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-zinc-500 text-xs">Schedule</p>
                          <p className="text-white text-xs">${plan.payment_amount}/{plan.payment_frequency?.replace('biweekly', '2wk')}</p>
                        </div>
                      </div>

                      {/* Next Payment & Log */}
                      {plan.status === 'active' && (
                        <div className="flex items-center gap-3 bg-green-900/10 border border-green-500/20 rounded p-2">
                          <div className="flex-1">
                            <p className="text-xs text-zinc-500">Next Payment</p>
                            <p className="text-green-400 font-medium">
                              {formatDateShort(plan.next_payment_date)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">$</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={loggingPayment === plan.plan_id ? paymentLogAmount : ''}
                                onChange={(e) => {
                                  setPaymentLogAmount(e.target.value.replace(/[^0-9]/g, ''));
                                  setLoggingPayment(plan.plan_id);
                                }}
                                onFocus={() => setLoggingPayment(plan.plan_id)}
                                placeholder={plan.payment_amount?.toString()}
                                className="w-20 bg-zinc-800 border border-zinc-600 rounded pl-5 pr-2 py-1 text-white text-sm placeholder-zinc-500 focus:border-green-500 outline-none"
                              />
                            </div>
                            <button
                              onClick={() => {
                                if (loggingPayment !== plan.plan_id) {
                                  setLoggingPayment(plan.plan_id);
                                  setPaymentLogAmount(plan.payment_amount?.toString() || '');
                                } else {
                                  handleLogPayment(plan);
                                }
                              }}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded flex items-center gap-1"
                            >
                              <DollarSign className="w-3 h-3" />
                              Log
                            </button>
                          </div>
                        </div>
                      )}

                      {plan.status === 'completed' && (
                        <div className="bg-blue-900/20 border border-blue-500/30 rounded p-2 text-center">
                          <p className="text-blue-400 text-sm font-medium">Paid in Full!</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contract History */}
          {activeTab === 'history' && (
            <div>
              {/* Contract Detail View */}
              {selectedContract ? (
                <div className="space-y-6">
                  <button
                    onClick={() => setSelectedContract(null)}
                    className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to list
                  </button>

                  <div className="bg-zinc-800 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-white">{selectedContract.client_name || selectedContract.client_email}</h3>
                        <p className="text-sm text-zinc-400">{selectedContract.client_email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedContract.loan_number && (
                          <span className="px-3 py-1 rounded-full text-sm font-bold bg-green-500/20 text-green-300 border border-green-500/30">
                            Loan #{selectedContract.loan_number}
                          </span>
                        )}
                        {getContractTypeBadge(selectedContract.contract_type)}
                        {getStatusBadge(selectedContract.status)}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-zinc-500">Created:</span>
                        <span className="text-white ml-2">{formatDateShort(selectedContract.created_at)}</span>
                      </div>
                      {selectedContract.signed_at && (
                        <div>
                          <span className="text-zinc-500">Signed:</span>
                          <span className="text-green-400 ml-2">{formatDateShort(selectedContract.signed_at)}</span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons for Signed Contracts */}
                    {selectedContract.status === 'signed' && (
                      <div className="flex flex-wrap gap-3 mb-4">
                        <a
                          href={`${API}/api/contracts/${selectedContract.id}/pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download PDF
                        </a>
                        
                        {/* Resend Confirmation Email */}
                        <button
                          onClick={async () => {
                            try {
                              toast.info('Resending confirmation emails...');
                              const response = await axios.post(`${API}/api/promise-to-pay/resend-confirmation/${selectedContract.id}`);
                              if (response.data.success) {
                                toast.success(response.data.message || 'Confirmation emails sent!');
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to resend emails');
                            }
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          Resend Confirmation
                        </button>
                        
                        {/* Record Payment - for both promissory notes and credit repair */}
                        <button
                          onClick={() => {
                            setPaymentForm({ amount: '', paymentMethod: '', notes: '' });
                            setShowPaymentModal(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm transition-colors"
                        >
                          <DollarSign className="w-4 h-4" />
                          Record Payment
                        </button>
                        
                        {/* Send Paid Receipt - for credit repair contracts */}
                        {selectedContract.contract_type === 'credit_repair' && (
                          <button
                            onClick={() => handleSendCreditRepairReceipt(selectedContract)}
                            disabled={isSendingConfirmation}
                            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 rounded-lg text-white text-sm transition-colors"
                          >
                            {isSendingConfirmation ? (
                              <>
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Sending...
                              </>
                            ) : (
                              <>
                                <Receipt className="w-4 h-4" />
                                Send Paid Receipt
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Payment Summary for Credit Repair */}
                    {selectedContract.contract_type === 'credit_repair' && selectedContract.payments && selectedContract.payments.length > 0 && (
                      <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-4">
                        <h4 className="text-green-300 text-sm font-medium mb-3 flex items-center gap-2">
                          <Receipt className="w-4 h-4" />
                          Payment History
                        </h4>
                        <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                          <div>
                            <span className="text-zinc-500">Total Contract:</span>
                            <span className="text-white ml-2 font-medium">
                              ${parseFloat(selectedContract.metadata?.totalAmount || selectedContract.metadata?.down_payment || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div>
                            <span className="text-zinc-500">Total Paid:</span>
                            <span className="text-green-400 ml-2 font-bold">
                              ${parseFloat(selectedContract.total_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {selectedContract.payments.map((payment, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-zinc-800 rounded-lg p-2 text-sm">
                              <div>
                                <span className="text-white font-medium">${parseFloat(payment.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                <span className="text-zinc-500 ml-2">({payment.payment_method || 'N/A'})</span>
                              </div>
                              <div className="text-xs text-zinc-500">
                                {formatDateShort(payment.timestamp)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Document Hash / Proof of Existence */}
                    {selectedContract.document_hash && (
                      <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4 mb-4">
                        <h4 className="text-amber-300 text-sm font-medium mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Document Integrity Verification
                        </h4>
                        <p className="text-xs text-amber-200/70 font-mono break-all">
                          SHA-256: {selectedContract.document_hash.hash}
                        </p>
                        <p className="text-xs text-amber-200/50 mt-1">
                          Generated: {selectedContract.document_hash.timestamp}
                        </p>
                      </div>
                    )}

                    <div className="bg-zinc-900 rounded-lg p-4 mb-4 max-h-60 overflow-y-auto">
                      <h4 className="text-sm text-zinc-400 mb-2">Contract Content:</h4>
                      <pre className="text-white text-sm whitespace-pre-wrap font-mono">{selectedContract.contract_text}</pre>
                    </div>

                    {selectedContract.signature_data && (
                      <div className="bg-zinc-900 rounded-lg p-4 mb-4">
                        <h4 className="text-sm text-zinc-400 mb-2">Client Signature:</h4>
                        <img src={selectedContract.signature_data} alt="Signature" className="max-w-xs border border-zinc-700 rounded" />
                        <p className="text-xs text-zinc-500 mt-2">
                          Signed from IP: {selectedContract.signed_ip_address || 'Unknown'}
                          {selectedContract.signed_user_agent && (
                            <span className="block mt-1">Device: {selectedContract.signed_user_agent.substring(0, 60)}...</span>
                          )}
                        </p>
                      </div>
                    )}

                    {selectedContract.id_document && (
                      <div className="bg-zinc-900 rounded-lg p-4 mb-4">
                        <h4 className="text-sm text-zinc-400 mb-2">Client ID:</h4>
                        <img src={selectedContract.id_document} alt="Client ID" className="max-w-md border border-zinc-700 rounded" />
                      </div>
                    )}

                    {/* Activity Log */}
                    {selectedContract.activity_log && selectedContract.activity_log.length > 0 && (
                      <div className="bg-zinc-900 rounded-lg p-4">
                        <h4 className="text-sm text-zinc-400 mb-3 flex items-center gap-2">
                          <Activity className="w-4 h-4" />
                          Activity Log ({selectedContract.activity_log.length} events)
                        </h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {selectedContract.activity_log.map((activity, idx) => (
                            <div key={idx} className="flex items-start gap-3 text-xs">
                              <span className={`px-2 py-1 rounded text-xs ${
                                activity.activity_type === 'contract_signed' ? 'bg-green-500/20 text-green-300' :
                                activity.activity_type === 'email_opened' ? 'bg-blue-500/20 text-blue-300' :
                                activity.activity_type === 'contract_viewed' ? 'bg-purple-500/20 text-purple-300' :
                                activity.activity_type === 'payment_recorded' ? 'bg-yellow-500/20 text-yellow-300' :
                                'bg-zinc-700 text-zinc-300'
                              }`}>
                                {activity.activity_type.replace(/_/g, ' ')}
                              </span>
                              <span className="text-zinc-500">{activity.timestamp}</span>
                              {activity.ip_address && (
                                <span className="text-zinc-600">IP: {activity.ip_address}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Contract List */}
                  {loadingContracts ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="ml-3 text-zinc-400">Loading contracts...</span>
                    </div>
                  ) : contracts.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-400">No contracts yet</p>
                      <p className="text-sm text-zinc-500 mt-2">Create your first contract</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {contracts.map((contract) => (
                        <div
                          key={contract.id}
                          className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 hover:border-zinc-600 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className="font-semibold text-white">
                                  {contract.client_name || contract.client_email}
                                </span>
                                {contract.loan_number && (
                                  <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30">
                                    Loan #{contract.loan_number}
                                  </span>
                                )}
                                {getContractTypeBadge(contract.contract_type)}
                                {getStatusBadge(contract.status)}
                              </div>
                              <p className="text-sm text-zinc-400 mt-1">{contract.client_email}</p>
                              <p className="text-xs text-zinc-500 mt-1">
                                Created: {formatDateShort(contract.created_at)}
                                {contract.signed_at && (
                                  <span className="text-green-400 ml-3">
                                    Signed: {formatDateShort(contract.signed_at)}
                                  </span>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => viewContractDetail(contract.id)}
                                className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteContract(contract.id)}
                                className="p-2 bg-red-600/20 hover:bg-red-600 rounded-lg text-red-400 hover:text-white transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Loan Log Tab */}
          {activeTab === 'loans' && (
            <div>
              {/* Loan Detail View */}
              {selectedLoan ? (
                <div className="space-y-6">
                  <button
                    onClick={() => setSelectedLoan(null)}
                    className="text-sm text-green-400 hover:text-green-300 flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to Loan Log
                  </button>

                  <div className="bg-zinc-800 rounded-lg p-6">
                    {/* Loan Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="px-4 py-2 rounded-lg text-xl font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            Loan #{selectedLoan.loan_number}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            selectedLoan.status === 'paid' ? 'bg-green-500/20 text-green-300' :
                            selectedLoan.status === 'signed' ? 'bg-blue-500/20 text-blue-300' :
                            selectedLoan.status === 'defaulted' ? 'bg-red-500/20 text-red-300' :
                            'bg-yellow-500/20 text-yellow-300'
                          }`}>
                            {selectedLoan.status?.toUpperCase() || 'PENDING'}
                          </span>
                          {/* Promissory Note signature indicator — RED before signature, GREEN after */}
                          {getPromissoryNoteBadge(selectedLoan, 'lg')}
                        </div>
                        <p className="text-zinc-400 mt-2">{selectedLoan.borrower_name} ({selectedLoan.borrower_email})</p>
                      </div>
                    </div>

                    {/* Loan Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-zinc-900 rounded-lg p-4">
                        <p className="text-zinc-500 text-xs">Amount Lent</p>
                        <p className="text-xl font-bold text-white">${parseFloat(selectedLoan.amount_lent || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="bg-zinc-900 rounded-lg p-4">
                        <p className="text-zinc-500 text-xs">Amount Due</p>
                        <p className="text-xl font-bold text-white">${parseFloat(selectedLoan.amount_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="bg-zinc-900 rounded-lg p-4">
                        <p className="text-zinc-500 text-xs">Total Paid</p>
                        <p className="text-xl font-bold text-green-400">${parseFloat(selectedLoan.total_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="bg-zinc-900 rounded-lg p-4">
                        <p className="text-zinc-500 text-xs">Remaining</p>
                        <p className={`text-xl font-bold ${parseFloat(selectedLoan.remaining_balance || 0) <= 0 ? 'text-green-400' : 'text-amber-400'}`}>
                          ${parseFloat(selectedLoan.remaining_balance || selectedLoan.amount_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                      <div>
                        <span className="text-zinc-500">Date Lent:</span>
                        <span className="text-white ml-2">{formatDateShort(selectedLoan.date_lent)}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Due Date:</span>
                        <span className="text-white ml-2">{formatDateShort(selectedLoan.date_return)}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 mb-6">
                      {/* Record Payment */}
                      <button
                        onClick={() => {
                          setPaymentForm({ amount: '', paymentMethod: '', notes: '' });
                          setShowPaymentModal(true);
                          setSelectedContract({ loan_number: selectedLoan.loan_number, client_name: selectedLoan.borrower_name, metadata: { amount_return: selectedLoan.amount_return } });
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
                      >
                        <DollarSign className="w-4 h-4" />
                        Record Payment
                      </button>

                      {/* Mark as Paid & Send Confirmation */}
                      {selectedLoan.status !== 'paid' && (
                        <button
                          onClick={handleMarkAsPaidAndNotify}
                          disabled={isSendingConfirmation}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-lg text-white text-sm transition-colors"
                        >
                          {isSendingConfirmation ? (
                            <>
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Sending...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Mark as PAID & Send Confirmation
                            </>
                          )}
                        </button>
                      )}

                      {/* Send Payment Summary */}
                      <button
                        onClick={handleSendPaymentConfirmation}
                        disabled={isSendingConfirmation}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 rounded-lg text-white text-sm transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        Send Payment Summary
                      </button>
                    </div>

                    {/* Payment History */}
                    {selectedLoan.payments && selectedLoan.payments.length > 0 && (
                      <div className="bg-zinc-900 rounded-lg p-4 mb-4">
                        <h4 className="text-sm text-zinc-400 mb-3 flex items-center gap-2">
                          <Receipt className="w-4 h-4" />
                          Payment History ({selectedLoan.payments.length} payments)
                        </h4>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {selectedLoan.payments.map((payment, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-zinc-800 rounded-lg p-3">
                              <div>
                                <p className="text-white font-medium">${parseFloat(payment.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                                <p className="text-xs text-zinc-500">{payment.payment_method || 'Not specified'}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-green-400 font-mono">{payment.confirmation_number}</p>
                                <p className="text-xs text-zinc-500">{formatDateShort(payment.timestamp)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {selectedLoan.notes && selectedLoan.notes.length > 0 && (
                      <div className="bg-zinc-900 rounded-lg p-4">
                        <h4 className="text-sm text-zinc-400 mb-3">Notes</h4>
                        <div className="space-y-2">
                          {selectedLoan.notes.map((note, idx) => (
                            <div key={idx} className="text-sm bg-zinc-800 rounded p-2">
                              <p className="text-white">{note.note}</p>
                              <p className="text-xs text-zinc-500 mt-1">{note.timestamp}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Loan List */}
                  {loadingLoans ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="ml-3 text-zinc-400">Loading loans...</span>
                    </div>
                  ) : loans.length === 0 ? (
                    <div className="text-center py-12">
                      <Receipt className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-400">No loans yet</p>
                      <p className="text-sm text-zinc-500 mt-2">Create a Promissory Note to start tracking loans</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {loans.map((loan) => (
                        <div
                          key={loan.loan_number}
                          onClick={() => viewLoanDetail(loan.loan_number)}
                          className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 hover:border-purple-500/50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className="px-3 py-1 rounded-lg text-sm font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                  Loan #{loan.loan_number}
                                </span>
                                <span className="font-semibold text-white">
                                  {loan.borrower_name}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  loan.status === 'paid' ? 'bg-green-500/20 text-green-300' :
                                  loan.status === 'signed' ? 'bg-blue-500/20 text-blue-300' :
                                  loan.status === 'defaulted' ? 'bg-red-500/20 text-red-300' :
                                  'bg-yellow-500/20 text-yellow-300'
                                }`}>
                                  {loan.status?.toUpperCase() || 'PENDING'}
                                </span>
                                {/* Promissory Note signature indicator — RED before signature, GREEN after */}
                                {getPromissoryNoteBadge(loan)}
                                {/* Signed-notes chip — click to open manager */}
                                <SignedNotesChip
                                  loan={loan}
                                  onChanged={fetchLoans}
                                />
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-sm">
                                <span className="text-zinc-400">
                                  Due: <span className="text-white">${parseFloat(loan.amount_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                </span>
                                <span className="text-zinc-400">
                                  Paid: <span className="text-green-400">${parseFloat(loan.total_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                </span>
                                <span className="text-zinc-400">
                                  Remaining: <span className={parseFloat(loan.remaining_balance || loan.amount_return || 0) <= 0 ? 'text-green-400' : 'text-amber-400'}>
                                    ${parseFloat(loan.remaining_balance || loan.amount_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                  </span>
                                </span>
                                {loan.penalty_enabled && (loan.penalty_amount || 0) > 0 && (
                                  <span className="text-red-300 font-semibold">
                                    + Penalty: <span className="text-red-200">${(loan.penalty_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                    <span className="text-red-400/70 text-[10px] ml-1">({loan.penalty_days}d)</span>
                                    <span className="text-red-100 ml-1.5">= ${(loan.total_owed_with_penalty || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                    {!loan.penalty_frozen && loan.next_penalty_at && (
                                      <PenaltyCountdown iso={loan.next_penalty_at} />
                                    )}
                                  </span>
                                )}
                                {/* 2026-02-08 — Loans that have the clause but
                                    aren't past due yet still show a subtle
                                    "next $200 hits at …" hint so the owner
                                    knows the meter's armed. */}
                                {(!loan.penalty_amount || loan.penalty_amount === 0)
                                  && loan.has_late_penalty_clause
                                  && !loan.penalty_frozen
                                  && loan.next_penalty_at && (
                                  <span className="text-amber-400/70 font-mono text-[10px]">
                                    <PenaltyCountdown iso={loan.next_penalty_at} />
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-zinc-500 mt-1">
                                Due: {formatDateShort(loan.date_return)}
                              </p>
                            </div>
                            <div className="flex items-center">
                              <Eye className="w-5 h-5 text-zinc-500" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Loans Ledger Tab */}
          {activeTab === 'ledger' && (
            <div className="space-y-6">
              {/* Header with Save Button */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                  Loans Ledger
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {isMultiDeleteMode ? (
                    <>
                      <button
                        onClick={handleDeleteMultipleLoans}
                        disabled={selectedLoansForDelete.length === 0}
                        className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-zinc-600 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete ({selectedLoansForDelete.length})
                      </button>
                      <button
                        onClick={() => { setIsMultiDeleteMode(false); setSelectedLoansForDelete([]); }}
                        className="px-3 py-2 bg-zinc-600 hover:bg-zinc-500 rounded-lg text-white text-sm font-medium transition-colors"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowAddLoanModal(true)}
                        className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                      >
                        <span className="text-lg">+</span> Add Loan
                      </button>
                      <button
                        onClick={() => setIsMultiDeleteMode(true)}
                        className="px-3 py-2 bg-red-600/20 hover:bg-red-600 border border-red-500/30 rounded-lg text-red-400 hover:text-white text-sm font-medium transition-colors flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Multi Delete
                      </button>
                      <button
                        onClick={handleSaveLedger}
                        disabled={isSavingLedger}
                        className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-zinc-600 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                      >
                        {isSavingLedger ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Saving...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Save
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {loadingLedger ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-zinc-400">Loading ledger...</span>
                </div>
              ) : ledgerData ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/30 border border-purple-500/30 rounded-xl p-4">
                      <p className="text-purple-300 text-xs mb-1">Total Loaned Out</p>
                      <p className="text-2xl font-bold text-white">${ledgerData.summary?.total_loaned?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/30 border border-blue-500/30 rounded-xl p-4">
                      <p className="text-blue-300 text-xs mb-1">Expected Back</p>
                      <p className="text-2xl font-bold text-white">${ledgerData.summary?.total_expected?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-900/40 to-green-800/30 border border-green-500/30 rounded-xl p-4">
                      <p className="text-green-300 text-xs mb-1">Expected Profit</p>
                      <p className="text-2xl font-bold text-green-400">${ledgerData.summary?.total_profit?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-900/40 to-amber-800/30 border border-amber-500/30 rounded-xl p-4">
                      <p className="text-amber-300 text-xs mb-1">Paid So Far</p>
                      <p className="text-2xl font-bold text-amber-400">${ledgerData.summary?.total_paid?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
                    </div>
                    <div className="bg-gradient-to-br from-red-900/40 to-red-800/30 border border-red-500/30 rounded-xl p-4">
                      <p className="text-red-300 text-xs mb-1">Still Owed</p>
                      <p className="text-2xl font-bold text-red-400">${ledgerData.summary?.total_remaining?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
                    </div>
                  </div>

                  {/* Active Loans Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                        <Activity className="w-4 h-4 text-green-400" />
                        Active Loans ({ledgerData.active_loans?.length || 0})
                      </h4>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => setShowEmailActiveLoansModal(true)}
                          disabled={!ledgerData.active_loans?.length}
                          className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 text-xs"
                          data-testid="email-active-loans-btn"
                        >
                          <Mail className="w-3.5 h-3.5 mr-1.5" />
                          Email Active Loans
                        </Button>
                        {/* 2026-02-24 — Bulk switch every $200/day active loan
                            to $100/day (or vice versa).  Runs behind a
                            confirm() so it's never accidental. */}
                        <Button
                          size="sm"
                          onClick={() => handleBulkChangePenaltyRate(200, 100)}
                          disabled={!ledgerData.active_loans?.some(l => l.penalty_enabled && Number(l.penalty_per_day) === 200)}
                          className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                          data-testid="bulk-change-penalty-200-to-100-btn"
                          title="Change every ACTIVE loan currently at $200/day → $100/day and recalculate"
                        >
                          <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                          Bulk $200 → $100/day
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleBulkChangePenaltyRate(100, 200)}
                          disabled={!ledgerData.active_loans?.some(l => l.penalty_enabled && Number(l.penalty_per_day) === 100)}
                          className="bg-zinc-700 hover:bg-zinc-600 text-white h-8 text-xs"
                          data-testid="bulk-change-penalty-100-to-200-btn"
                          title="Change every ACTIVE loan currently at $100/day → $200/day and recalculate"
                        >
                          <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                          Bulk $100 → $200/day
                        </Button>
                        <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
                          Sorted: Due Date (Earliest First)
                        </span>
                      </div>
                    </div>
                    {!ledgerData.active_loans || ledgerData.active_loans.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500 bg-zinc-800/50 rounded-xl">
                        No active loans. Click "Add Loan" to add your first loan.
                      </div>
                    ) : (
                      <>
                        {/* CLEAN PROFESSIONAL TABLE HEADER - Desktop */}
                        <div className="hidden lg:block bg-zinc-900 rounded-t-xl border border-zinc-700 overflow-hidden">
                          <div className="grid grid-cols-[70px_130px_85px_80px_100px_75px_130px] gap-2 text-[11px] font-bold tracking-wide uppercase text-zinc-400 px-3 py-3 bg-gradient-to-r from-zinc-800/80 to-zinc-900/80">
                            <div>Loan #</div>
                            <div>Borrower</div>
                            <div className="text-amber-400">Due Date</div>
                            <div>Lent On</div>
                            <div className="text-right">Amount</div>
                            <div className="text-right">Balance</div>
                            <div className="text-center">Actions</div>
                          </div>
                        </div>
                        
                        {/* View-mode toggle (Detailed / Compact / Calendar) */}
                        <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1 mb-3 border border-zinc-700 max-w-md">
                          {[
                            { id: 'detailed', icon: LayoutGrid, label: 'Detailed' },
                            { id: 'compact',  icon: List,       label: 'Compact'  },
                            { id: 'calendar', icon: CalendarDays, label: 'Calendar' },
                          ].map(v => (
                            <button
                              key={v.id}
                              onClick={() => setLoanLedgerView(v.id)}
                              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded text-xs font-medium transition-colors ${loanLedgerView === v.id ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                              data-testid={`loan-view-${v.id}`}
                            >
                              <v.icon className="w-3.5 h-3.5" /> {v.label}
                            </button>
                          ))}
                        </div>

                        {/* COMPACT view — full loan detail table with
                            horizontal scroll on narrow screens.  User
                            requested (2026-02-28): "The date amount
                            invested proceeds that supposed to be
                            received back in late fees … widen the
                            table so it scrolls horizontally — 7
                            columns visible at once, swipe to see
                            more … Borrower name in yellow, date
                            underneath in smaller gray." */}
                        {loanLedgerView === 'compact' && (
                          <div
                            className="bg-zinc-900 border border-zinc-700 rounded-xl overflow-hidden mb-3"
                            data-testid="loan-compact-view"
                          >
                            <div className="overflow-x-auto">
                              <div className="min-w-[820px]">
                                <div className="grid grid-cols-[minmax(150px,1fr)_90px_100px_100px_100px_100px_100px_60px] gap-2 text-[10px] uppercase tracking-wide text-zinc-400 bg-zinc-800/80 px-3 py-2 font-bold">
                                  <span>Borrower / Loan #</span>
                                  <span className="text-center">Loan Date</span>
                                  <span className="text-right">Invested</span>
                                  <span className="text-right">Proceeds</span>
                                  <span className="text-right">Late&nbsp;Fees</span>
                                  <span className="text-center">Next&nbsp;Pay</span>
                                  <span className="text-right">Balance</span>
                                  <span className="text-center">Edit</span>
                                </div>
                                {sortLoansByNextPayment(ledgerData.active_loans).map(loan => {
                                  const nextPay = loan.expected_payment_date || loan.date_return;
                                  const nextLabel = nextPay
                                    ? new Date(`${String(nextPay).slice(0,10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
                                    : '—';
                                  const isExpected = !!loan.expected_payment_date;
                                  const lentLabel = loan.date_lent
                                    ? new Date(`${String(loan.date_lent).slice(0,10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
                                    : '—';
                                  const invested = Number(loan.amount || 0);
                                  const proceeds = Number(loan.amount_return || 0);
                                  const lateFees = loan.penalty_enabled ? Number(loan.penalty_amount || 0) : 0;
                                  const balance  = Number(loan.remaining_balance || 0);
                                  return (
                                    <div
                                      key={loan.loan_number}
                                      className="grid grid-cols-[minmax(150px,1fr)_90px_100px_100px_100px_100px_100px_60px] gap-2 items-center px-3 py-2 border-t border-zinc-800 text-xs hover:bg-zinc-800/40"
                                      data-testid={`compact-row-${loan.loan_number}`}
                                    >
                                      <div className="min-w-0">
                                        <p className="text-amber-300 font-semibold truncate" data-testid={`compact-borrower-${loan.loan_number}`}>
                                          {loan.borrower_name}
                                        </p>
                                        <p className="text-zinc-500 text-[10px] truncate">
                                          #{loan.loan_number}
                                        </p>
                                      </div>
                                      <span
                                        className="text-center text-[11px] text-zinc-300 font-mono"
                                        title="Original loan date"
                                        data-testid={`compact-lent-${loan.loan_number}`}
                                      >
                                        {lentLabel}
                                      </span>
                                      <span
                                        className="text-right font-mono text-emerald-300"
                                        title="Principal invested"
                                        data-testid={`compact-invested-${loan.loan_number}`}
                                      >
                                        ${invested.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                                      </span>
                                      <span
                                        className="text-right font-mono text-blue-300"
                                        title="Expected proceeds to be received back"
                                        data-testid={`compact-proceeds-${loan.loan_number}`}
                                      >
                                        ${proceeds.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                                      </span>
                                      <span
                                        className={`text-right font-mono ${lateFees > 0 ? 'text-red-300 font-bold' : 'text-zinc-600'}`}
                                        title={loan.penalty_enabled ? `Accrued at $${loan.penalty_per_day || 200}/day` : 'No late fees applied'}
                                        data-testid={`compact-late-${loan.loan_number}`}
                                      >
                                        {lateFees > 0 ? `+$${lateFees.toLocaleString('en-US',{minimumFractionDigits:0})}` : '—'}
                                      </span>
                                      <span
                                        className={`text-center text-[11px] font-semibold ${isExpected ? 'text-cyan-300' : 'text-amber-300'}`}
                                        title={isExpected ? 'Expected payment date (rescheduled)' : 'Original due date'}
                                        data-testid={`compact-nextpay-${loan.loan_number}`}
                                      >
                                        {nextLabel}{isExpected && '*'}
                                      </span>
                                      <span
                                        className={`text-right font-mono ${balance > 0 ? 'text-red-400' : 'text-emerald-400'}`}
                                        data-testid={`compact-balance-${loan.loan_number}`}
                                      >
                                        ${balance.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                                      </span>
                                      <div className="flex justify-center">
                                        <button
                                          onClick={() => openEditLoanModal(loan)}
                                          className="text-zinc-400 hover:text-emerald-300"
                                          title="Edit loan"
                                          data-testid={`compact-edit-${loan.loan_number}`}
                                        >
                                          <Edit2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            <p className="text-[10px] text-zinc-500 italic px-3 py-2 border-t border-zinc-800">
                              * = expected payment date (rescheduled). No asterisk = original due date. Late fees accrue daily when enabled.<br/>
                              <span className="text-cyan-400">Tip:</span> Swipe the table left / right to see every column on narrow screens.
                            </p>
                          </div>
                        )}

                        {/* CALENDAR view — real calendar with Month + Week toggle */}
                        {loanLedgerView === 'calendar' && (() => {
                          // ---------- Shared helpers ----------
                          const fmtMoney = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
                          const todayIso = new Date().toISOString().slice(0, 10);
                          // Bucket every active loan by its next-payment YYYY-MM-DD (expected if set, else original due date)
                          const buckets = {};
                          for (const loan of ledgerData.active_loans || []) {
                            const npd = loan.expected_payment_date || loan.date_return;
                            if (!npd) continue;
                            const key = String(npd).slice(0, 10);
                            (buckets[key] = buckets[key] || []).push(loan);
                          }

                          // Full inline loan card used by BOTH month-detail list and week view
                          // so the user can see name, amount, dates, status, penalty, phone, email.
                          const renderLoanCard = (l, dateKey) => {
                            const penalty = (l.penalty_enabled && l.penalty_amount > 0) ? parseFloat(l.penalty_amount) : 0;
                            const remaining = parseFloat(l.remaining_balance) || parseFloat(l.amount_return) || 0;
                            const balanceWithPenalty = remaining + penalty;
                            const signed = !!l.signed_at || ['signed','paid','paid_in_full'].includes(l.status);
                            return (
                              <div key={`${dateKey}-${l.loan_number}`} className={`rounded-lg p-3 border ${penalty > 0 ? 'bg-red-950/40 border-red-500/40' : 'bg-zinc-900 border-zinc-700'}`} data-testid={`cal-loan-${l.loan_number}`}>
                                <div className="flex items-start justify-between gap-2 mb-1.5">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-white font-bold text-sm truncate">{l.borrower_name}</p>
                                    <p className="text-zinc-400 text-[10px] truncate">#{l.loan_number}{l.borrower_phone ? ` · ${l.borrower_phone}` : ''}</p>
                                    {l.borrower_email && <p className="text-zinc-500 text-[10px] truncate">{l.borrower_email}</p>}
                                  </div>
                                  <button
                                    onClick={() => openEditLoanModal(l)}
                                    className="bg-cyan-700/40 hover:bg-cyan-700/60 text-cyan-100 text-[10px] px-2 py-1 rounded flex-shrink-0"
                                    data-testid={`cal-edit-${l.loan_number}`}
                                  >Edit</button>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-[10px] mb-1.5">
                                  <div>
                                    <p className="text-zinc-500 uppercase text-[9px] tracking-wide">Lent</p>
                                    <p className="text-white font-bold">{fmtMoney(l.amount_lent || 0)}</p>
                                    <p className="text-zinc-500 text-[9px]">{l.date_lent ? new Date(`${String(l.date_lent).slice(0,10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</p>
                                  </div>
                                  <div>
                                    <p className="text-zinc-500 uppercase text-[9px] tracking-wide">Return</p>
                                    <p className="text-white font-bold">{fmtMoney(l.amount_return || 0)}</p>
                                    <p className="text-zinc-500 text-[9px]">due {l.date_return ? new Date(`${String(l.date_return).slice(0,10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</p>
                                  </div>
                                  <div>
                                    <p className="text-zinc-500 uppercase text-[9px] tracking-wide">Balance</p>
                                    <p className={`font-bold ${penalty > 0 ? 'text-red-300' : 'text-emerald-300'}`}>{fmtMoney(balanceWithPenalty)}</p>
                                    {penalty > 0 && <p className="text-red-400 text-[9px]">+{fmtMoney(penalty)} pen.</p>}
                                  </div>
                                </div>
                                {l.expected_payment_date && l.expected_payment_date !== l.date_return && (
                                  <p className="text-cyan-300 text-[10px] mb-1">
                                    Expected: {new Date(`${String(l.expected_payment_date).slice(0,10)}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                  </p>
                                )}
                                <div className="flex flex-wrap items-center gap-1">
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${signed ? 'bg-green-500/20 text-green-300 border border-green-500/40' : 'bg-red-500/20 text-red-300 border border-red-500/40'}`}>
                                    {signed ? '✓ Signed' : 'Awaiting sig.'}
                                  </span>
                                  {l.interest_rate ? <span className="bg-zinc-700/60 text-zinc-300 text-[9px] px-1.5 py-0.5 rounded">{l.interest_rate}%</span> : null}
                                  {Array.isArray(l.payment_shifts) && l.payment_shifts.length > 0 && (
                                    <span className="bg-cyan-700/40 text-cyan-200 text-[9px] px-1.5 py-0.5 rounded">{l.payment_shifts.length} resched.</span>
                                  )}
                                </div>
                                {l.notes && <p className="text-zinc-400 text-[10px] mt-1 italic truncate">"{l.notes}"</p>}
                              </div>
                            );
                          };

                          // ---------- WEEK VIEW ----------
                          if (calendarMode === 'week') {
                            const weekStart = new Date(calendarWeekAnchor);
                            weekStart.setHours(0,0,0,0);
                            const weekEnd = new Date(weekStart);
                            weekEnd.setDate(weekEnd.getDate() + 6);
                            const days = [];
                            for (let i = 0; i < 7; i++) {
                              const d = new Date(weekStart);
                              d.setDate(weekStart.getDate() + i);
                              const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                              days.push({ date: d, iso, loans: buckets[iso] || [] });
                            }
                            const weekTotal = days.reduce((s, dd) => s + dd.loans.reduce((ss, l) => ss + (parseFloat(l.remaining_balance) || parseFloat(l.amount_return) || 0), 0), 0);
                            const weekLoanCount = days.reduce((s, dd) => s + dd.loans.length, 0);
                            const rangeLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                            const shiftWeek = (dir) => {
                              const n = new Date(calendarWeekAnchor);
                              n.setDate(n.getDate() + (dir * 7));
                              setCalendarWeekAnchor(n);
                            };
                            const jumpToThisWeek = () => {
                              const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - d.getDay());
                              setCalendarWeekAnchor(d);
                            };
                            return (
                              <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 mb-3" data-testid="loan-calendar-week-view">
                                {/* Mode toggle Month/Week */}
                                <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1 mb-3 border border-zinc-700 max-w-xs">
                                  <button onClick={() => setCalendarMode('month')} className={`flex-1 py-1 px-2 rounded text-xs font-medium ${calendarMode==='month' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`} data-testid="cal-mode-month">Month</button>
                                  <button onClick={() => setCalendarMode('week')} className={`flex-1 py-1 px-2 rounded text-xs font-medium ${calendarMode==='week' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`} data-testid="cal-mode-week">Week</button>
                                </div>
                                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                                  <button onClick={() => shiftWeek(-1)} className="text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-800 text-sm" data-testid="cal-week-prev">‹ Prev week</button>
                                  <div className="text-center">
                                    <h5 className="text-white font-bold text-sm">{rangeLabel}</h5>
                                    <button onClick={jumpToThisWeek} className="text-[10px] text-cyan-300 hover:text-cyan-200 underline" data-testid="cal-week-today">Jump to this week</button>
                                  </div>
                                  <button onClick={() => shiftWeek(1)} className="text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-800 text-sm" data-testid="cal-week-next">Next week ›</button>
                                </div>
                                <div className="flex items-center justify-between mb-3 px-1">
                                  <span className="text-fuchsia-200 text-xs font-bold uppercase tracking-wide">{weekLoanCount} loan{weekLoanCount===1?'':'s'} this week</span>
                                  <span className="text-emerald-300 text-xs font-bold">{fmtMoney(weekTotal)} total</span>
                                </div>
                                {/* 7-day stack — each day is a header + full loan cards inline */}
                                <div className="space-y-3">
                                  {days.map(dd => {
                                    const isToday = dd.iso === todayIso;
                                    const dayHeader = dd.date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
                                    const dayTotal = dd.loans.reduce((s, l) => s + (parseFloat(l.remaining_balance) || parseFloat(l.amount_return) || 0), 0);
                                    const hasPenalty = dd.loans.some(l => l.penalty_enabled && l.penalty_amount > 0);
                                    return (
                                      <div key={dd.iso} className={`rounded-lg border ${isToday ? 'border-cyan-500/60 bg-cyan-950/20' : (dd.loans.length ? (hasPenalty ? 'border-red-500/40 bg-red-950/10' : 'border-emerald-700/40 bg-emerald-950/10') : 'border-zinc-800 bg-zinc-950/40')} p-2`} data-testid={`cal-week-day-${dd.iso}`}>
                                        <div className="flex items-baseline justify-between mb-2 px-1">
                                          <p className={`text-xs font-bold uppercase tracking-wide ${isToday ? 'text-cyan-300' : 'text-zinc-200'}`}>
                                            {dayHeader}{isToday && ' · TODAY'}
                                          </p>
                                          <p className={`text-xs font-bold ${dd.loans.length ? (hasPenalty ? 'text-red-300' : 'text-emerald-300') : 'text-zinc-600'}`}>
                                            {dd.loans.length ? `${dd.loans.length} · ${fmtMoney(dayTotal)}` : 'nothing due'}
                                          </p>
                                        </div>
                                        {dd.loans.length > 0 && (
                                          <div className="space-y-2">
                                            {dd.loans.map(l => renderLoanCard(l, dd.iso))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                <p className="text-[10px] text-zinc-500 italic mt-3">Cyan border = today · Green = scheduled · Red = late penalty active. Use Prev/Next to scroll weeks.</p>
                              </div>
                            );
                          }

                          // ---------- MONTH VIEW (default) ----------
                          const monthStart = calendarMonth;
                          const year = monthStart.getFullYear();
                          const month = monthStart.getMonth();
                          const firstDay = new Date(year, month, 1);
                          const daysInMonth = new Date(year, month + 1, 0).getDate();
                          const startWeekday = firstDay.getDay();
                          const cells = [];
                          for (let i = 0; i < startWeekday; i++) cells.push(null);
                          for (let d = 1; d <= daysInMonth; d++) {
                            const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                            cells.push({ day: d, iso, loans: buckets[iso] || [] });
                          }
                          while (cells.length % 7 !== 0) cells.push(null);
                          const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                          // Selected day defaults to today (or first day with loans this month)
                          const selectedDay = calendarSelectedDay || todayIso;
                          const selectedDayLoans = buckets[selectedDay] || [];
                          const selectedDt = (() => { try { return new Date(`${selectedDay}T00:00:00`); } catch { return new Date(); } })();
                          return (
                            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 mb-3" data-testid="loan-calendar-view">
                              {/* Mode toggle Month/Week */}
                              <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1 mb-3 border border-zinc-700 max-w-xs">
                                <button onClick={() => setCalendarMode('month')} className={`flex-1 py-1 px-2 rounded text-xs font-medium ${calendarMode==='month' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`} data-testid="cal-mode-month">Month</button>
                                <button onClick={() => { setCalendarMode('week'); const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - d.getDay()); setCalendarWeekAnchor(d); }} className={`flex-1 py-1 px-2 rounded text-xs font-medium ${calendarMode==='week' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`} data-testid="cal-mode-week">Week</button>
                              </div>
                              <div className="flex items-center justify-between mb-3">
                                <button onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} className="text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-800" data-testid="cal-prev">‹ Prev</button>
                                <h5 className="text-white font-bold text-sm">{monthLabel}</h5>
                                <button onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} className="text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-800" data-testid="cal-next">Next ›</button>
                              </div>
                              <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wide text-zinc-500 mb-1 font-semibold">
                                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="text-center py-1">{d}</div>)}
                              </div>
                              {/* Bigger day cells — was h-20, now h-28 sm:h-32 lg:h-36 so we can fit 4 names + amount */}
                              <div className="grid grid-cols-7 gap-1">
                                {cells.map((cell, idx) => {
                                  if (!cell) return <div key={idx} className="h-24 sm:h-32 lg:h-36 bg-zinc-800/30 rounded" />;
                                  const isToday = cell.iso === todayIso;
                                  const isSelected = cell.iso === selectedDay;
                                  const dayTotal = cell.loans.reduce((s, l) => s + (parseFloat(l.remaining_balance) || parseFloat(l.amount_return) || 0), 0);
                                  // Compact $ for mobile cells
                                  const compactAmt = dayTotal >= 1000 ? `$${(dayTotal/1000).toFixed(dayTotal>=10000?0:1)}K` : (dayTotal>0 ? `$${dayTotal.toFixed(0)}` : '');
                                  const hasPenalty = cell.loans.some(l => l.penalty_enabled && l.penalty_amount > 0);
                                  return (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={() => { setCalendarSelectedDay(cell.iso); if (cell.loans.length > 0) setCalendarDayModal(cell.iso); }}
                                      className={`h-24 sm:h-32 lg:h-36 rounded p-1 sm:p-1.5 text-[10px] overflow-hidden flex flex-col text-left transition-colors ${cell.loans.length ? (hasPenalty ? 'bg-red-900/40 border border-red-600/60 hover:bg-red-900/60' : 'bg-emerald-900/30 border border-emerald-700/50 hover:bg-emerald-900/50') : 'bg-zinc-800/40 border border-zinc-700/40 hover:bg-zinc-800/70'} ${isToday ? 'ring-2 ring-cyan-400' : ''} ${isSelected ? 'ring-2 ring-fuchsia-400' : ''}`}
                                      data-testid={`cal-day-${cell.iso}`}
                                    >
                                      <div className="flex items-center justify-between text-[9px] sm:mb-1">
                                        <span className={`font-bold ${isToday ? 'text-cyan-300' : 'text-zinc-200'}`}>{cell.day}</span>
                                        {cell.loans.length > 0 && (
                                          <span className={`text-white rounded-full px-1 sm:px-1.5 text-[8px] sm:text-[9px] font-bold leading-tight ${hasPenalty ? 'bg-red-600' : 'bg-emerald-600'}`}>{cell.loans.length}</span>
                                        )}
                                      </div>
                                      {compactAmt && (
                                        <p className={`text-[9px] sm:text-[10px] font-bold truncate ${hasPenalty ? 'text-red-300' : 'text-emerald-300'}`}>{compactAmt}</p>
                                      )}
                                      {/* Borrower pills inside the cell on BOTH mobile and desktop now
                                          (per user request 2026-02-26: see all loan info on the calendar). */}
                                      <div className="flex-1 overflow-hidden flex flex-col gap-0.5 mt-0.5 sm:mt-1">
                                        {cell.loans.slice(0, 3).map(l => {
                                          const bal = parseFloat(l.remaining_balance) || parseFloat(l.amount_return) || 0;
                                          const penalty = (l.penalty_enabled && l.penalty_amount > 0) ? parseFloat(l.penalty_amount) : 0;
                                          const totalDue = bal + penalty;
                                          const parts = (l.borrower_name || '').trim().split(/\s+/).filter(Boolean);
                                          // First name only on mobile, first + initial on desktop
                                          const abbrMobile = parts[0] ? parts[0].slice(0, 5) : '—';
                                          const abbrDesktop = parts.length >= 2 ? `${parts[0]} ${parts[parts.length-1][0]}.` : (parts[0] || '—');
                                          const amtCompact = totalDue >= 1000 ? `$${(totalDue/1000).toFixed(totalDue>=10000?0:1)}K` : `$${totalDue.toFixed(0)}`;
                                          return (
                                            <div
                                              key={l.loan_number}
                                              className={`px-1 py-0.5 rounded text-[8px] sm:text-[9px] leading-tight ${penalty > 0 ? 'bg-red-900/60 text-red-200' : 'bg-zinc-700/60 text-zinc-100'}`}
                                              title={`${l.borrower_name} — ${fmtMoney(totalDue)}`}
                                            >
                                              <p className="font-semibold truncate">
                                                <span className="sm:hidden">{abbrMobile}</span>
                                                <span className="hidden sm:inline">{abbrDesktop}</span>
                                              </p>
                                              <p className="font-bold opacity-90 truncate">{amtCompact}</p>
                                            </div>
                                          );
                                        })}
                                        {cell.loans.length > 3 && <p className="text-[8px] sm:text-[9px] text-zinc-400 italic px-1">+{cell.loans.length - 3}</p>}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                              {/* Per-day list of EVERY loan due in the visible month
                                  (per user request 2026-02-26 — calendar cells are too small
                                  on mobile, so we render a full chronological list below
                                  with every detail). When a day is selected, the list
                                  filters to that single day. */}
                              <div className="mt-3" data-testid={`cal-month-list-${year}-${month+1}`}>
                                {(() => {
                                  // Days of the visible month that have loans, in order
                                  const datesWithLoans = Object.keys(buckets).filter(k => {
                                    const dt = new Date(`${k}T00:00:00`);
                                    return dt.getFullYear() === year && dt.getMonth() === month;
                                  }).sort();
                                  const showOnlySelected = calendarSelectedDay && buckets[calendarSelectedDay];
                                  const datesToShow = showOnlySelected ? [calendarSelectedDay] : datesWithLoans;
                                  const monthTotal = datesWithLoans.reduce((s, k) => s + (buckets[k] || []).reduce((ss, l) => ss + (parseFloat(l.remaining_balance) || parseFloat(l.amount_return) || 0), 0), 0);
                                  return (
                                    <>
                                      <div className="flex items-baseline justify-between mb-2 px-1">
                                        <h6 className="text-fuchsia-200 text-xs font-bold uppercase tracking-wide">
                                          {showOnlySelected
                                            ? `Loans due ${selectedDt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`
                                            : `All ${monthLabel} loans`}
                                        </h6>
                                        <div className="flex items-center gap-2">
                                          {showOnlySelected && (
                                            <button onClick={() => setCalendarSelectedDay(null)} className="text-[10px] text-cyan-300 hover:text-cyan-200 underline" data-testid="cal-clear-filter">show all month</button>
                                          )}
                                          <span className="text-[10px] text-zinc-400">{fmtMoney(showOnlySelected ? selectedDayLoans.reduce((s, l) => s + (parseFloat(l.remaining_balance) || parseFloat(l.amount_return) || 0), 0) : monthTotal)} total</span>
                                        </div>
                                      </div>
                                      {datesToShow.length === 0 ? (
                                        <p className="text-zinc-500 text-xs italic py-4 text-center bg-zinc-950/60 border border-zinc-700 rounded-xl">No loans due in {monthLabel}.</p>
                                      ) : (
                                        <div className="space-y-3">
                                          {datesToShow.map(dateKey => {
                                            const loansForDay = buckets[dateKey] || [];
                                            const dDt = new Date(`${dateKey}T00:00:00`);
                                            const dayHeader = dDt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                                            const dayTotal = loansForDay.reduce((s, l) => s + (parseFloat(l.remaining_balance) || parseFloat(l.amount_return) || 0), 0);
                                            return (
                                              <div key={dateKey} data-testid={`cal-list-day-${dateKey}`}>
                                                <div className="flex items-baseline justify-between mb-1.5 px-1">
                                                  <p className="text-cyan-300 text-[11px] font-bold uppercase tracking-wide">{dayHeader} — {loansForDay.length} loan{loansForDay.length===1?'':'s'}</p>
                                                  <p className="text-emerald-300 text-[11px] font-bold">{fmtMoney(dayTotal)}</p>
                                                </div>
                                                <div className="space-y-2">
                                                  {loansForDay.map(l => {
                                                    const penalty = (l.penalty_enabled && l.penalty_amount > 0) ? parseFloat(l.penalty_amount) : 0;
                                                    const remaining = parseFloat(l.remaining_balance) || parseFloat(l.amount_return) || 0;
                                                    const balanceWithPenalty = remaining + penalty;
                                                    const signed = !!l.signed_at || ['signed','paid','paid_in_full'].includes(l.status);
                                                    return (
                                                      <div key={l.loan_number} className={`rounded-lg p-3 border ${penalty > 0 ? 'bg-red-950/40 border-red-500/40' : 'bg-zinc-900 border-zinc-700'}`} data-testid={`cal-list-loan-${l.loan_number}`}>
                                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                                          <div className="min-w-0 flex-1">
                                                            <p className="text-white font-bold text-sm truncate">{l.borrower_name}</p>
                                                            <p className="text-zinc-400 text-[10px] truncate">#{l.loan_number}{l.borrower_phone ? ` · ${l.borrower_phone}` : ''}</p>
                                                            {l.borrower_email && <p className="text-zinc-500 text-[10px] truncate">{l.borrower_email}</p>}
                                                          </div>
                                                          <button
                                                            onClick={() => openEditLoanModal(l)}
                                                            className="bg-cyan-700/40 hover:bg-cyan-700/60 text-cyan-100 text-[10px] px-2 py-1 rounded flex-shrink-0"
                                                            data-testid={`cal-list-edit-${l.loan_number}`}
                                                          >Edit</button>
                                                        </div>
                                                        <div className="grid grid-cols-3 gap-2 text-[10px] mb-1.5">
                                                          <div>
                                                            <p className="text-zinc-500 uppercase text-[9px] tracking-wide">Lent</p>
                                                            <p className="text-white font-bold">{fmtMoney(l.amount_lent || 0)}</p>
                                                            <p className="text-zinc-500 text-[9px]">{l.date_lent ? new Date(`${String(l.date_lent).slice(0,10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</p>
                                                          </div>
                                                          <div>
                                                            <p className="text-zinc-500 uppercase text-[9px] tracking-wide">Return</p>
                                                            <p className="text-white font-bold">{fmtMoney(l.amount_return || 0)}</p>
                                                            <p className="text-zinc-500 text-[9px]">due {l.date_return ? new Date(`${String(l.date_return).slice(0,10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</p>
                                                          </div>
                                                          <div>
                                                            <p className="text-zinc-500 uppercase text-[9px] tracking-wide">Balance</p>
                                                            <p className={`font-bold ${penalty > 0 ? 'text-red-300' : 'text-emerald-300'}`}>{fmtMoney(balanceWithPenalty)}</p>
                                                            {penalty > 0 && <p className="text-red-400 text-[9px]">+{fmtMoney(penalty)} pen.</p>}
                                                          </div>
                                                        </div>
                                                        {l.expected_payment_date && l.expected_payment_date !== l.date_return && (
                                                          <p className="text-cyan-300 text-[10px] mb-1">
                                                            Expected: {new Date(`${String(l.expected_payment_date).slice(0,10)}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                          </p>
                                                        )}
                                                        <div className="flex flex-wrap items-center gap-1">
                                                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${signed ? 'bg-green-500/20 text-green-300 border border-green-500/40' : 'bg-red-500/20 text-red-300 border border-red-500/40'}`}>
                                                            {signed ? '✓ Signed' : 'Awaiting sig.'}
                                                          </span>
                                                          {l.interest_rate ? <span className="bg-zinc-700/60 text-zinc-300 text-[9px] px-1.5 py-0.5 rounded">{l.interest_rate}%</span> : null}
                                                          {Array.isArray(l.payment_shifts) && l.payment_shifts.length > 0 && (
                                                            <span className="bg-cyan-700/40 text-cyan-200 text-[9px] px-1.5 py-0.5 rounded">{l.payment_shifts.length} resched.</span>
                                                          )}
                                                        </div>
                                                        {l.notes && <p className="text-zinc-400 text-[10px] mt-1 italic truncate">"{l.notes}"</p>}
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              <p className="text-[10px] text-zinc-500 italic mt-2">Tap any day with loans to pop up full details. Cyan ring = today · Pink ring = selected · Red = penalty active.</p>
                              {/* Per-day popup modal (per user request 2026-02-27) */}
                              {calendarDayModal && buckets[calendarDayModal] && (() => {
                                const modalLoans = buckets[calendarDayModal];
                                const modalDt = new Date(`${calendarDayModal}T00:00:00`);
                                const modalHeader = modalDt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                                const modalTotal = modalLoans.reduce((s, l) => s + (parseFloat(l.remaining_balance) || parseFloat(l.amount_return) || 0), 0);
                                const modalPenalty = modalLoans.reduce((s, l) => s + ((l.penalty_enabled && l.penalty_amount > 0) ? parseFloat(l.penalty_amount) : 0), 0);
                                return (
                                  <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-3 sm:p-6" onClick={() => setCalendarDayModal(null)} data-testid="cal-day-modal">
                                    <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                                      <div className="flex items-start justify-between gap-3 p-4 border-b border-zinc-700 bg-gradient-to-r from-fuchsia-900/40 to-cyan-900/40">
                                        <div className="min-w-0">
                                          <p className="text-fuchsia-200 text-[10px] uppercase tracking-widest font-bold">Loans due</p>
                                          <h3 className="text-white font-bold text-base sm:text-lg truncate" data-testid="cal-day-modal-header">{modalHeader}</h3>
                                          <p className="text-[11px] text-zinc-300 mt-1">
                                            <span className="text-emerald-300 font-bold">{modalLoans.length}</span> loan{modalLoans.length===1?'':'s'} · <span className="text-emerald-300 font-bold">{fmtMoney(modalTotal)}</span>{modalPenalty > 0 && <> · <span className="text-red-300 font-bold">+{fmtMoney(modalPenalty)} pen.</span></>}
                                          </p>
                                        </div>
                                        <button onClick={() => setCalendarDayModal(null)} className="text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0" data-testid="cal-day-modal-close" aria-label="Close">
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                      <div className="overflow-y-auto p-3 space-y-3 flex-1">
                                        {modalLoans.map(l => renderLoanCard(l, calendarDayModal))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })()}

                        {/* DETAILED view — original cards (default) */}
                        {loanLedgerView === 'detailed' && (<>
                        {/* Loan Rows - Clean Professional Design (sorted by next payment date — expected_payment_date if set, else date_return) */}
                        <div className="space-y-0 lg:space-y-0">
                        {sortLoansByNextPayment(ledgerData.active_loans)
                          .map((loan, index) => {
                          // Feb 2026 — color-code every card by lender_name
                          // so you can visually distinguish whose loan it is
                          // at a glance.  Palette hashes off the lender's
                          // name so the same lender always gets the same
                          // color.  The 4px left-border stripe is what does
                          // the visual grouping; card bg stays neutral.
                          const stripe = lenderColorStripe(loan.lender_name);
                          return (
                          <div
                            key={loan.loan_number}
                            className={`relative transition-all duration-200 ${
                              index === ledgerData.active_loans.length - 1 ? 'lg:rounded-b-xl' : ''
                            } ${
                              isMultiDeleteMode && selectedLoansForDelete.includes(loan.loan_number)
                                ? 'border-2 border-red-500 bg-red-900/20'
                                : 'border border-zinc-700 lg:border-x lg:border-b lg:border-t-0 hover:bg-zinc-800/70'
                            } bg-zinc-900/90 rounded-xl lg:rounded-none mb-3 lg:mb-0 overflow-hidden`}
                            style={{
                              // 6px lender-color stripe on the left edge.
                              boxShadow: `inset 6px 0 0 0 ${stripe.color}`,
                            }}
                            data-lender-stripe={stripe.key}
                          >
                            {/* Desktop Table Row */}
                            <div className="hidden lg:grid lg:grid-cols-[70px_130px_85px_80px_100px_75px_130px] gap-2 items-center px-3 py-3">
                              {isMultiDeleteMode && (
                                <button
                                  onClick={() => toggleLoanSelection(loan.loan_number)}
                                  className={`absolute left-2 w-5 h-5 rounded border-2 flex items-center justify-center z-10 ${
                                    selectedLoansForDelete.includes(loan.loan_number)
                                      ? 'bg-red-500 border-red-500' : 'border-zinc-500'
                                  }`}
                                >
                                  {selectedLoansForDelete.includes(loan.loan_number) && <Check className="w-3 h-3 text-white" />}
                                </button>
                              )}
                              
                              {/* Loan Number - Fixed Width */}
                              <div>
                                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-purple-600/30 text-purple-300 border border-purple-500/40 truncate max-w-[65px]">
                                  {loan.loan_number}
                                </span>
                              </div>
                              
                              {/* Borrower - Fixed Width */}
                              <div className="min-w-0">
                                <p className="font-semibold text-white text-xs truncate">{loan.borrower_name}</p>
                                <p className="text-[10px] text-zinc-500 truncate mb-1">{loan.borrower_email}</p>
                                {/* Promissory Note signature status — RED when sent (awaiting), GREEN when signed */}
                                {(() => {
                                  const isSigned = !!loan.signed_at || ['signed','paid','paid_in_full'].includes(loan.status);
                                  return (
                                    <span
                                      data-testid={`promissory-note-pill-${loan.loan_number}`}
                                      title={isSigned ? 'Promissory Note Signed' : 'Promissory Note Sent — awaiting borrower signature'}
                                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${
                                        isSigned
                                          ? 'bg-green-500/20 text-green-300 border border-green-500/50'
                                          : 'bg-red-500/20 text-red-300 border border-red-500/50 animate-pulse'
                                      }`}
                                    >
                                      {isSigned ? (
                                        <>
                                          <CheckCircle className="w-2.5 h-2.5" />
                                          Note Signed
                                        </>
                                      ) : (
                                        <>
                                          <Send className="w-2.5 h-2.5" />
                                          Note Sent
                                        </>
                                      )}
                                    </span>
                                  );
                                })()}
                              </div>
                              
                              {/* DUE DATE - PRIMARY */}
                              <div>
                                <p className="text-amber-400 font-bold text-xs">{formatDateShort(loan.date_return)}</p>
                                <p className="text-[10px] text-zinc-600">Due</p>
                              </div>
                              
                              {/* DATE LENT */}
                              <div>
                                <p className="text-cyan-400 font-medium text-xs">{formatDateShort(loan.date_lent || loan.created_at)}</p>
                                <p className="text-[10px] text-zinc-600">Lent</p>
                              </div>
                              
                              {/* Amount Info */}
                              <div className="text-right">
                                <p className="text-white font-semibold text-xs">${loan.amount_lent?.toLocaleString('en-US', { minimumFractionDigits: 0 })}</p>
                                <p className="text-[10px] text-zinc-500">→${loan.amount_return?.toLocaleString('en-US', { minimumFractionDigits: 0 })}</p>
                                <p className="text-[10px] text-emerald-400">+${loan.profit?.toLocaleString('en-US', { minimumFractionDigits: 0 })}</p>
                              </div>
                              
                              {/* Balance */}
                              <div className="text-right">
                                <p className={`font-bold text-xs ${loan.remaining_balance <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  ${loan.remaining_balance?.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                                </p>
                                {loan.total_paid > 0 && (
                                  <div className="w-full bg-zinc-700 rounded-full h-1 mt-1">
                                    <div 
                                      className={`h-1 rounded-full ${loan.remaining_balance <= 0 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                                      style={{ width: `${Math.min(100, (loan.total_paid / loan.amount_return) * 100)}%` }}
                                    />
                                  </div>
                                )}
                              </div>
                              
                              {/* Action Buttons - Compact */}
                              <div className="flex justify-center gap-1 flex-wrap">
                                {loan.remaining_balance > 0 && (
                                  <button
                                    onClick={() => {
                                      setSelectedLoan(loan);
                                      setPaymentForm({ amount: '', paymentMethod: '', notes: '' });
                                      setShowPaymentModal(true);
                                    }}
                                    className="p-1.5 bg-emerald-600 hover:bg-emerald-500 rounded text-white transition-colors"
                                    title="Record Payment"
                                    data-testid={`record-payment-${loan.loan_number}`}
                                  >
                                    <DollarSign className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {loan.remaining_balance > 0 && loan.total_paid > 0 && (
                                  <button
                                    onClick={() => handleSendPartialPaymentNotice(loan)}
                                    className="p-1.5 bg-amber-600 hover:bg-amber-500 rounded text-white transition-colors"
                                    title="Email Partial-Payment Notice"
                                    data-testid={`partial-notice-${loan.loan_number}`}
                                  >
                                    <Bell className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => openEditLoanModal(loan)}
                                  className="p-1.5 bg-cyan-600 hover:bg-cyan-500 rounded text-white transition-colors"
                                  title="Edit Loan"
                                  data-testid={`edit-loan-${loan.loan_number}`}
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleResendNote(loan.loan_number, loan.borrower_name)}
                                  className="p-1.5 bg-blue-600 hover:bg-blue-500 rounded text-white transition-colors"
                                  title="Send Promissory Note"
                                >
                                  <Mail className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={async () => {
                                    if (loan.contract_id) {
                                      const link = `${window.location.origin}/sign-contract/${loan.contract_id}`;
                                      await navigator.clipboard.writeText(link);
                                      toast.success('Signing link copied to clipboard!');
                                    } else {
                                      toast.info('No contract yet. Click "Send Promissory Note" first to generate a signing link.');
                                    }
                                  }}
                                  className="p-1.5 bg-violet-600 hover:bg-violet-500 rounded text-white transition-colors"
                                  title="Copy Signing Link"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleCloseLoan(loan.loan_number)}
                                  className="p-1.5 bg-zinc-600 hover:bg-zinc-500 rounded text-white transition-colors"
                                  title="Close Loan"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Mobile Card View */}
                            <div className="lg:hidden">
                            {/* Header: Loan # and Name */}
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              {/* Checkbox for multi-delete */}
                              {isMultiDeleteMode && (
                                <button
                                  onClick={() => toggleLoanSelection(loan.loan_number)}
                                  className={`mr-3 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                                    selectedLoansForDelete.includes(loan.loan_number)
                                      ? 'bg-red-500 border-red-500'
                                      : 'border-zinc-500 hover:border-red-400'
                                  }`}
                                >
                                  {selectedLoansForDelete.includes(loan.loan_number) && (
                                    <Check className="w-4 h-4 text-white" />
                                  )}
                                </button>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <span className="px-2 py-1 rounded-lg text-xs sm:text-sm font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                    #{loan.loan_number}
                                  </span>
                                  <span className="font-semibold text-white text-base sm:text-lg truncate">{loan.borrower_name}</span>
                                  {/* Lender chip — same color as the left
                                      stripe so the eye can group loans by
                                      lender across the whole list. */}
                                  {loan.lender_name && (
                                    <span
                                      className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border"
                                      style={{
                                        color: stripe.color,
                                        borderColor: `${stripe.color}66`,
                                        backgroundColor: `${stripe.color}1A`,
                                      }}
                                      title={`Lender: ${loan.lender_name}`}
                                      data-testid={`loan-lender-chip-${loan.loan_number}`}
                                    >
                                      💼 {loan.lender_name}
                                    </span>
                                  )}
                                  {/* Rollover indicator */}
                                  {loan.rolled_over_from && (
                                    <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                                      Rolled from #{loan.rolled_over_from}
                                    </span>
                                  )}
                                  {/* Promissory Note signature indicator — RED before signature, GREEN after */}
                                  {getPromissoryNoteBadge(loan)}
                                </div>
                                {/* Paid status badge */}
                                {loan.total_paid > 0 && (
                                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-2 ${
                                    loan.remaining_balance <= 0 
                                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                      : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                  }`}>
                                    {loan.remaining_balance <= 0 ? 'PAID IN FULL' : `$${loan.total_paid?.toLocaleString('en-US', { minimumFractionDigits: 2 })} Paid`}
                                  </span>
                                )}
                                
                                {/* DUE DATE FIRST - Prominent Display */}
                                <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-3 mb-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 rounded-lg bg-amber-500/30 flex items-center justify-center">
                                        <span className="text-amber-400 text-xs font-bold">DUE</span>
                                      </div>
                                      <div>
                                        <p className="text-amber-400 font-bold text-lg">{formatDateShort(loan.date_return)}</p>
                                        <p className="text-xs text-zinc-400">Repayment Date</p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-xs text-zinc-500">Lent On</p>
                                      <p className="text-cyan-400 font-medium">{formatDateShort(loan.date_lent || loan.created_at)}</p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Loan Details - Mobile Grid */}
                                <div className="grid grid-cols-3 gap-2 text-sm">
                                  <div className="bg-zinc-800/60 rounded-xl p-3 border border-zinc-700/50">
                                    <p className="text-zinc-500 text-xs mb-1">Lent</p>
                                    <p className="text-white font-bold">${loan.amount_lent?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                                  </div>
                                  <div className="bg-zinc-800/60 rounded-xl p-3 border border-zinc-700/50">
                                    <p className="text-zinc-500 text-xs mb-1">Return</p>
                                    <p className="text-blue-400 font-bold">${(loan.amount_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                                    <p className="text-emerald-400 text-xs">+${(loan.profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                                    {loan.penalty_enabled && (loan.penalty_amount || 0) > 0 && (
                                      <>
                                        <p className="text-red-300 text-[10px] mt-1 leading-tight">
                                          +${(loan.penalty_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} penalty
                                          <span className="text-red-400/70 ml-1">({loan.penalty_days}d)</span>
                                        </p>
                                        <p className="text-red-200 text-xs font-bold mt-0.5 border-t border-red-500/30 pt-1">
                                          = ${((loan.amount_return || 0) + (loan.penalty_amount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </p>
                                      </>
                                    )}
                                  </div>
                                  <div className="bg-zinc-800/60 rounded-xl p-3 border border-zinc-700/50">
                                    <p className="text-zinc-500 text-xs mb-1">Balance</p>
                                    <p className={`font-bold ${loan.remaining_balance <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                      ${(loan.remaining_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </p>
                                    {loan.penalty_enabled && (loan.penalty_amount || 0) > 0 && (
                                      <>
                                        <p className="text-red-300 text-[10px] mt-1 leading-tight">
                                          +${(loan.penalty_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} penalty
                                          <span className="text-red-400/70 ml-1">({loan.penalty_days}d)</span>
                                        </p>
                                        <p className="text-red-200 text-xs font-bold mt-0.5 border-t border-red-500/30 pt-1">
                                          = ${(loan.total_owed_with_penalty || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </p>
                                      </>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Payment Progress Bar */}
                                {loan.amount_return > 0 && (
                                  <div className="mt-4">
                                    <div className="flex justify-between text-xs text-zinc-400 mb-2">
                                      <span className="font-medium">Payment Progress</span>
                                      <span className="text-cyan-400 font-bold">{Math.round((loan.total_paid / loan.amount_return) * 100)}%</span>
                                    </div>
                                    <div className="w-full bg-zinc-700/50 rounded-full h-2.5 overflow-hidden">
                                      <div 
                                        className={`h-2.5 rounded-full transition-all duration-500 ${loan.remaining_balance <= 0 ? 'bg-gradient-to-r from-emerald-500 to-cyan-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'}`}
                                        style={{ width: `${Math.min(100, (loan.total_paid / loan.amount_return) * 100)}%` }}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Split Payments preview (Feb 2026) — shown inline
                                    on the loan card so you can see the schedule
                                    without opening the edit modal.  Tap a row to
                                    toggle paid/unpaid inline. */}
                                {Array.isArray(loan.split_payments) && loan.split_payments.length > 0 && (
                                  <LoanSplitsInline
                                    loan={loan}
                                    onToggle={async (splitId, targetPaid) => {
                                      try {
                                        await axios.post(`${API}/api/loans/${loan.loan_number}/split-payments/${splitId}/toggle-paid?paid=${targetPaid}`);
                                        fetchLoanLedger();
                                      } catch (e) {
                                        toast.error("Failed to update split");
                                      }
                                    }}
                                  />
                                )}

                                {/* Additional Terms (late fees, penalties, special clauses) */}
                                {loan.additional_terms && loan.additional_terms.trim() && (
                                  <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/5 p-3" data-testid={`loan-additional-terms-${loan.loan_number}`}>
                                    <div className="flex items-start gap-2">
                                      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-amber-300 text-xs font-semibold uppercase tracking-wide mb-1">Additional Terms</p>
                                        <p className="text-amber-100 text-sm whitespace-pre-wrap break-words">{loan.additional_terms}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Payment Shift History — shown inline when the
                                    borrower has rescheduled at least once.
                                    Tracks broken-promise patterns visually. */}
                                {Array.isArray(loan.payment_shifts) && loan.payment_shifts.length > 0 && (
                                  <div className="mt-4 rounded-xl border border-cyan-500/40 bg-cyan-500/5 p-3" data-testid={`loan-payment-shifts-${loan.loan_number}`}>
                                    <div className="flex items-start gap-2">
                                      <CalendarDays className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-cyan-300 text-xs font-semibold uppercase tracking-wide mb-2">
                                          Payment Shift History
                                          <span className="ml-1.5 text-cyan-400/70 font-normal normal-case">({loan.payment_shifts.length} reschedule{loan.payment_shifts.length === 1 ? '' : 's'})</span>
                                        </p>
                                        <ul className="space-y-1.5">
                                          {[...loan.payment_shifts].reverse().map((sh, idx) => (
                                            <li key={idx} className="text-cyan-100 text-xs leading-snug">
                                              <span className="text-zinc-400 line-through">{sh.previous_date ? formatDateShort(sh.previous_date) : '—'}</span>
                                              <span className="mx-1.5 text-cyan-400">→</span>
                                              <span className="text-cyan-200 font-semibold">{sh.new_date ? formatDateShort(sh.new_date) : 'cleared'}</span>
                                              {sh.reason && <span className="text-cyan-100/80"> · {sh.reason}</span>}
                                              {sh.shifted_at && <span className="block text-[10px] text-zinc-500">on {formatDateShort(sh.shifted_at)}</span>}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Action Buttons - Full width on mobile, side on desktop */}
                              {!isMultiDeleteMode && (
                                <div className="flex flex-col gap-2 mt-3 sm:mt-0 sm:ml-4 pt-3 sm:pt-0 border-t sm:border-t-0 border-zinc-700">
                                  {/* Primary Actions Row */}
                                  <div className="flex flex-wrap gap-2">
                                    {/* Record Payment Button */}
                                    {loan.remaining_balance > 0 && (
                                      <button
                                        onClick={() => {
                                          setSelectedLoan(loan);
                                          setPaymentForm({ 
                                            amount: '', 
                                            paymentMethod: '', 
                                            notes: '' 
                                          });
                                          setShowPaymentModal(true);
                                        }}
                                        className="flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-xs sm:text-sm font-medium transition-colors"
                                        title="Record Payment"
                                      >
                                        <DollarSign className="w-4 h-4" />
                                        <span>Pay</span>
                                      </button>
                                    )}
                                    
                                    {/* Mark Paid in Full Button */}
                                    {loan.remaining_balance > 0 && (
                                      <button
                                        onClick={() => handleMarkPaidInFull(loan)}
                                        disabled={isMarkingPaid}
                                        className="flex items-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-600 rounded-lg text-white text-xs sm:text-sm font-medium transition-colors"
                                        title="Mark as Paid in Full"
                                      >
                                        <CheckCircle className="w-4 h-4" />
                                        <span className="hidden sm:inline">Paid Full</span>
                                      </button>
                                    )}

                                    {/* Late Penalty — either add-with-rate-picker
                                        OR the running tally with an inline
                                        $100 ↔ $200 ↔ Off switch (2026-02-24). */}
                                    {loan.remaining_balance > 0 && !loan.penalty_enabled && (
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => handleAddLatePenalty(loan, 200)}
                                          className="flex items-center gap-1 px-2.5 py-2 bg-amber-600 hover:bg-amber-700 rounded-l-lg text-white text-xs sm:text-sm font-medium transition-colors border-r border-amber-800"
                                          title="Start the $200/day penalty meter (accrues from the day after the due date)"
                                          data-testid={`add-late-penalty-200-${loan.loan_number}`}
                                        >
                                          <AlertTriangle className="w-4 h-4" />
                                          <span className="hidden sm:inline">+$200/day</span>
                                          <span className="sm:hidden">$200</span>
                                        </button>
                                        <button
                                          onClick={() => handleAddLatePenalty(loan, 100)}
                                          className="flex items-center gap-1 px-2.5 py-2 bg-amber-700 hover:bg-amber-800 rounded-r-lg text-white text-xs sm:text-sm font-medium transition-colors"
                                          title="Start the $100/day penalty meter (accrues from the day after the due date)"
                                          data-testid={`add-late-penalty-100-${loan.loan_number}`}
                                        >
                                          <span className="hidden sm:inline">+$100/day</span>
                                          <span className="sm:hidden">$100</span>
                                        </button>
                                      </div>
                                    )}
                                    {loan.penalty_enabled && (
                                      <div className="flex items-center gap-1.5 bg-red-600/30 border border-red-500/60 px-2.5 py-1.5 rounded-lg" title={`Penalty meter ON · ${loan.penalty_days || 0} day(s) × $${loan.penalty_per_day || 200} = $${(loan.penalty_amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}`}>
                                        <AlertTriangle className="w-3.5 h-3.5 text-red-300" />
                                        <div className="flex flex-col leading-tight">
                                          <span className="text-[10px] text-red-300 uppercase font-semibold tracking-wide">Penalty @ ${loan.penalty_per_day || 200}/day</span>
                                          <span className="text-white text-xs font-bold">
                                            +${(loan.penalty_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                            <span className="text-red-300 font-normal text-[10px] ml-1">({loan.penalty_days || 0}d)</span>
                                          </span>
                                        </div>
                                        {/* 3-way rate picker: $100 · $200 · Off — recalculates automatically. */}
                                        <div className="flex items-center gap-0.5 ml-1 border-l border-red-500/40 pl-1">
                                          <button
                                            onClick={() => Number(loan.penalty_per_day) !== 100 && handleChangePenaltyRate(loan, 100)}
                                            className={`text-[10px] px-1.5 py-0.5 rounded font-semibold transition-colors ${Number(loan.penalty_per_day) === 100 ? 'bg-red-500 text-white cursor-default' : 'bg-red-500/20 text-red-200 hover:bg-red-500/40'}`}
                                            title="Change to $100/day and recalculate"
                                            data-testid={`penalty-rate-100-${loan.loan_number}`}
                                          >$100</button>
                                          <button
                                            onClick={() => Number(loan.penalty_per_day) !== 200 && handleChangePenaltyRate(loan, 200)}
                                            className={`text-[10px] px-1.5 py-0.5 rounded font-semibold transition-colors ${Number(loan.penalty_per_day) === 200 ? 'bg-red-500 text-white cursor-default' : 'bg-red-500/20 text-red-200 hover:bg-red-500/40'}`}
                                            title="Change to $200/day and recalculate"
                                            data-testid={`penalty-rate-200-${loan.loan_number}`}
                                          >$200</button>
                                          <button
                                            onClick={() => handleClearLatePenalty(loan)}
                                            className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-zinc-700/60 text-zinc-200 hover:bg-zinc-600/80 transition-colors"
                                            title="Turn the penalty meter OFF for this loan"
                                            data-testid={`penalty-off-${loan.loan_number}`}
                                          >Off</button>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Missed Payment Button */}
                                    {loan.remaining_balance > 0 && (
                                      <button
                                        onClick={() => {
                                          setSelectedLoan(loan);
                                          setMissedPaymentForm({ interestAmount: '', newDateReturn: '', notes: '' });
                                          setShowMissedPaymentModal(true);
                                        }}
                                        className="flex items-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-xs sm:text-sm font-medium transition-colors"
                                        title="Missed Payment - Add Interest & New Due Date"
                                        data-testid="missed-payment-btn"
                                      >
                                        <AlertTriangle className="w-4 h-4" />
                                        <span className="hidden sm:inline">Missed Payment</span>
                                      </button>
                                    )}
                                    
                                    {/* Email Paid Confirmation Button - Shows when loan is fully paid */}
                                    {(loan.remaining_balance <= 0 || loan.status === 'paid') && (
                                      <button
                                        onClick={() => handleSendPaidConfirmationEmail(loan)}
                                        disabled={isSendingConfirmation}
                                        className="flex items-center gap-1 px-3 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-600 rounded-lg text-white text-xs sm:text-sm font-medium transition-colors"
                                        title="Email Paid Confirmation to Borrower"
                                      >
                                        <Mail className="w-4 h-4" />
                                        <span className="hidden sm:inline">Email Paid</span>
                                      </button>
                                    )}
                                    
                                    {/* Rollover Button */}
                                    {loan.remaining_balance > 0 && (
                                      <button
                                        onClick={() => {
                                          setSelectedLoan(loan);
                                          setRolloverForm({
                                            paymentBeforeRollover: '',
                                            rolloverAmount: String(loan.remaining_balance || 0),
                                            newBorrowerName: loan.borrower_name,
                                            newBorrowerEmail: loan.borrower_email || '',
                                            newBorrowerPhone: loan.borrower_phone || '',
                                            additionalAmount: '',
                                            newAmountReturn: '',
                                            newDateReturn: '',
                                            newDateLent: new Date().toISOString().split('T')[0],
                                            interestRate: '',
                                            notes: `Rolled over from Loan #${loan.loan_number}`,
                                            sendContract: true
                                          });
                                          setShowRolloverModal(true);
                                        }}
                                        className="flex items-center gap-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-xs sm:text-sm font-medium transition-colors"
                                        title="Rollover to New Loan"
                                      >
                                        <RefreshCw className="w-4 h-4" />
                                        <span className="hidden sm:inline">Rollover</span>
                                      </button>
                                    )}
                                  </div>
                                  
                                  {/* Secondary Actions Row */}
                                  <div className="flex flex-wrap items-center gap-2">
                                    {/* Send Payment Reminder Button */}
                                    <button
                                      onClick={() => handleSendPaymentReminder(loan)}
                                      disabled={isSendingConfirmation || !loan.borrower_email}
                                      className="flex items-center gap-1 px-2 py-1.5 bg-amber-600/20 hover:bg-amber-600 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-amber-400 hover:text-white text-xs transition-colors"
                                      title={loan.borrower_email ? "Send Payment Reminder" : "No email on file"}
                                    >
                                      <Clock className="w-3 h-3" />
                                      <span>Send Reminder</span>
                                    </button>
                                    
                                    {/* Partial Payment Notice */}
                                    {loan.remaining_balance > 0 && loan.total_paid > 0 && (
                                      <button
                                        onClick={() => handleSendPartialPaymentNotice(loan)}
                                        className="flex items-center gap-1 px-2 py-1.5 bg-amber-600/20 hover:bg-amber-600 rounded-lg text-amber-300 hover:text-white text-xs transition-colors"
                                        title="Email Partial-Payment Notice"
                                        data-testid={`partial-notice-mobile-${loan.loan_number}`}
                                      >
                                        <Bell className="w-3 h-3" />
                                        <span>Partial Notice</span>
                                      </button>
                                    )}
                                    
                                    {/* Edit Loan */}
                                    <button
                                      onClick={() => openEditLoanModal(loan)}
                                      className="flex items-center gap-1 px-2 py-1.5 bg-cyan-600/20 hover:bg-cyan-600 rounded-lg text-cyan-300 hover:text-white text-xs transition-colors"
                                      title="Edit Loan Details"
                                      data-testid={`edit-loan-mobile-${loan.loan_number}`}
                                    >
                                      <Edit2 className="w-3 h-3" />
                                      <span>Edit</span>
                                    </button>
                                    
                                    {/* Resend Contract Button */}
                                    <button
                                      onClick={() => handleResendContract(loan)}
                                      className="flex items-center gap-1 px-2 py-1.5 bg-blue-600/20 hover:bg-blue-600 rounded-lg text-blue-400 hover:text-white text-xs transition-colors"
                                      title="Resend Contract"
                                    >
                                      <FileText className="w-3 h-3" />
                                      <span>Resend Contract</span>
                                    </button>

                                    {/* Cancel Loan (Contract Error) — voids the promissory
                                        note and emails the borrower a "no payment due" notice. */}
                                    <button
                                      onClick={() => handleCancelLoanContractError(loan)}
                                      className="flex items-center gap-1 px-2 py-1.5 bg-rose-600/20 hover:bg-rose-600 rounded-lg text-rose-300 hover:text-white text-xs transition-colors"
                                      title="Cancel this loan due to a contract error and notify the borrower that no payment is owed"
                                      data-testid={`cancel-loan-contract-error-${loan.loan_number}`}
                                    >
                                      <AlertTriangle className="w-3 h-3" />
                                      <span>Cancel (No Payment)</span>
                                    </button>
                                    
                                    {/* View Payment History */}
                                    {loan.payments && loan.payments.length > 0 && (
                                      <button
                                        onClick={() => {
                                          setSelectedLoan(loan);
                                          setShowPaymentHistory(true);
                                        }}
                                        className="flex items-center gap-1 px-2 py-1.5 bg-zinc-700/50 hover:bg-zinc-600 rounded-lg text-zinc-300 text-xs transition-colors"
                                        title="View Payment History"
                                      >
                                        <Activity className="w-3 h-3" />
                                        <span>{loan.payments.length} payment{loan.payments.length > 1 ? 's' : ''}</span>
                                      </button>
                                    )}
                                    
                                    {/* Archive Loan */}
                                    <button
                                      onClick={() => handleCloseLoan(loan.loan_number)}
                                      className="p-1.5 bg-amber-600/20 hover:bg-amber-600 rounded-lg text-amber-400 hover:text-white transition-colors"
                                      title="Archive Loan"
                                    >
                                      <Archive className="w-3 h-3" />
                                    </button>
                                    
                                    {/* Delete Loan */}
                                    <button
                                      onClick={() => handleDeleteLoanPermanently(loan.loan_number)}
                                      className="p-1.5 bg-red-600/20 hover:bg-red-600 rounded-lg text-red-400 hover:text-white transition-colors"
                                      title="Delete Permanently"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            </div>
                            {/* End Mobile Card View */}
                          </div>
                          );
                        })}
                        </div>
                        </>)}
                      </>
                    )}
                  </div>

                  {/* PAID OFF LOANS Section */}
                  {ledgerData.closed_loans && ledgerData.closed_loans.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowClosedLoans(!showClosedLoans)}
                        className="text-sm font-semibold text-emerald-400 mb-3 flex items-center gap-2 hover:text-emerald-300 transition-colors"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Paid Off Loans ({ledgerData.closed_loans.length})
                        <ChevronDown className={`w-4 h-4 transition-transform ${showClosedLoans ? 'rotate-180' : ''}`} />
                      </button>
                      {showClosedLoans && (
                        <div className="space-y-3">
                          {ledgerData.closed_loans.map((loan) => (
                            <div
                              key={loan.loan_number}
                              className="bg-gradient-to-r from-emerald-900/20 to-zinc-900 border border-emerald-500/30 rounded-xl p-4"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 flex-wrap mb-2">
                                    <span className="px-3 py-1 rounded-lg text-sm font-bold bg-emerald-600/20 text-emerald-400 border border-emerald-500/30">
                                      #{loan.loan_number}
                                    </span>
                                    <span className="font-semibold text-white">{loan.borrower_name}</span>
                                    <span className="px-2 py-1 rounded-full text-xs bg-emerald-600 text-white font-bold">
                                      PAID IN FULL
                                    </span>
                                  </div>
                                  
                                  {/* Loan Details Grid */}
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                                    <div>
                                      <span className="text-zinc-500">Amount Lent:</span>
                                      <span className="text-white ml-1 font-medium">${loan.amount_lent?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div>
                                      <span className="text-zinc-500">Total Paid:</span>
                                      <span className="text-emerald-400 ml-1 font-medium">${loan.amount_return?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div>
                                      <span className="text-zinc-500">Profit:</span>
                                      <span className="text-green-400 ml-1 font-medium">${loan.profit?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div>
                                      <span className="text-zinc-500">Date Lent:</span>
                                      <span className="text-zinc-300 ml-1">{formatDateShort(loan.date_lent || loan.created_at)}</span>
                                    </div>
                                  </div>
                                  
                                  {/* Payment Info Row */}
                                  <div className="flex flex-wrap items-center gap-4 text-sm bg-emerald-500/10 rounded-lg px-3 py-2">
                                    <div className="flex items-center gap-1">
                                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                                      <span className="text-emerald-400 font-medium">
                                        Paid Off: {loan.paid_date || loan.closed_date || loan.updated_at?.split('T')[0] || 'N/A'}
                                      </span>
                                    </div>
                                    {loan.borrower_email && (
                                      <div className="text-zinc-400">
                                        <Mail className="w-3 h-3 inline mr-1" />
                                        {loan.borrower_email}
                                      </div>
                                    )}
                                    {loan.borrower_phone && (
                                      <div className="text-zinc-400">
                                        {loan.borrower_phone}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="flex flex-col items-center gap-2 ml-4">
                                  {/* Send Receipt Button */}
                                  <button
                                    onClick={() => handleSendPaidConfirmationEmail(loan)}
                                    disabled={isSendingConfirmation || !loan.borrower_email}
                                    className="flex items-center gap-1 px-3 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-white text-xs font-medium transition-colors"
                                    title={loan.borrower_email ? "Send Paid Receipt to Borrower" : "No email on file"}
                                  >
                                    <Mail className="w-4 h-4" />
                                    <span>Send Receipt</span>
                                  </button>
                                  
                                  {/* Reopen Button */}
                                  <button
                                    onClick={() => handleReopenLoan(loan.loan_number)}
                                    className="p-2 bg-amber-600/20 hover:bg-amber-600 rounded-lg text-amber-400 hover:text-white transition-colors"
                                    title="Reopen Loan"
                                  >
                                    <ArrowLeft className="w-4 h-4" />
                                  </button>
                                  
                                  {/* Delete Button */}
                                  <button
                                    onClick={() => handleDeleteLoanPermanently(loan.loan_number)}
                                    className="p-2 bg-red-600/20 hover:bg-red-600 rounded-lg text-red-400 hover:text-white transition-colors"
                                    title="Delete Permanently"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-zinc-400">
                  <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No loan data available</p>
                </div>
              )}

              {/* Add Loan Modal */}
              {showAddLoanModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
                  <div className="bg-zinc-900 rounded-xl w-full max-w-lg border border-zinc-700 flex flex-col" style={{ maxHeight: 'calc(100dvh - 1rem)' }}>
                    <div className="flex items-center justify-between p-4 sm:p-6 border-b border-zinc-700 flex-shrink-0">
                      <h3 className="text-lg font-bold text-white">Add Manual Loan Entry</h3>
                      <button onClick={() => setShowAddLoanModal(false)} className="text-zinc-400 hover:text-white w-10 h-10 flex items-center justify-center" aria-label="Close">
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="space-y-4 p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1">Borrower Name *</label>
                        <input
                          type="text"
                          value={manualLoanForm.borrowerName}
                          onChange={(e) => setManualLoanForm({...manualLoanForm, borrowerName: e.target.value})}
                          placeholder="Norma Ramirez"
                          className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1">Borrower Email</label>
                        <input
                          type="email"
                          value={manualLoanForm.borrowerEmail}
                          onChange={(e) => setManualLoanForm({...manualLoanForm, borrowerEmail: e.target.value})}
                          placeholder="email@example.com"
                          className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1">Amount Lent *</label>
                          <input
                            type="number"
                            value={manualLoanForm.amountLent}
                            onChange={(e) => setManualLoanForm({...manualLoanForm, amountLent: e.target.value})}
                            placeholder="3000"
                            className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1">Payback Amount *</label>
                          <input
                            type="number"
                            value={manualLoanForm.amountReturn}
                            onChange={(e) => setManualLoanForm({...manualLoanForm, amountReturn: e.target.value})}
                            placeholder="4000"
                            className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1">Date Lent</label>
                          <input
                            type="date"
                            value={manualLoanForm.dateLent}
                            onChange={(e) => setManualLoanForm({...manualLoanForm, dateLent: e.target.value})}
                            className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1">Due Date</label>
                          <input
                            type="date"
                            value={manualLoanForm.dateReturn}
                            onChange={(e) => setManualLoanForm({...manualLoanForm, dateReturn: e.target.value})}
                            className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1">Notes</label>
                        <textarea
                          value={manualLoanForm.notes}
                          onChange={(e) => setManualLoanForm({...manualLoanForm, notes: e.target.value})}
                          placeholder="Optional notes about this loan..."
                          rows={2}
                          className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-zinc-700 flex-shrink-0">
                      <button
                        onClick={() => setShowAddLoanModal(false)}
                        className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddManualLoan}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white transition-colors"
                      >
                        Add Loan
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Promissory Notes Log */}
          {activeTab === 'promissory-logs' && (
            <div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-400" />
                Promissory Notes Log
              </h3>
              {loadingContracts ? (
                <div className="text-center py-8 text-zinc-400">Loading...</div>
              ) : contracts.filter(c => c.contract_type === 'promissory_note').length === 0 ? (
                <div className="text-center py-8 text-zinc-400">No promissory notes yet</div>
              ) : (
                <div className="space-y-3">
                  {contracts.filter(c => c.contract_type === 'promissory_note')
                    .sort((a, b) => {
                      // Signed first (newest signed_at), then unsigned by created_at desc
                      const aSigned = !!a.signed_at;
                      const bSigned = !!b.signed_at;
                      if (aSigned !== bSigned) return aSigned ? -1 : 1;
                      const aDate = a.signed_at || a.created_at || '';
                      const bDate = b.signed_at || b.created_at || '';
                      return bDate.localeCompare(aDate);
                    })
                    .map((contract) => {
                    const amount = contract?.metadata?.amount_return ?? contract?.metadata?.amount_lent;
                    const fmtAmount = amount != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(amount) || 0) : null;
                    const isSigned = contract.status === 'signed' && contract.signed_at;
                    return (
                    <div
                      key={contract.id}
                      className={`bg-zinc-800 border rounded-lg p-4 transition-colors ${isSigned ? 'border-green-500/40 hover:border-green-500/60' : 'border-purple-500/30 hover:border-purple-500/50'}`}
                      data-testid={`promissory-row-${contract.id}`}
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-white">{contract.client_name || contract.client_email}</span>
                            {fmtAmount && (
                              <span className="text-pink-400 font-bold text-base" data-testid={`promissory-amount-${contract.id}`}>{fmtAmount}</span>
                            )}
                            {contract.loan_number && (
                              <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30">
                                Loan #{contract.loan_number}
                              </span>
                            )}
                            {getStatusBadge(contract.status)}
                          </div>
                          <p className="text-sm text-zinc-400 mt-1">{contract.client_email}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 flex-wrap">
                            <span>Created: {formatDate(contract.created_at)}</span>
                            {isSigned && (
                              <span className="text-green-400">Signed: {formatDateShort(contract.signed_at)}</span>
                            )}
                            {contract?.metadata?.date_return && (
                              <span>Due: {contract.metadata.date_return}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isSigned && (
                            <a
                              href={`${API}/api/contracts/${contract.id}/pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-green-600 hover:bg-green-700 rounded-lg text-white transition-colors flex items-center gap-1 text-xs font-medium"
                              title="Download signed PDF"
                              data-testid={`download-signed-${contract.id}`}
                            >
                              <Download className="w-4 h-4" /> Signed PDF
                            </a>
                          )}
                          <button
                            onClick={() => viewContractDetail(contract.id)}
                            className="p-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-colors"
                            title="View"
                            data-testid={`view-contract-${contract.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleMoveToTrash(contract.id)}
                            className="p-2 bg-red-600/20 hover:bg-red-600 rounded-lg text-red-400 hover:text-white transition-colors"
                            title="Move to Trash"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Credit Repair Contracts Log */}
          {activeTab === 'credit-repair-logs' && (
            <div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-400" />
                Credit Repair Contracts Log
              </h3>
              {loadingContracts ? (
                <div className="text-center py-8 text-zinc-400">Loading...</div>
              ) : contracts.filter(c => c.contract_type === 'credit_repair').length === 0 ? (
                <div className="text-center py-8 text-zinc-400">No credit repair contracts yet</div>
              ) : (
                <div className="space-y-3">
                  {contracts.filter(c => c.contract_type === 'credit_repair').map((contract) => (
                    <div
                      key={contract.id}
                      className="bg-zinc-800 border border-blue-500/30 rounded-lg p-4 hover:border-blue-500/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-semibold text-white">{contract.client_name || contract.client_email}</span>
                            {getStatusBadge(contract.status)}
                          </div>
                          <p className="text-sm text-zinc-400 mt-1">{contract.client_email}</p>
                          <p className="text-xs text-zinc-500 mt-1">
                            Created: {formatDate(contract.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => viewContractDetail(contract.id)}
                            className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleMoveToTrash(contract.id)}
                            className="p-2 bg-red-600/20 hover:bg-red-600 rounded-lg text-red-400 hover:text-white transition-colors"
                            title="Move to Trash"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quick Reminder */}
          {activeTab === 'quick-reminder' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-orange-400" />
                Quick Payment Reminder
              </h3>

              {/* ─── Payment-Options Blast (Feb 2026) ────────────────────
                  One-tap email to EVERYONE with a balance showing their
                  balance + Zelle/PayPal/Cash App/Venmo/Apple Pay options.
                  Admin gets ONE consolidated summary at the end. */}
              <div
                className="rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-900/50 via-teal-900/40 to-cyan-900/40 p-5 shadow-xl shadow-emerald-500/10"
                data-testid="payment-options-blast-card"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/40 shrink-0">
                    <span className="text-3xl">💰</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-black text-lg leading-tight">Blast Payment Options to Everyone With a Balance</h4>
                    <p className="text-emerald-100/80 text-sm mt-1 leading-relaxed">
                      One email per client showing their <b className="text-white">balance</b> + <b className="text-white">Zelle / PayPal / Cash&nbsp;App / Venmo / Apple&nbsp;Pay</b>.
                      Cash&nbsp;App &amp; PayPal auto-show the <b className="text-amber-200">+$3 per $100</b> fee, plus a screenshot reminder &amp; a "reply with a date" CTA. You'll get <b className="text-white">ONE</b> summary email at the end.
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 text-xs font-bold">
                        {quickReminders.filter(r => (parseFloat(r.balance) || 0) > 0 && r.client_email).length} clients with a balance
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-zinc-300 text-xs">
                        Free: Zelle · Venmo · Apple Pay
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/15 border border-red-400/40 text-red-200 text-xs">
                        +$3/$100: Cash App · PayPal
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleSendPaymentOptionsBlast}
                    disabled={sendingPaymentBlast || quickReminders.filter(r => (parseFloat(r.balance) || 0) > 0 && r.client_email).length === 0}
                    className="w-full sm:w-auto shrink-0 px-5 py-3 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 hover:brightness-110 disabled:from-zinc-700 disabled:to-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed text-white text-sm font-black shadow-lg shadow-emerald-500/30 transition flex items-center justify-center gap-2"
                    data-testid="payment-options-blast-btn"
                  >
                    {sendingPaymentBlast ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Queuing…
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Blast Now
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Send New Reminder Form */}
              <div className="bg-zinc-800 border border-orange-500/30 rounded-xl p-6" id="quick-reminder-form-section">
                <h4 className="text-white font-semibold mb-4">Send Payment Reminder</h4>
                
                <div className="space-y-4">
                  {/* Paste Client Info */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Paste Client Info</label>
                    <textarea
                      value={quickReminderForm.pastedInfo || ''}
                      onChange={(e) => {
                        const text = e.target.value;
                        setQuickReminderForm(prev => ({ ...prev, pastedInfo: text }));
                        
                        // Auto-extract name, email, and phone
                        const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
                        let extractedName = '';
                        let extractedEmail = '';
                        let extractedPhone = '';
                        let emailLineIndex = -1;
                        
                        // Look for email pattern and find its line index
                        for (let i = 0; i < lines.length; i++) {
                          const emailMatch = lines[i].match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
                          if (emailMatch) {
                            extractedEmail = emailMatch[0];
                            emailLineIndex = i;
                            break;
                          }
                        }
                        
                        // Look for phone number - prioritize the line RIGHT BEFORE the email
                        // Phone patterns: 10 digits with various formatting
                        const isPhoneNumber = (str) => {
                          const digits = str.replace(/[^\d]/g, '');
                          // Must be exactly 10 digits (or 11 with leading 1)
                          return digits.length === 10 || (digits.length === 11 && digits[0] === '1');
                        };
                        
                        const formatPhone = (str) => {
                          let digits = str.replace(/[^\d]/g, '');
                          if (digits.length === 11 && digits[0] === '1') {
                            digits = digits.slice(1);
                          }
                          if (digits.length === 10) {
                            return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
                          }
                          return str;
                        };
                        
                        // First priority: Check line directly before email
                        if (emailLineIndex > 0) {
                          const lineBeforeEmail = lines[emailLineIndex - 1];
                          if (isPhoneNumber(lineBeforeEmail)) {
                            extractedPhone = formatPhone(lineBeforeEmail);
                          }
                        }
                        
                        // Second priority: Look for a line that's ONLY a phone number (no other text)
                        if (!extractedPhone) {
                          for (const line of lines) {
                            // Skip lines with @ (email) or lines that look like addresses/SSN
                            if (line.includes('@')) continue;
                            const cleanLine = line.replace(/[^\d]/g, '');
                            // Line should be primarily digits (phone number standalone)
                            if (cleanLine.length >= 10 && cleanLine.length <= 11) {
                              // Make sure it's not an SSN (9 digits) or date
                              if (isPhoneNumber(line)) {
                                extractedPhone = formatPhone(line);
                                break;
                              }
                            }
                          }
                        }
                        
                        // Look for name - first line that's 2-4 words of letters only
                        for (const line of lines) {
                          if (line.includes('@')) continue;
                          if (/\d{5,}/.test(line)) continue; // Skip lines with long numbers
                          if (line.toLowerCase().includes('dob') || line.toLowerCase().includes('ssn')) continue;
                          
                          const words = line.split(/\s+/);
                          if (words.length >= 2 && words.length <= 5) {
                            const looksLikeName = words.every(w => /^[a-zA-Z'-]+$/.test(w));
                            if (looksLikeName) {
                              extractedName = line;
                              break;
                            }
                          }
                        }
                        
                        if (extractedName || extractedEmail || extractedPhone) {
                          setQuickReminderForm(prev => ({
                            ...prev,
                            clientName: extractedName || prev.clientName,
                            clientEmail: extractedEmail || prev.clientEmail,
                            clientPhone: extractedPhone || prev.clientPhone
                          }));
                        }
                      }}
                      placeholder="Paste client info here...&#10;Example:&#10;John Smith&#10;01-15-1985&#10;123 Main St&#10;5551234567&#10;john@email.com"
                      rows={4}
                      className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:border-orange-500 focus:outline-none text-sm"
                    />
                  </div>
                  
                  {/* Editable Extracted Info */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Name</label>
                      <input
                        type="text"
                        value={quickReminderForm.clientName}
                        onChange={(e) => setQuickReminderForm(prev => ({ ...prev, clientName: e.target.value }))}
                        placeholder="Client name"
                        className={`w-full px-3 py-2 bg-zinc-900 border rounded-lg text-sm focus:outline-none focus:border-orange-500 ${
                          quickReminderForm.clientName ? 'border-green-500/50 text-green-400' : 'border-zinc-700 text-zinc-400'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Email</label>
                      <input
                        type="email"
                        value={quickReminderForm.clientEmail}
                        onChange={(e) => setQuickReminderForm(prev => ({ ...prev, clientEmail: e.target.value }))}
                        placeholder="client@email.com"
                        className={`w-full px-3 py-2 bg-zinc-900 border rounded-lg text-sm focus:outline-none focus:border-orange-500 ${
                          quickReminderForm.clientEmail ? 'border-green-500/50 text-green-400' : 'border-zinc-700 text-zinc-400'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={quickReminderForm.clientPhone}
                        onChange={(e) => setQuickReminderForm(prev => ({ ...prev, clientPhone: e.target.value }))}
                        placeholder="(555) 123-4567"
                        className={`w-full px-3 py-2 bg-zinc-900 border rounded-lg text-sm focus:outline-none focus:border-orange-500 ${
                          quickReminderForm.clientPhone ? 'border-blue-500/50 text-blue-400' : 'border-zinc-700 text-zinc-400'
                        }`}
                      />
                    </div>
                  </div>
                  
                  {/* Balance Amount */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Balance Owed *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="text"
                        value={quickReminderForm.balance}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^\d.]/g, '');
                          setQuickReminderForm(prev => ({ ...prev, balance: val }));
                        }}
                        placeholder="0.00"
                        className="w-full pl-8 pr-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-xl font-bold focus:border-orange-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  {/* Payment Methods Preview */}
                  <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3">
                    <p className="text-xs text-zinc-400 mb-2 font-medium">Payment Methods Included in Message:</p>
                    <div className="text-xs text-zinc-300 grid grid-cols-2 gap-1">
                      <p className="text-green-400 col-span-2 font-medium">FREE: Zelle, Venmo, Apple Pay</p>
                      <p>• Zelle: srsworldLLC@gmail.com</p>
                      <p>• Apple Pay: 619-253-9785</p>
                      <p>• Venmo: Clearall-Creditrepairs-1</p>
                      <p className="text-amber-400">+$3/100: PayPal, Cash App</p>
                    </div>
                  </div>
                  
                  {/* Send Method Selection */}
                  <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                    <p className="text-sm text-zinc-300 mb-3 font-medium">How would you like to send the reminder?</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setQuickReminderSendMethod('email')}
                        disabled={!quickReminderForm.clientEmail}
                        className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                          quickReminderSendMethod === 'email'
                            ? 'bg-orange-600 text-white'
                            : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                        } ${!quickReminderForm.clientEmail ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Mail className="w-4 h-4" />
                        Email Only
                      </button>
                    </div>
                    {!quickReminderForm.clientEmail && (
                      <p className="text-xs text-amber-400 mt-2">Email address required</p>
                    )}
                  </div>
                  
                  {/* Send Button */}
                  <button
                    onClick={handleSendQuickReminder}
                    disabled={
                      sendingQuickReminder || 
                      !quickReminderForm.clientName || 
                      !quickReminderForm.balance ||
                      !quickReminderForm.clientEmail
                    }
                    className="w-full py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-700 disabled:cursor-not-allowed rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                  >
                    {sendingQuickReminder ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Sending Email...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Email & Save
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Quick Reminder Clients - Permanent Folder with Trash */}
              <div className="mt-6" id="quick-reminder-clients-section">
                {/* Sub-tabs for Clients vs Promise to Pay vs Trash */}
                <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-4 border-b border-zinc-700 pb-3">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setQuickReminderSubTab('clients'); fetchQuickReminders(); }}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 transition-colors ${
                      quickReminderSubTab === 'clients' 
                        ? 'bg-orange-600 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Quick Reminder</span> Clients
                    <span className="text-[10px] sm:text-xs bg-black/20 px-1.5 sm:px-2 py-0.5 rounded-full">
                      {quickReminders.length}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setQuickReminderSubTab('promise'); }}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 transition-colors ${
                      quickReminderSubTab === 'promise' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    <CalendarDays className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Promise to</span> Pay
                    <span className="text-[10px] sm:text-xs bg-black/20 px-1.5 sm:px-2 py-0.5 rounded-full">
                      {quickReminders.filter(r => r.promise_pay_date).length}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setQuickReminderSubTab('priority'); }}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 transition-colors ${
                      quickReminderSubTab === 'priority' 
                        ? 'bg-green-600 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
                    Priority
                    {quickReminders.filter(r => r.high_priority).length > 0 && (
                      <span className="text-[10px] sm:text-xs bg-black/20 px-1.5 sm:px-2 py-0.5 rounded-full">
                        {quickReminders.filter(r => r.high_priority).length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setQuickReminderSubTab('step3friday'); }}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 transition-colors ${
                      quickReminderSubTab === 'step3friday' 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Step 3</span> Fri
                    {quickReminders.filter(r => r.step3_friday_reminder).length > 0 && (
                      <span className="text-[10px] sm:text-xs bg-black/20 px-1.5 sm:px-2 py-0.5 rounded-full">
                        {quickReminders.filter(r => r.step3_friday_reminder).length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setQuickReminderSubTab('trash'); fetchQuickRemindersTrash(); }}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 transition-colors ${
                      quickReminderSubTab === 'trash' 
                        ? 'bg-red-600 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                    Trash
                    {quickRemindersTrash.length > 0 && (
                      <span className="text-[10px] sm:text-xs bg-black/20 px-1.5 sm:px-2 py-0.5 rounded-full">
                        {quickRemindersTrash.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* CLIENTS SUB-TAB */}
                {quickReminderSubTab === 'clients' && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-semibold flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-orange-400" />
                        Active Clients
                      </h4>
                      {quickReminders.length > 0 && (
                        <span className="text-lg font-bold text-red-400">
                          Total Owed: ${quickReminders.reduce((sum, r) => sum + parseFloat(r.balance || 0), 0).toFixed(2)}
                        </span>
                      )}
                    </div>
                
                {/* Bulk Actions Bar */}
                {quickReminders.length > 0 && (
                  <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedClients.length === quickReminders.length && quickReminders.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded border-zinc-600 bg-zinc-900 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-zinc-400">Select All</span>
                      </label>
                      {selectedClients.length > 0 && (
                        <span className="text-sm text-orange-400 font-medium">
                          {selectedClients.length} selected
                        </span>
                      )}
                    </div>
                    {selectedClients.length > 0 && (
                      <button
                        onClick={handleBulkSend}
                        disabled={sendingBulk}
                        className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-700 rounded-lg text-white font-medium flex items-center gap-2 transition-colors"
                      >
                        {sendingBulk ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send to {selectedClients.length} Client{selectedClients.length > 1 ? 's' : ''}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
                
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                  <p className="text-amber-400 text-xs flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Clients are saved permanently. Removing a client moves them to Trash for safekeeping.
                  </p>
                </div>
                
                {/* Search Bar */}
                {quickReminders.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {/* Search Input */}
                      <div className="relative flex-1 min-w-[150px]">
                        <input
                          type="text"
                          value={clientSearchQuery}
                          onChange={(e) => setClientSearchQuery(e.target.value)}
                          placeholder="Search..."
                          className="w-full pl-8 sm:pl-10 pr-8 sm:pr-10 py-1.5 sm:py-2 text-sm bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-orange-500 focus:outline-none"
                        />
                        <Search className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500 absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2" />
                        {clientSearchQuery && (
                          <button
                            onClick={() => setClientSearchQuery('')}
                            className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                          >
                            <X className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Sort Toggle */}
                      <div className="flex bg-zinc-900 border border-zinc-700 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setClientSortBy('newest'); }}
                          className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-1.5 transition-colors ${
                            clientSortBy === 'newest' 
                              ? 'bg-orange-600 text-white' 
                              : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                          }`}
                          title="Sort by newest first (most recently added)"
                        >
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="hidden xs:inline">Newest</span>
                          <span className="xs:hidden">New</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setClientSortBy('balance'); }}
                          className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-1.5 transition-colors ${
                            clientSortBy === 'balance' 
                              ? 'bg-orange-600 text-white' 
                              : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                          }`}
                          title="Sort by balance (high to low)"
                        >
                          <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="hidden xs:inline">Balance</span>
                          <span className="xs:hidden">$</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setClientSortBy('name'); }}
                          className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-1.5 transition-colors ${
                            clientSortBy === 'name' 
                              ? 'bg-orange-600 text-white' 
                              : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                          }`}
                          title="Sort alphabetically (A-Z)"
                        >
                          <User className="w-3 h-3 sm:w-4 sm:h-4" />
                          A-Z
                        </button>
                      </div>
                      
                      {/* Priority Filter */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setShowPriorityOnly(!showPriorityOnly); }}
                        data-testid="priority-filter-btn"
                        className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-1.5 transition-colors rounded-lg border ${
                          showPriorityOnly 
                            ? 'bg-green-600 border-green-500 text-white' 
                            : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800'
                        }`}
                        title={showPriorityOnly ? "Showing priority clients only - Click to show all" : "Click to show priority clients only"}
                      >
                        <DollarSign className={`w-3 h-3 sm:w-4 sm:h-4 ${showPriorityOnly ? 'animate-pulse' : ''}`} />
                        <span className="hidden sm:inline">Priority</span>
                        <span className="sm:hidden">Pri</span>
                        {quickReminders.filter(r => r.high_priority).length > 0 && (
                          <span className={`ml-0.5 sm:ml-1 px-1 sm:px-1.5 py-0.5 text-[10px] sm:text-xs rounded-full ${showPriorityOnly ? 'bg-green-700' : 'bg-green-600 text-white'}`}>
                            {quickReminders.filter(r => r.high_priority).length}
                          </span>
                        )}
                      </button>
                      
                      {/* View Toggle */}
                      <div className="flex bg-zinc-900 border border-zinc-700 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setClientViewMode('card'); }}
                          className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-1.5 transition-colors ${
                            clientViewMode === 'card' 
                              ? 'bg-blue-600 text-white' 
                              : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                          }`}
                          title="Card View"
                        >
                          <LayoutGrid className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setClientViewMode('list'); }}
                          className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-1.5 transition-colors ${
                            clientViewMode === 'list' 
                              ? 'bg-blue-600 text-white' 
                              : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                          }`}
                          title="List View (see more clients)"
                        >
                          <List className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </div>
                    {clientSearchQuery && (
                      <p className="text-xs text-zinc-500 mt-1">
                        Found {quickReminders.filter(r => 
                          r.client_name?.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
                          r.client_phone?.replace(/[^\d]/g, '').includes(clientSearchQuery.replace(/[^\d]/g, ''))
                        ).length} of {quickReminders.length} clients
                      </p>
                    )}
                  </div>
                )}
                
                {loadingReminders ? (
                  <div className="text-center py-8 text-zinc-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading clients...
                  </div>
                ) : quickReminders.length === 0 ? (
                  <div className="text-center py-8 bg-zinc-800/50 rounded-xl border border-zinc-700">
                    <DollarSign className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400">No clients added yet</p>
                    <p className="text-zinc-500 text-sm">Send a reminder above to add a client</p>
                  </div>
                ) : (
                  <>
                    {/* CARD VIEW */}
                    {clientViewMode === 'card' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 max-h-[500px] overflow-y-auto pr-1" key={`card-view-${clientSortBy}-${showPriorityOnly}`}>
                        {[...quickReminders]
                          .filter(reminder => {
                            // Priority filter first
                            if (showPriorityOnly && !reminder.high_priority) return false;
                            // Then search filter
                            if (!clientSearchQuery) return true;
                            const searchLower = clientSearchQuery.toLowerCase();
                            const searchDigits = clientSearchQuery.replace(/[^\d]/g, '');
                            const nameMatch = reminder.client_name?.toLowerCase().includes(searchLower);
                            const phoneMatch = searchDigits && reminder.client_phone?.replace(/[^\d]/g, '').includes(searchDigits);
                            return nameMatch || phoneMatch;
                          })
                          .sort((a, b) => {
                            // Priority clients always first
                            if (a.high_priority && !b.high_priority) return -1;
                            if (!a.high_priority && b.high_priority) return 1;
                            // Then by selected sort
                            if (clientSortBy === 'name') {
                              return (a.client_name || '').toLowerCase().localeCompare((b.client_name || '').toLowerCase());
                            } else if (clientSortBy === 'newest') {
                              // Use created_at if available, fall back to sent_at
                              const dateA = new Date(a.created_at || a.sent_at || 0);
                              const dateB = new Date(b.created_at || b.sent_at || 0);
                              return dateB - dateA; // Newest (most recent) first
                            } else {
                              // Balance - highest first
                              return (parseFloat(b.balance) || 0) - (parseFloat(a.balance) || 0);
                            }
                          })
                          .map((reminder) => (
                      <div
                        key={reminder.id}
                        className={`bg-zinc-800/80 border rounded-lg p-2.5 transition-colors relative ${
                          selectedClients.includes(reminder.id) 
                            ? 'border-orange-500 bg-orange-500/10' 
                            : reminder.high_priority
                              ? 'border-green-500/50 bg-green-500/5'
                              : 'border-zinc-700/50 hover:border-zinc-600'
                        }`}
                      >
                        {/* Flashing Green $ for High Priority */}
                        {reminder.high_priority && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-green-500/50">
                            <DollarSign className="w-3 h-3 text-white" />
                          </div>
                        )}
                        
                        {/* Header Row: Checkbox, Name, Balance */}
                        <div className="mb-1.5">
                          <div className="flex items-start gap-2 mb-1">
                            <input
                              type="checkbox"
                              checked={selectedClients.includes(reminder.id)}
                              onChange={() => handleSelectClient(reminder.id)}
                              className="w-3.5 h-3.5 mt-0.5 rounded border-zinc-600 bg-zinc-900 text-orange-500 focus:ring-orange-500 cursor-pointer flex-shrink-0"
                            />
                            <span className="font-semibold text-white text-sm leading-snug flex-1 break-words" data-testid={`client-name-${reminder.id}`}>
                              {reminder.client_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap pl-5.5">
                            {reminder.created_at && (
                              <span
                                className="text-[9px] text-zinc-500 bg-zinc-800/80 border border-zinc-700 rounded px-1.5 py-0.5 flex items-center gap-1"
                                title={`Added to Promise to Pay on ${new Date(reminder.created_at).toLocaleDateString()}`}
                                data-testid={`client-started-${reminder.id}`}
                              >
                                <Calendar className="w-2.5 h-2.5" />
                                Since {new Date(reminder.created_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}
                              </span>
                            )}
                            {editingBalance === reminder.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={editBalanceValue}
                                onChange={(e) => setEditBalanceValue(e.target.value.replace(/[^\d.]/g, ''))}
                                className="w-14 px-1.5 py-0.5 bg-zinc-900 border border-orange-500 rounded text-orange-400 font-bold text-xs focus:outline-none"
                                autoFocus
                              />
                              <button onClick={() => handleUpdateBalance(reminder.id)} className="p-0.5 bg-green-600 rounded text-white"><Check className="w-3 h-3" /></button>
                              <button onClick={() => { setEditingBalance(null); setEditBalanceValue(''); }} className="p-0.5 bg-zinc-600 rounded text-white"><X className="w-3 h-3" /></button>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => { setEditingBalance(reminder.id); setEditBalanceValue(reminder.balance); }}
                                className="text-xs font-bold text-red-400 hover:text-red-300 flex items-center"
                                data-testid={`balance-edit-${reminder.id}`}
                              >
                                ${parseFloat(reminder.balance).toFixed(0)}<Pen className="w-2 h-2 ml-0.5 opacity-50" />
                              </button>
                              {parseFloat(reminder.balance) > 0 && (
                                <button
                                  onClick={() => { setRecordingPayment(reminder.id); setPaymentAmount(''); }}
                                  className="text-[9px] font-bold uppercase tracking-wide bg-emerald-600 hover:bg-emerald-500 text-white rounded px-1.5 py-0.5 flex items-center gap-1 transition-colors"
                                  title="Record a payment toward this balance"
                                  data-testid={`payment-${reminder.id}`}
                                >
                                  <DollarSign className="w-2.5 h-2.5" />
                                  Payment
                                </button>
                              )}
                            </>
                          )}
                          </div>
                        </div>
                        
                        {/* Contact Info - Single Line + inline edit */}
                        {editingContact === reminder.id ? (
                          <div className="mb-1.5 flex flex-wrap items-center gap-1" data-testid={`edit-contact-row-${reminder.id}`}>
                            <input
                              type="email"
                              placeholder="Email"
                              value={editContactForm.email}
                              onChange={(e) => setEditContactForm({ ...editContactForm, email: e.target.value })}
                              className="flex-1 min-w-[120px] px-1.5 py-0.5 bg-zinc-900 border border-blue-500/60 rounded text-zinc-100 text-[10px] focus:outline-none focus:border-blue-400"
                              data-testid={`edit-contact-email-${reminder.id}`}
                            />
                            <input
                              type="tel"
                              placeholder="Phone"
                              value={editContactForm.phone}
                              onChange={(e) => setEditContactForm({ ...editContactForm, phone: e.target.value })}
                              className="w-28 px-1.5 py-0.5 bg-zinc-900 border border-blue-500/60 rounded text-zinc-100 text-[10px] focus:outline-none focus:border-blue-400"
                              data-testid={`edit-contact-phone-${reminder.id}`}
                            />
                            <button
                              onClick={() => handleSaveContact(reminder.id)}
                              disabled={savingContact}
                              className="p-0.5 bg-green-600 rounded text-white disabled:opacity-50"
                              title="Save contact info"
                              data-testid={`edit-contact-save-${reminder.id}`}
                            ><Check className="w-3 h-3" /></button>
                            <button
                              onClick={() => { setEditingContact(null); setEditContactForm({ email: '', phone: '' }); }}
                              className="p-0.5 bg-zinc-600 rounded text-white"
                              title="Cancel"
                              data-testid={`edit-contact-cancel-${reminder.id}`}
                            ><X className="w-3 h-3" /></button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-[10px] text-zinc-500 mb-1.5 truncate group">
                            {reminder.client_email && <span className="flex items-center gap-0.5 truncate"><Mail className="w-2.5 h-2.5" />{reminder.client_email}</span>}
                            {reminder.client_phone && <span className="flex items-center gap-0.5 text-blue-400"><Phone className="w-2.5 h-2.5" />{reminder.client_phone}</span>}
                            {!reminder.client_email && !reminder.client_phone && (
                              <span className="text-amber-400/80 italic">No contact info on file</span>
                            )}
                            <button
                              onClick={() => {
                                setEditingContact(reminder.id);
                                setEditContactForm({
                                  email: reminder.client_email || '',
                                  phone: reminder.client_phone || '',
                                });
                              }}
                              className="ml-auto text-zinc-500 hover:text-blue-400 transition-colors flex-shrink-0"
                              title="Edit contact info"
                              data-testid={`edit-contact-btn-${reminder.id}`}
                            >
                              <Pen className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        )}
                        
                        {/* Status Badges */}
                        {(reminder.promise_pay_date || reminder.auto_friday_reminder) && (
                          <div className="flex gap-1 mb-1.5">
                            {reminder.promise_pay_date && (
                              <span className="bg-purple-500/20 text-purple-400 px-1 py-0.5 rounded text-[9px] flex items-center gap-0.5">
                                <CalendarClock className="w-2 h-2" />
                                {new Date(reminder.promise_pay_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                            {reminder.auto_friday_reminder && (
                              <span className="bg-blue-500/20 text-blue-400 px-1 py-0.5 rounded text-[9px] flex items-center gap-0.5">
                                <BellRing className="w-2 h-2" /> Fri
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Actions Dropdown (replaces the bulky 7-button grid) */}
                        <div className="relative">
                          <button
                            type="button"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Defer the state flip past React's batched event
                              // handler so the outside-click useEffect can't
                              // race with us on mobile Safari (the documented
                              // failure mode the user reported 3 times).
                              const next = actionMenuOpen === reminder.id ? null : reminder.id;
                              window.requestAnimationFrame(() => setActionMenuOpen(next));
                            }}
                            className="w-full px-2 py-1.5 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 rounded text-white text-xs font-semibold flex items-center justify-between gap-2"
                            data-testid={`actions-toggle-${reminder.id}`}
                          >
                            <span className="flex items-center gap-1.5">
                              {reminder.high_priority && <span className="text-yellow-300">⭐</span>}
                              Actions
                            </span>
                            {actionMenuOpen === reminder.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          </button>
                          {actionMenuOpen === reminder.id && (
                            <div
                              className="absolute left-0 right-0 top-full mt-1 z-[60] bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl overflow-hidden"
                              data-testid={`actions-menu-${reminder.id}`}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {reminder.client_email && (
                                <button onClick={() => { handleResendReminder(reminder.id, 'email'); setActionMenuOpen(null); }} disabled={sendingEmailTo === reminder.id}
                                  className="w-full px-3 py-2 text-left text-xs text-white hover:bg-orange-600/20 flex items-center gap-2 border-b border-zinc-800">
                                  {sendingEmailTo === reminder.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-orange-400" /> : <Mail className="w-3.5 h-3.5 text-orange-400" />}
                                  Email Reminder
                                </button>
                              )}
                              <button onClick={() => { setRecordingPayment(reminder.id); setPaymentAmount(''); setActionMenuOpen(null); }}
                                className="w-full px-3 py-2 text-left text-xs text-white hover:bg-green-600/20 flex items-center gap-2 border-b border-zinc-800">
                                <DollarSign className="w-3.5 h-3.5 text-green-400" />
                                Record Payment
                              </button>
                              {parseFloat(reminder.balance) > 0 && (
                                <button onClick={() => { handleMarkClientPaidInFull(reminder); setActionMenuOpen(null); }}
                                  disabled={markingPaidReminderId === reminder.id}
                                  className="w-full px-3 py-2 text-left text-xs text-white hover:bg-emerald-600/20 flex items-center gap-2 border-b border-zinc-800"
                                  data-testid={`menu-paid-in-full-${reminder.id}`}
                                >
                                  {markingPaidReminderId === reminder.id
                                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-400" />
                                    : <Check className="w-3.5 h-3.5 text-emerald-400" />}
                                  Mark Paid in Full
                                </button>
                              )}
                              {reminder.client_email && (
                                <button onClick={() => { openContractForClient(reminder); setActionMenuOpen(null); }} disabled={sendingContractTo === reminder.id}
                                  className="w-full px-3 py-2 text-left text-xs text-white hover:bg-indigo-600/20 flex items-center gap-2 border-b border-zinc-800">
                                  {sendingContractTo === reminder.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-400" /> : <FileText className="w-3.5 h-3.5 text-indigo-400" />}
                                  Send Contract
                                </button>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); setSettingPromiseDate(reminder.id); setPromiseDateValue(''); setActionMenuOpen(null); }}
                                className="w-full px-3 py-2 text-left text-xs text-white hover:bg-purple-600/20 flex items-center gap-2 border-b border-zinc-800">
                                <CalendarClock className="w-3.5 h-3.5 text-purple-400" />
                                {reminder.promise_pay_date ? `Promise: ${new Date(reminder.promise_pay_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Set Promise Date'}
                              </button>
                              {/* Setup Email Payment Reminders — Off / Weekly / Biweekly / Monthly */}
                              {(() => {
                                const currentFreq = reminder.payment_reminder_frequency
                                  || (reminder.auto_friday_reminder ? 'weekly' : 'off');
                                const freqLabels = { off: 'Off', weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly' };
                                const isExpanded = reminderFrequencySubmenuOpen === reminder.id;
                                return (
                                  <div className="border-b border-zinc-800" data-testid={`reminder-frequency-section-${reminder.id}`}>
                                    <button
                                      onClick={() => setReminderFrequencySubmenuOpen(isExpanded ? null : reminder.id)}
                                      className="w-full px-3 py-2 text-left text-xs text-white hover:bg-blue-600/20 flex items-center gap-2"
                                      data-testid={`reminder-frequency-toggle-${reminder.id}`}
                                    >
                                      {togglingAutoFriday === reminder.id
                                        ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-400" />
                                        : (currentFreq !== 'off' ? <BellRing className="w-3.5 h-3.5 text-blue-400" /> : <Bell className="w-3.5 h-3.5 text-blue-400" />)}
                                      <span className="truncate">Setup Email Payment Reminders</span>
                                      <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-bold ${currentFreq !== 'off' ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300'}`}>
                                        {freqLabels[currentFreq] || 'Off'}
                                      </span>
                                      {isExpanded ? <ChevronUp className="w-3 h-3 text-zinc-400" /> : <ChevronDown className="w-3 h-3 text-zinc-400" />}
                                    </button>
                                    {isExpanded && (
                                      <div className="bg-zinc-950/60 px-2 py-1.5 space-y-0.5" data-testid={`reminder-frequency-options-${reminder.id}`}>
                                        {['off', 'weekly', 'biweekly', 'monthly'].map((freq) => (
                                          <button
                                            key={freq}
                                            onClick={() => { handleSetReminderFrequency(reminder.id, freq); setReminderFrequencySubmenuOpen(null); setActionMenuOpen(null); }}
                                            disabled={togglingAutoFriday === reminder.id}
                                            data-testid={`reminder-frequency-${freq}-${reminder.id}`}
                                            className={`w-full px-2 py-1.5 text-left text-[11px] rounded flex items-center justify-between ${
                                              currentFreq === freq
                                                ? 'bg-blue-600 text-white font-semibold'
                                                : 'text-zinc-300 hover:bg-blue-600/20'
                                            }`}
                                          >
                                            <span>{freqLabels[freq]}</span>
                                            {currentFreq === freq && <Check className="w-3 h-3" />}
                                          </button>
                                        ))}
                                        <p className="text-[9px] text-zinc-500 px-2 pt-1 pb-0.5 leading-tight">
                                          Weekly = every Fri · Biweekly = every other Fri · Monthly = 1st Fri of month
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              <button onClick={() => { handleTogglePriority(reminder.id, reminder.high_priority); setActionMenuOpen(null); }} disabled={togglingPriority === reminder.id}
                                className="w-full px-3 py-2 text-left text-xs text-white hover:bg-green-600/20 flex items-center gap-2 border-b border-zinc-800">
                                {togglingPriority === reminder.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-green-400" /> : <span className="text-yellow-300 text-sm">{reminder.high_priority ? '⭐' : '☆'}</span>}
                                {reminder.high_priority ? 'Remove VIP Priority' : 'Mark as VIP Priority'}
                              </button>
                              <button onClick={() => { handleDeleteReminder(reminder.id); setActionMenuOpen(null); }}
                                className="w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-red-600/20 flex items-center gap-2">
                                <Trash2 className="w-3.5 h-3.5" />
                                Remove Client
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Expandable Payment Form */}
                        {recordingPayment === reminder.id && (
                          <div className="mt-1.5 p-2 bg-green-500/10 border border-green-500/30 rounded">
                            <p className="text-[10px] text-green-400 mb-1.5 font-medium">Record Payment for {reminder.client_name}</p>
                            <div className="flex items-center gap-1 mb-2">
                              <span className="text-green-400 font-bold">$</span>
                              <input 
                                type="text" 
                                value={paymentAmount} 
                                onChange={(e) => setPaymentAmount(e.target.value.replace(/[^\d.]/g, ''))}
                                placeholder="0.00" 
                                className="flex-1 px-2 py-1.5 bg-zinc-900 border border-green-500/50 rounded text-green-400 font-bold text-sm focus:outline-none focus:border-green-400" 
                                autoFocus 
                              />
                              <button 
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleQuickReminderPayment(reminder.id); }} 
                                disabled={savingPayment || !paymentAmount}
                                className="p-1.5 bg-green-600 hover:bg-green-700 disabled:bg-zinc-700 rounded text-white"
                                title="Record Payment"
                              >
                                {savingPayment ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              </button>
                              <button 
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setRecordingPayment(null); setPaymentAmount(''); }} 
                                className="p-1.5 bg-red-600 hover:bg-red-700 rounded text-white"
                                title="Cancel"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            {reminder.client_email && (
                              <label className="flex items-center gap-2 text-[10px] text-zinc-400 cursor-pointer hover:text-white">
                                <input 
                                  type="checkbox" 
                                  checked={sendPaymentConfirmation}
                                  onChange={(e) => setSendPaymentConfirmation(e.target.checked)}
                                  className="w-3 h-3 rounded border-zinc-600 bg-zinc-800 text-green-500 focus:ring-green-500"
                                />
                                <Mail className="w-3 h-3" />
                                Send payment confirmation to {reminder.client_email}
                              </label>
                            )}
                          </div>
                        )}
                        
                        {/* Expandable Promise Date Form */}
                        {settingPromiseDate === reminder.id && (
                          <div className="mt-1.5 p-1.5 bg-purple-500/10 border border-purple-500/30 rounded">
                            <p className="text-[10px] text-purple-400 mb-1">Enter date (MM/DD/YYYY):</p>
                            <div className="flex items-center gap-1">
                              <input 
                                type="text" 
                                value={promiseDateValue} 
                                onChange={(e) => {
                                  // Auto-format as MM/DD/YYYY
                                  let val = e.target.value.replace(/\D/g, '');
                                  if (val.length > 2) val = val.slice(0, 2) + '/' + val.slice(2);
                                  if (val.length > 5) val = val.slice(0, 5) + '/' + val.slice(5, 9);
                                  setPromiseDateValue(val);
                                }}
                                placeholder="MM/DD/YYYY"
                                maxLength={10}
                                className="flex-1 px-2 py-1 bg-zinc-900 border border-purple-500/50 rounded text-purple-400 text-xs focus:outline-none placeholder-zinc-600"
                                data-testid="promise-date-input"
                              />
                              <button 
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleSetPromiseDate(reminder.id); }} 
                                disabled={savingPromiseDate || !promiseDateValue || promiseDateValue.length < 10}
                                className="p-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-700 rounded text-white"
                              >
                                {savingPromiseDate ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              </button>
                              <button 
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSettingPromiseDate(null); setPromiseDateValue(''); }} 
                                className="p-1.5 bg-red-600 hover:bg-red-700 rounded text-white"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                      </div>
                    )}
                    
                    {/* LIST VIEW - Compact view for bulk operations */}
                    {clientViewMode === 'list' && (
                      <div className="max-h-[600px] overflow-y-auto border border-zinc-700 rounded-lg">
                        {/* List Header */}
                        <div className="sticky top-0 bg-zinc-800 border-b border-zinc-700 px-3 py-2 flex items-center gap-3 text-xs font-medium text-zinc-400 z-10">
                          <div className="w-6">
                            <input
                              type="checkbox"
                              checked={selectedClients.length === quickReminders.length && quickReminders.length > 0}
                              onChange={handleSelectAll}
                              className="w-4 h-4 rounded border-zinc-600 bg-zinc-900 text-orange-500 focus:ring-orange-500 cursor-pointer"
                            />
                          </div>
                          <div className="w-5"></div>
                          <div className="flex-1 min-w-[150px]">NAME</div>
                          <div className="w-28 text-right">BALANCE</div>
                          <div className="w-40">EMAIL</div>
                          <div className="w-28">PHONE</div>
                          <div className="w-44 text-center">ACTIONS</div>
                        </div>
                        
                        {/* List Items */}
                        {[...quickReminders]
                          .filter(reminder => {
                            // Priority filter first
                            if (showPriorityOnly && !reminder.high_priority) return false;
                            // Then search filter
                            if (!clientSearchQuery) return true;
                            const searchLower = clientSearchQuery.toLowerCase();
                            const searchDigits = clientSearchQuery.replace(/[^\d]/g, '');
                            const nameMatch = reminder.client_name?.toLowerCase().includes(searchLower);
                            const phoneMatch = searchDigits && reminder.client_phone?.replace(/[^\d]/g, '').includes(searchDigits);
                            return nameMatch || phoneMatch;
                          })
                          .sort((a, b) => {
                            // Priority clients always first
                            if (a.high_priority && !b.high_priority) return -1;
                            if (!a.high_priority && b.high_priority) return 1;
                            // Then by selected sort
                            if (clientSortBy === 'name') {
                              return (a.client_name || '').toLowerCase().localeCompare((b.client_name || '').toLowerCase());
                            } else if (clientSortBy === 'newest') {
                              // Use created_at if available, fall back to sent_at
                              const dateA = new Date(a.created_at || a.sent_at || 0);
                              const dateB = new Date(b.created_at || b.sent_at || 0);
                              return dateB - dateA; // Newest (most recent) first
                            } else {
                              // Balance - highest first
                              return (parseFloat(b.balance) || 0) - (parseFloat(a.balance) || 0);
                            }
                          })
                          .map((reminder, index) => (
                            <div
                              key={`${reminder.id}-${clientSortBy}`}
                              className={`px-3 py-2 flex items-center gap-3 text-sm border-b border-zinc-700/50 hover:bg-zinc-800/50 transition-colors ${
                                selectedClients.includes(reminder.id) 
                                  ? 'bg-orange-500/10' 
                                  : reminder.high_priority
                                    ? 'bg-green-500/5'
                                    : index % 2 === 0 ? 'bg-zinc-900/30' : ''
                              }`}
                            >
                              {/* Checkbox */}
                              <div className="w-6">
                                <input
                                  type="checkbox"
                                  checked={selectedClients.includes(reminder.id)}
                                  onChange={() => handleSelectClient(reminder.id)}
                                  className="w-4 h-4 rounded border-zinc-600 bg-zinc-900 text-orange-500 focus:ring-orange-500 cursor-pointer"
                                />
                              </div>
                              
                              {/* Priority Indicator */}
                              <div className="w-5">
                                {reminder.high_priority && (
                                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-green-500/50">
                                    <DollarSign className="w-3 h-3 text-white" />
                                  </div>
                                )}
                              </div>
                              
                              {/* Name + Since badge */}
                              <div className="flex-1 min-w-[150px] flex items-center gap-2 font-medium text-white truncate">
                                <span className="truncate">{reminder.client_name}</span>
                                {reminder.created_at && (
                                  <span
                                    className="text-[9px] text-zinc-400 bg-zinc-800/80 border border-zinc-700 rounded px-1.5 py-0.5 flex items-center gap-1 flex-shrink-0"
                                    title={`Added to Promise to Pay on ${new Date(reminder.created_at).toLocaleDateString()}`}
                                  >
                                    <Calendar className="w-2.5 h-2.5" />
                                    Since {new Date(reminder.created_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}
                                  </span>
                                )}
                              </div>
                              
                              {/* Balance */}
                              <div className="w-28 text-right font-bold text-red-400">
                                ${parseFloat(reminder.balance).toFixed(2)}
                              </div>
                              
                              {/* Email */}
                              <div className="w-40 text-zinc-400 truncate text-xs">
                                {reminder.client_email || '-'}
                              </div>
                              
                              {/* Phone */}
                              <div className="w-28 text-blue-400 text-xs">
                                {reminder.client_phone || '-'}
                              </div>
                              
                              {/* Quick Actions */}
                              <div className="w-44 flex items-center justify-center gap-1">
                                {reminder.client_email && (
                                  <button
                                    onClick={() => handleResendReminder(reminder.id, 'email')}
                                    disabled={sendingEmailTo === reminder.id}
                                    className="p-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-700 rounded text-white transition-colors"
                                    title="Send Email"
                                  >
                                    {sendingEmailTo === reminder.id ? (
                                      <RefreshCw className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Mail className="w-3 h-3" />
                                    )}
                                  </button>
                                )}
                                <button
                                  onClick={() => { setRecordingPayment(reminder.id); setPaymentAmount(''); setClientViewMode('card'); }}
                                  className="p-1.5 bg-green-600 hover:bg-green-700 rounded text-white transition-colors"
                                  title="Record Payment"
                                >
                                  <DollarSign className="w-3 h-3" />
                                </button>
                                {reminder.client_email && (
                                  <button
                                    onClick={() => openContractForClient(reminder)}
                                    disabled={sendingContractTo === reminder.id}
                                    className="p-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:bg-zinc-700 rounded text-white transition-colors"
                                    title="Send Contract"
                                  >
                                    {sendingContractTo === reminder.id ? (
                                      <RefreshCw className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <FileText className="w-3 h-3" />
                                    )}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleTogglePriority(reminder.id, reminder.high_priority)}
                                  disabled={togglingPriority === reminder.id}
                                  className={`p-1.5 rounded transition-colors ${reminder.high_priority ? 'bg-green-600 text-white ring-2 ring-green-400' : 'bg-zinc-700 hover:bg-green-600 text-zinc-400 hover:text-white'}`}
                                  title={reminder.high_priority ? "High Priority ON" : "Set High Priority"}
                                >
                                  {togglingPriority === reminder.id ? (
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <DollarSign className={`w-3 h-3 ${reminder.high_priority ? 'animate-pulse' : ''}`} />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleDeleteReminder(reminder.id)}
                                  className="p-1.5 bg-zinc-700 hover:bg-red-600 rounded text-zinc-400 hover:text-white transition-colors"
                                  title="Move to Trash"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                          
                        {/* List Footer with count */}
                        <div className="sticky bottom-0 bg-zinc-800 border-t border-zinc-700 px-3 py-2 text-xs text-zinc-500">
                          Showing {quickReminders.filter(r => {
                            if (!clientSearchQuery) return true;
                            const searchLower = clientSearchQuery.toLowerCase();
                            const searchDigits = clientSearchQuery.replace(/[^\d]/g, '');
                            return r.client_name?.toLowerCase().includes(searchLower) || 
                              (searchDigits && r.client_phone?.replace(/[^\d]/g, '').includes(searchDigits));
                          }).length} of {quickReminders.length} clients
                          {selectedClients.length > 0 && (
                            <span className="ml-2 text-orange-400">• {selectedClients.length} selected</span>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
                  </>
                )}

                {/* PROMISE TO PAY SUB-TAB */}
                {quickReminderSubTab === 'promise' && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-semibold flex items-center gap-2">
                        <CalendarDays className="w-5 h-5 text-blue-400" />
                        Promise to Pay Dates
                        <span className="text-xs text-zinc-500">
                          ({quickReminders.filter(r => r.promise_pay_date).length} clients)
                        </span>
                      </h4>
                    </div>

                    {quickReminders.filter(r => r.promise_pay_date).length === 0 ? (
                      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-8 text-center">
                        <CalendarDays className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                        <p className="text-zinc-400">No clients with promise to pay dates</p>
                        <p className="text-zinc-500 text-sm mt-1">When you set a promise to pay date for a client, they'll appear here</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                        {[...quickReminders]
                          .filter(r => r.promise_pay_date)
                          .sort((a, b) => new Date(a.promise_pay_date) - new Date(b.promise_pay_date))
                          .map((reminder) => {
                            const promiseDate = new Date(reminder.promise_pay_date);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const isOverdue = promiseDate < today;
                            const isToday = promiseDate.toDateString() === today.toDateString();
                            const daysUntil = Math.ceil((promiseDate - today) / (1000 * 60 * 60 * 24));
                            
                            return (
                              <div
                                key={reminder.id}
                                className={`bg-zinc-800/80 border rounded-lg p-4 transition-colors ${
                                  isOverdue ? 'border-red-500/50 bg-red-500/10' : 
                                  isToday ? 'border-yellow-500/50 bg-yellow-500/10' : 
                                  'border-zinc-700'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${
                                      isOverdue ? 'bg-red-600' : 
                                      isToday ? 'bg-yellow-600' : 
                                      'bg-blue-600'
                                    }`}>
                                      <CalendarDays className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                      <p className="text-white font-medium">{reminder.client_name}</p>
                                      <p className="text-zinc-400 text-sm">{reminder.client_email}</p>
                                      {reminder.client_phone && (
                                        <p className="text-zinc-500 text-xs">{reminder.client_phone}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className={`font-bold text-lg ${
                                      isOverdue ? 'text-red-400' : 
                                      isToday ? 'text-yellow-400' : 
                                      'text-green-400'
                                    }`}>
                                      ${parseFloat(reminder.balance || 0).toFixed(2)}
                                    </p>
                                    <p className={`text-sm font-medium ${
                                      isOverdue ? 'text-red-400' : 
                                      isToday ? 'text-yellow-400' : 
                                      'text-blue-400'
                                    }`}>
                                      {promiseDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </p>
                                    <p className={`text-xs ${
                                      isOverdue ? 'text-red-500' : 
                                      isToday ? 'text-yellow-500' : 
                                      'text-zinc-500'
                                    }`}>
                                      {isOverdue ? `${Math.abs(daysUntil)} days overdue` : 
                                       isToday ? 'Due TODAY' : 
                                       `In ${daysUntil} days`}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-700">
                                  <button
                                    onClick={() => handleResend(reminder)}
                                    disabled={resendingReminder === reminder.id}
                                    className="flex-1 px-3 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-700 rounded-lg text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                  >
                                    {resendingReminder === reminder.id ? (
                                      <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Send className="w-4 h-4" />
                                    )}
                                    Send Reminder
                                  </button>
                                  <button
                                    onClick={() => {
                                      setPaymentClientId(reminder.id);
                                      setPaymentAmount(reminder.balance || '');
                                      setShowPaymentModal(true);
                                    }}
                                    className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                                  >
                                    <DollarSign className="w-4 h-4" />
                                    Record Payment
                                  </button>
                                  <button
                                    onClick={() => setPaymentPlanOpen(s => ({ ...s, [reminder.id]: !s[reminder.id] }))}
                                    className={`px-3 py-2 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2 ${paymentPlanOpen[reminder.id] ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                                    data-testid={`open-plan-${reminder.id}`}
                                  >
                                    <CalendarDays className="w-4 h-4" />
                                    Payment Plan
                                    {paymentPlanOpen[reminder.id] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                                {paymentPlanOpen[reminder.id] && (
                                  <div className="mt-3 pt-3 border-t border-zinc-700">
                                    <PromisedPaymentsPanel
                                      clientName={reminder.client_name}
                                      clientEmail={reminder.client_email}
                                      defaultAmount={reminder.balance}
                                      source="quick_reminders"
                                      sourceId={reminder.id}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </>
                )}

                {/* PRIORITY SUB-TAB */}
                {quickReminderSubTab === 'priority' && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-semibold flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-400 animate-pulse" />
                        Priority Clients
                        <span className="text-xs text-zinc-500">
                          ({quickReminders.filter(r => r.high_priority).length} clients)
                        </span>
                      </h4>
                      {quickReminders.filter(r => r.high_priority).length > 0 && (
                        <span className="text-lg font-bold text-green-400">
                          Total: ${quickReminders.filter(r => r.high_priority).reduce((sum, r) => sum + parseFloat(r.balance || 0), 0).toFixed(2)}
                        </span>
                      )}
                    </div>

                    {quickReminders.filter(r => r.high_priority).length === 0 ? (
                      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-8 text-center">
                        <DollarSign className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                        <p className="text-zinc-400">No priority clients</p>
                        <p className="text-zinc-500 text-sm mt-1">Mark clients as priority by clicking the VIP button on their card</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                        {[...quickReminders]
                          .filter(r => r.high_priority)
                          .sort((a, b) => (parseFloat(b.balance) || 0) - (parseFloat(a.balance) || 0))
                          .map((reminder) => (
                            <div
                              key={reminder.id}
                              className="bg-gradient-to-r from-green-500/10 to-zinc-800/80 border border-green-500/30 rounded-lg p-4 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 rounded-lg bg-green-600 animate-pulse">
                                    <DollarSign className="w-5 h-5 text-white" />
                                  </div>
                                  <div>
                                    <p className="text-white font-medium flex items-center gap-2">
                                      {reminder.client_name}
                                      <span className="text-yellow-400">⭐</span>
                                    </p>
                                    <p className="text-zinc-400 text-sm">{reminder.client_email}</p>
                                    {reminder.client_phone && (
                                      <p className="text-zinc-500 text-xs">{reminder.client_phone}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-2xl text-green-400">
                                    ${parseFloat(reminder.balance || 0).toFixed(2)}
                                  </p>
                                  {reminder.promise_pay_date && (
                                    <p className="text-purple-400 text-sm">
                                      Due: {new Date(reminder.promise_pay_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-green-500/30">
                                <button
                                  onClick={() => handleResend(reminder)}
                                  disabled={resendingReminder === reminder.id}
                                  className="flex-1 px-3 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-700 rounded-lg text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                  {resendingReminder === reminder.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4" />
                                  )}
                                  Send Reminder
                                </button>
                                <button
                                  onClick={() => {
                                    setPaymentClientId(reminder.id);
                                    setPaymentAmount(reminder.balance || '');
                                    setShowPaymentModal(true);
                                  }}
                                  className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                                >
                                  <DollarSign className="w-4 h-4" />
                                  Record Payment
                                </button>
                                <button
                                  onClick={() => handleTogglePriority(reminder.id, reminder.high_priority)}
                                  disabled={togglingPriority === reminder.id}
                                  className="px-3 py-2 bg-zinc-700 hover:bg-red-600 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                                  title="Remove from Priority"
                                >
                                  {togglingPriority === reminder.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <X className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </>
                )}

                {/* TRASH SUB-TAB */}
                {quickReminderSubTab === 'trash' && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-semibold flex items-center gap-2">
                        <Trash2 className="w-5 h-5 text-red-400" />
                        Trash
                        <span className="text-xs text-zinc-500">
                          ({quickRemindersTrash.length} clients)
                        </span>
                      </h4>
                      {quickRemindersTrash.length > 0 && (
                        <button
                          onClick={handleQuickReminderEmptyTrash}
                          disabled={emptyingTrash}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                        >
                          {emptyingTrash ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Emptying...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" />
                              Empty Trash
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                      <p className="text-red-400 text-xs flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Clients in trash can be restored. Empty trash to permanently delete.
                      </p>
                    </div>

                    {loadingTrash ? (
                      <div className="text-center py-8 text-zinc-400">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading trash...
                      </div>
                    ) : quickRemindersTrash.length === 0 ? (
                      <div className="text-center py-8 bg-zinc-800/50 rounded-xl border border-zinc-700">
                        <Trash2 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                        <p className="text-zinc-400">Trash is empty</p>
                        <p className="text-zinc-500 text-sm">Removed clients will appear here</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {quickRemindersTrash.map((reminder) => (
                          <div
                            key={reminder.id}
                            className="bg-zinc-800 border border-red-500/30 rounded-lg p-4 opacity-80 hover:opacity-100 transition-opacity"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <span className="font-semibold text-white text-lg">{reminder.client_name}</span>
                                  <span className="text-lg font-bold text-red-400/60 line-through">
                                    ${parseFloat(reminder.balance).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-zinc-400 mt-1">
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {reminder.client_email}
                                  </span>
                                  {reminder.client_phone && (
                                    <span className="flex items-center gap-1 text-blue-400/60">
                                      <Phone className="w-3 h-3" />
                                      {reminder.client_phone}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                                  <span>Deleted: {new Date(reminder.deleted_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                              
                              <div className="flex flex-col gap-2">
                                <button
                                  onClick={() => handleRestoreClient(reminder.id)}
                                  disabled={restoringClient === reminder.id}
                                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-zinc-700 rounded-lg text-white transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap"
                                  title="Restore to Quick Reminder Clients"
                                >
                                  {restoringClient === reminder.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <RotateCcw className="w-4 h-4" />
                                      Restore
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleQuickReminderPermanentDelete(reminder.id)}
                                  disabled={deletingPermanently === reminder.id}
                                  className="px-4 py-2 bg-zinc-700 hover:bg-red-700 disabled:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white transition-colors flex items-center gap-2 text-sm"
                                  title="Delete permanently"
                                >
                                  {deletingPermanently === reminder.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Trash2 className="w-4 h-4" />
                                      Delete Forever
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                
                {/* STEP 3 AUTO-EMAILS SUB-TAB (Simplified - All clients get emails) */}
                {quickReminderSubTab === 'step3friday' && (
                  <>
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="bg-gradient-to-r from-emerald-900/50 to-green-900/50 border border-emerald-500/30 rounded-xl p-6">
                        <h4 className="text-white font-bold text-xl flex items-center gap-2 mb-2">
                          <Phone className="w-6 h-6 text-emerald-400" />
                          Step 3 Auto-Emails
                        </h4>
                        <p className="text-emerald-300/70 text-sm">
                          <strong>All {quickReminders.length} active clients</strong> in this folder automatically receive Step 3 reminder emails. When a client is removed from this folder, they stop receiving emails.
                        </p>
                      </div>
                      
                      {/* Schedule Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Thursday Night Email */}
                        <div className="bg-gradient-to-br from-purple-900/40 to-violet-900/40 border border-purple-500/30 rounded-xl p-5">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                                <span className="text-xl">🌙</span>
                              </div>
                              <div>
                                <h5 className="text-white font-bold">Thursday Night</h5>
                                <p className="text-purple-300 text-xs">7:00 PM Pacific</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowThursdayPreview(true)}
                              className="px-3 py-1.5 bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/30 rounded-lg text-purple-300 text-xs font-medium flex items-center gap-1.5 transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              Preview
                            </button>
                          </div>
                          <p className="text-purple-200/70 text-sm mb-3">
                            "Heads up! Tomorrow is Step 3 day - get ready to call the bureaus!"
                          </p>
                          <button
                            onClick={handleSendThursdayNow}
                            disabled={sendingThursdayNow || quickReminders.length === 0}
                            className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-colors text-sm"
                          >
                            {sendingThursdayNow ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4" />
                                Send Thursday Email Now
                              </>
                            )}
                          </button>
                        </div>
                        
                        {/* Friday Morning Email */}
                        <div className="bg-gradient-to-br from-emerald-900/40 to-green-900/40 border border-emerald-500/30 rounded-xl p-5">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                                <span className="text-xl">📞</span>
                              </div>
                              <div>
                                <h5 className="text-white font-bold">Friday Morning</h5>
                                <p className="text-emerald-300 text-xs">9:00 AM Pacific</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowStep3Preview(true)}
                              className="px-3 py-1.5 bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-500/30 rounded-lg text-emerald-300 text-xs font-medium flex items-center gap-1.5 transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              Preview
                            </button>
                          </div>
                          <p className="text-emerald-200/70 text-sm mb-3">
                            "It's FRIDAY! Time to call and check your dispute status!"
                          </p>
                          <button
                            onClick={handleSendStep3Now}
                            disabled={sendingStep3Now || quickReminders.length === 0}
                            className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-colors text-sm"
                          >
                            {sendingStep3Now ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4" />
                                Send Friday Email Now
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {/* How It Works */}
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <h5 className="text-blue-400 font-bold mb-2 flex items-center gap-2">
                          <CalendarDays className="w-4 h-4" />
                          How It Works
                        </h5>
                        <ul className="text-blue-300/80 text-sm space-y-1">
                          <li>✓ <strong>Thursday 7 PM:</strong> Clients get a heads-up email to prepare for tomorrow</li>
                          <li>✓ <strong>Friday 9 AM:</strong> Clients get the reminder with bureau phone numbers</li>
                          <li>✓ <strong>Auto-stop:</strong> When you remove a client from this folder, emails stop automatically</li>
                        </ul>
                      </div>
                      
                      {/* Active Clients List */}
                      <div className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden">
                        <div className="bg-zinc-900 px-4 py-3 border-b border-zinc-700">
                          <h5 className="text-white font-semibold flex items-center gap-2">
                            <Users className="w-4 h-4 text-zinc-400" />
                            Active Clients Receiving Emails ({quickReminders.length})
                          </h5>
                        </div>
                        
                        {quickReminders.length === 0 ? (
                          <div className="text-center py-8">
                            <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                            <p className="text-zinc-400">No active clients</p>
                            <p className="text-zinc-500 text-sm">Add clients via the Quick Reminder Clients tab</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-zinc-700 max-h-[300px] overflow-y-auto">
                            {quickReminders.map((reminder) => (
                              <div key={reminder.id} className="px-4 py-3 flex items-center justify-between hover:bg-zinc-700/50 transition-colors">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-white">{reminder.client_name}</span>
                                    <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                                      <BellRing className="w-2.5 h-2.5" /> Auto
                                    </span>
                                  </div>
                                  <p className="text-sm text-zinc-500 truncate">{reminder.client_email}</p>
                                </div>
                                <span className="text-xs text-zinc-500">
                                  ${parseFloat(reminder.balance || 0).toFixed(0)} balance
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Trash */}
          {activeTab === 'trash' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-red-400" />
                  Trash
                </h3>
                {trashedContracts.length > 0 && (
                  <button
                    onClick={handleEmptyTrash}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-medium transition-colors"
                  >
                    Empty Trash
                  </button>
                )}
              </div>
              {trashedContracts.length === 0 ? (
                <div className="text-center py-8 text-zinc-400">Trash is empty</div>
              ) : (
                <div className="space-y-3">
                  {trashedContracts.map((contract) => (
                    <div
                      key={contract.id}
                      className="bg-zinc-800 border border-red-500/30 rounded-lg p-4 opacity-75 hover:opacity-100 transition-opacity"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-semibold text-white">{contract.client_name || contract.client_email}</span>
                            {getContractTypeBadge(contract.contract_type)}
                          </div>
                          <p className="text-sm text-zinc-400 mt-1">{contract.client_email}</p>
                          <p className="text-xs text-red-400 mt-1">
                            Trashed: {formatDateShort(contract.trashed_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleRestoreFromTrash(contract.id)}
                            className="p-2 bg-green-600/20 hover:bg-green-600 rounded-lg text-green-400 hover:text-white transition-colors"
                            title="Restore"
                          >
                            <ArrowLeft className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(contract.id)}
                            className="p-2 bg-red-600 hover:bg-red-700 rounded-lg text-white transition-colors"
                            title="Delete Forever"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Payment Recording Modal */}
      {showPaymentModal && selectedContract && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-zinc-900 rounded-xl shadow-2xl w-full max-w-md border border-zinc-700">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Record Payment</h3>
                    <p className="text-sm text-zinc-400">
                      {selectedContract.loan_number 
                        ? `Loan #${selectedContract.loan_number}` 
                        : selectedContract.contract_type === 'credit_repair' 
                          ? 'Credit Repair Contract' 
                          : 'Contract Payment'}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowPaymentModal(false)} className="text-zinc-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Payment Info */}
              <div className="bg-zinc-800 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">{selectedContract.loan_number ? 'Borrower:' : 'Client:'}</span>
                    <p className="text-white font-medium">{selectedContract.client_name}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">{selectedContract.loan_number ? 'Amount Due:' : 'Contract Total:'}</span>
                    <p className="text-white font-medium">
                      ${parseFloat(
                        selectedContract.metadata?.amount_return || 
                        selectedContract.metadata?.totalAmount ||
                        selectedContract.metadata?.down_payment ||
                        0
                      ).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                {selectedContract.total_paid > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-700">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-500">Already Paid:</span>
                      <span className="text-green-400 font-medium">${parseFloat(selectedContract.total_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Payment Amount *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                      placeholder="0.00"
                      className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Payment Method</label>
                  <select
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm({...paymentForm, paymentMethod: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                  >
                    <option value="">Select method...</option>
                    <option value="Cash App">Cash App</option>
                    <option value="Venmo">Venmo</option>
                    <option value="Apple Pay">Apple Pay</option>
                    <option value="Zelle">Zelle</option>
                    <option value="Bank Deposit">Bank Deposit</option>
                    <option value="PayPal">PayPal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Notes (Optional)</label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                    placeholder="Any additional notes about this payment..."
                    rows={2}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={selectedContract.loan_number ? handleRecordPayment : handleRecordContractPayment}
                  disabled={isRecordingPayment || !paymentForm.amount}
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {isRecordingPayment ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Recording...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Record Payment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Active Loans Modal (Feb 2026) */}
      {showEmailActiveLoansModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={() => setShowEmailActiveLoansModal(false)} data-testid="email-active-loans-modal">
          <div className="bg-zinc-900 border border-cyan-500/30 rounded-xl w-full max-w-lg max-h-[88vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-zinc-900 border-b border-zinc-700 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-cyan-400" />
                <div>
                  <h3 className="text-white font-semibold">Email Active Loans</h3>
                  <p className="text-zinc-400 text-xs">Send a client a consolidated summary of their outstanding loans</p>
                </div>
              </div>
              <button onClick={() => setShowEmailActiveLoansModal(false)} className="text-zinc-400 hover:text-white" data-testid="email-active-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Client picker */}
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-wide">Client</Label>
                {loadingActiveLoanClients ? (
                  <p className="text-zinc-500 text-sm italic mt-2">Loading clients with active loans...</p>
                ) : activeLoanClients.length === 0 ? (
                  <p className="text-amber-400 text-sm mt-2">No clients currently have active loans.</p>
                ) : (
                  <select
                    value={selectedClientEmail}
                    onChange={(e) => { setSelectedClientEmail(e.target.value); setSelectedLoanNumbers([]); }}
                    className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-md px-3 py-2 text-sm mt-1"
                    data-testid="email-active-client-picker"
                  >
                    <option value="">— Pick a client —</option>
                    {activeLoanClients.map(c => (
                      <option key={c.borrower_email} value={c.borrower_email}>
                        {c.borrower_name} — {c.borrower_email} ({c.active_loan_count} loan{c.active_loan_count === 1 ? '' : 's'})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Loan multi-select — appears after a client is picked */}
              {selectedClientEmail && (() => {
                const client = activeLoanClients.find(c => c.borrower_email === selectedClientEmail);
                if (!client) return null;
                const allSelected = selectedLoanNumbers.length === 0; // empty = all
                return (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-zinc-300 text-xs uppercase tracking-wide">Loans to include</Label>
                      <button
                        type="button"
                        onClick={() => setSelectedLoanNumbers([])}
                        className={`text-[11px] px-2 py-0.5 rounded ${allSelected ? 'bg-cyan-600 text-white' : 'bg-zinc-700 text-zinc-300'}`}
                      >
                        All ({client.active_loan_count})
                      </button>
                    </div>
                    <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                      {client.loans.map(loan => {
                        const isSel = allSelected || selectedLoanNumbers.includes(loan.loan_number);
                        const bal = Number(loan.remaining_balance ?? loan.amount_return ?? 0);
                        const dueIso = (loan.date_return || '').slice(0, 10);
                        const todayIso = new Date().toISOString().slice(0, 10);
                        const todayD = new Date(`${todayIso}T00:00:00`);
                        const dueD = dueIso ? new Date(`${dueIso}T00:00:00`) : null;
                        const penaltyEnabled = !!loan.penalty_enabled;
                        const penaltyPerDay = Number(loan.penalty_per_day ?? 200);
                        // 2026-02-08 — trust the backend's Pacific-time /
                        // 23:59-rollover math instead of recomputing here in
                        // UTC. Previously this line used a naive
                        // `Math.floor((todayD - dueD) / 86400000)` which
                        // disagreed with the loans homepage (backend uses
                        // America/Los_Angeles + end-of-day rollover). Now
                        // both places always show the same day count.
                        const daysLate = Number(loan.penalty_days || 0);
                        const currentPenalty = Number(loan.penalty_amount || 0);
                        // 2026-02-24 — `isPastDue` was referenced below but never
                        // defined, which threw ReferenceError the instant a client
                        // was picked and rendered the loan list → black screen.
                        // Compute it locally from daysLate + due-date math so the
                        // "N days late" badge only renders when appropriate.
                        const isPastDue = daysLate > 0 || (!!dueD && todayD > dueD);
                        const signed = !!loan.signed_at || ['signed', 'paid', 'paid_in_full'].includes(loan.status);
                        const promiseIso = emailActivePromiseDates[loan.loan_number] || '';
                        let projectedPenalty = 0;
                        let daysToPromise = 0;
                        if (penaltyEnabled && bal > 0 && promiseIso) {
                          try {
                            const promiseD = new Date(`${promiseIso}T00:00:00`);
                            const start = dueD && todayD < dueD ? dueD : todayD;
                            if (promiseD > start) {
                              daysToPromise = Math.floor((promiseD - start) / 86400000);
                              projectedPenalty = daysToPromise * penaltyPerDay;
                            }
                          } catch { /* ignore parse error */ }
                        }
                        const totalByPromise = bal + currentPenalty + projectedPenalty;
                        const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        return (
                          <div
                            key={loan.loan_number}
                            className={`bg-zinc-800/60 border rounded-lg p-2.5 transition-colors ${isSel ? 'border-cyan-500/60' : 'border-zinc-700'}`}
                            data-testid={`email-active-loan-${loan.loan_number}`}
                          >
                            {/* Row 1: checkbox + loan #/dates */}
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                className="accent-cyan-500 mt-1 flex-shrink-0"
                                checked={isSel}
                                onChange={(e) => {
                                  if (allSelected) {
                                    setSelectedLoanNumbers(client.loans.filter(l => l.loan_number !== loan.loan_number).map(l => l.loan_number));
                                  } else if (e.target.checked) {
                                    setSelectedLoanNumbers([...selectedLoanNumbers, loan.loan_number]);
                                  } else {
                                    setSelectedLoanNumbers(selectedLoanNumbers.filter(n => n !== loan.loan_number));
                                  }
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="text-white font-bold text-sm">#{loan.loan_number}</span>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${signed ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-red-500/20 text-red-300 border border-red-500/40'}`}>
                                    {signed ? 'Signed' : 'Unsigned'}
                                  </span>
                                  {penaltyEnabled && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                      ${penaltyPerDay.toFixed(0)}/day penalty
                                    </span>
                                  )}
                                  {isPastDue && bal > 0 && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase bg-red-600/40 text-red-100 border border-red-500/60">
                                      {daysLate} day{daysLate === 1 ? '' : 's'} late
                                    </span>
                                  )}
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-[11px]">
                                  <div>
                                    <p className="text-zinc-500 uppercase text-[9px] tracking-wide">Lent</p>
                                    <p className="text-zinc-200 font-semibold">{fmt(loan.amount_lent || 0)}</p>
                                    <p className="text-zinc-500 text-[9px]">{loan.date_lent || '—'}</p>
                                  </div>
                                  <div>
                                    <p className="text-zinc-500 uppercase text-[9px] tracking-wide">Return</p>
                                    <p className="text-zinc-200 font-semibold">{fmt(loan.amount_return || 0)}</p>
                                    <p className="text-red-300 text-[9px]">due {loan.date_return || '—'}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-zinc-500 uppercase text-[9px] tracking-wide">Balance</p>
                                    <p className="text-emerald-300 font-bold">{fmt(bal)}</p>
                                  </div>
                                </div>
                              </div>
                            </label>

                            {/* Penalty + Promise Date — only show when penalty is relevant */}
                            {isSel && penaltyEnabled && bal > 0 && (
                              <div className="mt-2 pt-2 border-t border-zinc-700/60">
                                {currentPenalty > 0 && (
                                  <div className="flex items-center justify-between text-[11px] mb-1.5 bg-red-950/40 border border-red-500/30 rounded px-2 py-1">
                                    <span className="text-red-300">Penalty already accrued · {daysLate} × ${penaltyPerDay.toFixed(0)}</span>
                                    <span className="text-red-200 font-bold">+{fmt(currentPenalty)}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-2 text-[11px]">
                                  <Calendar className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                                  <span className="text-zinc-400 flex-shrink-0">Promise pay date:</span>
                                  <input
                                    type="date"
                                    value={promiseIso}
                                    min={todayIso}
                                    onChange={(e) => setEmailActivePromiseDates(prev => ({ ...prev, [loan.loan_number]: e.target.value }))}
                                    className="flex-1 bg-zinc-900 border border-zinc-600 text-white rounded px-2 py-1 text-[11px]"
                                    data-testid={`email-active-promise-${loan.loan_number}`}
                                  />
                                  {promiseIso && (
                                    <button
                                      type="button"
                                      onClick={() => setEmailActivePromiseDates(prev => { const c = { ...prev }; delete c[loan.loan_number]; return c; })}
                                      className="text-zinc-500 hover:text-red-400 flex-shrink-0 w-6 h-6 flex items-center justify-center"
                                      aria-label="Clear promise date"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                                {promiseIso && (
                                  <div className="mt-1.5 bg-amber-950/40 border border-amber-500/30 rounded px-2 py-1 text-[11px]">
                                    <div className="flex items-center justify-between">
                                      <span className="text-amber-300">+{daysToPromise} more day{daysToPromise === 1 ? '' : 's'} × ${penaltyPerDay.toFixed(0)}</span>
                                      <span className="text-amber-200 font-bold">+{fmt(projectedPenalty)}</span>
                                    </div>
                                    <div className="flex items-center justify-between mt-1 pt-1 border-t border-amber-500/20">
                                      <span className="text-amber-100 font-semibold">Total owed by {promiseIso}</span>
                                      <span className="text-amber-100 font-extrabold text-sm">{fmt(totalByPromise)}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-wide">Optional note (shown above the loan list)</Label>
                <Textarea
                  value={emailActiveNote}
                  onChange={(e) => setEmailActiveNote(e.target.value)}
                  placeholder="e.g. Hey Norma, wanted to send a reminder of all your outstanding loans…"
                  className="bg-zinc-800 border-zinc-600 text-white text-sm mt-1 min-h-[80px]"
                  data-testid="email-active-note"
                />
              </div>

              <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-3">
                <p className="text-cyan-200 text-xs"><strong>Who receives it:</strong> the selected client + a copy to you (<span className="font-mono">clearallcreditrepair@gmail.com</span>).</p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={sendActiveLoansEmail}
                  disabled={!selectedClientEmail || sendingActiveLoansEmail}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="email-active-send"
                >
                  {sendingActiveLoansEmail ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Mail className="w-4 h-4 mr-2" /> Send Email</>}
                </Button>
                <Button variant="ghost" onClick={() => setShowEmailActiveLoansModal(false)} className="text-zinc-400">Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loan Payment Modal (from Loan Ledger) */}
      {showPaymentModal && selectedLoan && !selectedContract && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-zinc-900 rounded-xl shadow-2xl w-full max-w-md border border-zinc-700">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Record Payment</h3>
                    <p className="text-sm text-zinc-400">Loan #{selectedLoan.loan_number}</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedLoan(null);
                  }} 
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Loan Summary */}
              <div className="bg-zinc-800 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-semibold">{selectedLoan.borrower_name}</span>
                  <span className="px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-300">
                    #{selectedLoan.loan_number}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-zinc-500">Total Due:</span>
                    <p className="text-blue-400 font-medium">
                      ${parseFloat(selectedLoan.amount_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Already Paid:</span>
                    <p className="text-green-400 font-medium">
                      ${parseFloat(selectedLoan.total_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-700">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Remaining Balance:</span>
                    <span className={`font-bold ${selectedLoan.remaining_balance <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${parseFloat(selectedLoan.remaining_balance || selectedLoan.amount_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="w-full bg-zinc-700 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-green-500 transition-all"
                      style={{ width: `${Math.min(100, ((selectedLoan.total_paid || 0) / (selectedLoan.amount_return || 1)) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-1 text-right">
                    {Math.round(((selectedLoan.total_paid || 0) / (selectedLoan.amount_return || 1)) * 100)}% paid
                  </p>
                </div>
              </div>

              {/* Quick Pay Buttons */}
              <div className="mb-4">
                <label className="block text-sm text-zinc-400 mb-2">Quick Amount</label>
                <div className="grid grid-cols-4 gap-2">
                  {/* 2026-02-28 (user request) — quick amounts bumped to
                      $500 / $1000 / $1500 / $2000 to match the size of
                      real loan payments coming through this modal. */}
                  {[500, 1000, 1500, 2000].map((amount, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPaymentForm({...paymentForm, amount: String(amount)})}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        paymentForm.amount === String(amount)
                          ? 'bg-green-600 text-white'
                          : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                      }`}
                    >
                      {`$${amount}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Payment Amount *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                      placeholder="0.00"
                      className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Payment Method</label>
                  <select
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm({...paymentForm, paymentMethod: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                  >
                    <option value="">Select method...</option>
                    <option value="Cash">Cash</option>
                    <option value="Cash App">Cash App</option>
                    <option value="Venmo">Venmo</option>
                    <option value="Apple Pay">Apple Pay</option>
                    <option value="Zelle">Zelle</option>
                    <option value="Bank Deposit">Bank Deposit</option>
                    <option value="PayPal">PayPal</option>
                    <option value="Check">Check</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Notes (Optional)</label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                    placeholder="Any additional notes about this payment..."
                    rows={2}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedLoan(null);
                  }}
                  className="flex-1 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordLoanPayment}
                  disabled={isRecordingPayment || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0}
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {isRecordingPayment ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Recording...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Record Payment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showPaymentHistory && selectedLoan && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-zinc-900 rounded-xl shadow-2xl w-full max-w-lg border border-zinc-700 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-zinc-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Payment History</h3>
                    <p className="text-sm text-zinc-400">
                      Loan #{selectedLoan.loan_number} • {selectedLoan.borrower_name}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowPaymentHistory(false);
                    setSelectedLoan(null);
                  }} 
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="p-4 bg-zinc-800/50 border-b border-zinc-700">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-zinc-500">Total Due</p>
                  <p className="text-blue-400 font-bold">
                    ${parseFloat(selectedLoan.amount_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Paid</p>
                  <p className="text-green-400 font-bold">
                    ${parseFloat(selectedLoan.total_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Remaining</p>
                  <p className={`font-bold ${selectedLoan.remaining_balance <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${parseFloat(selectedLoan.remaining_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment List */}
            <div className="flex-1 overflow-y-auto p-4">
              {selectedLoan.payments && selectedLoan.payments.length > 0 ? (
                <div className="space-y-3">
                  {selectedLoan.payments.map((payment, idx) => (
                    <div key={idx} className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-semibold">
                            ${parseFloat(payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {(payment.timestamp || payment.payment_date) ? new Date(payment.timestamp || payment.payment_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            }) : 'Date not recorded'}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
                            {payment.payment_method || 'Not specified'}
                          </span>
                          {payment.confirmation_number && (
                            <p className="text-xs text-zinc-500 mt-1 font-mono">
                              #{payment.confirmation_number}
                            </p>
                          )}
                        </div>
                      </div>
                      {payment.notes && (
                        <p className="text-xs text-zinc-400 mt-2 italic">"{payment.notes}"</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400">No payments recorded yet</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-zinc-700">
              <button
                onClick={() => {
                  setShowPaymentHistory(false);
                  setShowPaymentModal(true);
                }}
                disabled={selectedLoan.remaining_balance <= 0}
                className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2"
              >
                <DollarSign className="w-4 h-4" />
                {selectedLoan.remaining_balance <= 0 ? 'Loan Paid in Full' : 'Record New Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loan Rollover Modal */}
      {/* Missed Payment Modal */}
      {showMissedPaymentModal && selectedLoan && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-zinc-900 rounded-xl shadow-2xl w-full max-w-md border border-zinc-700">
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Missed Payment</h3>
                    <p className="text-sm text-zinc-400">Loan #{selectedLoan.loan_number} - {selectedLoan.borrower_name}</p>
                  </div>
                </div>
                <button onClick={() => setShowMissedPaymentModal(false)} className="text-zinc-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Original Loan Info */}
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                <p className="text-red-400 text-sm font-medium mb-2">Payment Missed</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-zinc-500">Original Due:</span>
                    <span className="text-white ml-2">{selectedLoan.date_return || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Amount Owed:</span>
                    <span className="text-white ml-2">${(selectedLoan.remaining_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
              
              {/* New Terms */}
              <div className="space-y-4">
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-1">Interest / Late Fee Amount *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="number"
                      value={missedPaymentForm.interestAmount}
                      onChange={(e) => setMissedPaymentForm({ ...missedPaymentForm, interestAmount: e.target.value })}
                      placeholder="0.00"
                      className="w-full pl-9 pr-3 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-white placeholder-zinc-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-1">New Payment Due Date *</label>
                  <input
                    type="date"
                    value={missedPaymentForm.newDateReturn}
                    onChange={(e) => setMissedPaymentForm({ ...missedPaymentForm, newDateReturn: e.target.value })}
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-1">Notes (optional)</label>
                  <input
                    type="text"
                    value={missedPaymentForm.notes}
                    onChange={(e) => setMissedPaymentForm({ ...missedPaymentForm, notes: e.target.value })}
                    placeholder="e.g., Late fee applied, extended 2 weeks..."
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-white placeholder-zinc-500"
                  />
                </div>
                
                {/* New Total Preview */}
                <div className="bg-zinc-800 rounded-lg p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-400">Original Balance:</span>
                    <span className="text-white">${(selectedLoan.remaining_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-400">Interest / Late Fee:</span>
                    <span className="text-amber-400">+ ${(parseFloat(missedPaymentForm.interestAmount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold pt-2 border-t border-zinc-700">
                    <span className="text-white">New Total to Repay:</span>
                    <span className="text-blue-400">${((selectedLoan.remaining_balance || 0) + (parseFloat(missedPaymentForm.interestAmount) || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
                
                <p className="text-zinc-500 text-xs">
                  A new promissory note will be sent to <strong className="text-zinc-300">{selectedLoan.borrower_email || 'N/A'}</strong> for them to sign. It will state that the original payment was missed on <strong className="text-red-400">{selectedLoan.date_return || 'the original date'}</strong>.
                </p>
              </div>
              
              <div className="flex gap-3 mt-5">
                <button
                  onClick={handleMissedPayment}
                  disabled={isProcessingMissedPayment || !missedPaymentForm.newDateReturn}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-white font-semibold transition-colors"
                  data-testid="send-missed-payment-btn"
                >
                  {isProcessingMissedPayment ? (
                    <><RefreshCw className="w-4 h-4 animate-spin" /> Processing...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Send New Promissory Note</>
                  )}
                </button>
                <button
                  onClick={() => setShowMissedPaymentModal(false)}
                  className="px-4 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRolloverModal && selectedLoan && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-zinc-900 rounded-xl shadow-2xl w-full max-w-2xl border border-zinc-700 max-h-[95vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Rollover Loan</h3>
                    <p className="text-sm text-zinc-400">Set up a new loan from existing balance</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowRolloverModal(false);
                    setSelectedLoan(null);
                  }} 
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Step 1: Current Loan & Payment/Rollover Amount */}
              <div className="bg-zinc-800 rounded-lg p-4 mb-6 border border-purple-500/30">
                <h4 className="text-purple-400 font-medium mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-xs text-white">1</span>
                  Payment & Rollover Breakdown
                </h4>
                
                {/* Current Loan Info */}
                <div className="bg-zinc-900/50 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-white font-semibold">{selectedLoan.borrower_name}</span>
                      <span className="ml-2 px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-300">
                        #{selectedLoan.loan_number}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-zinc-500">Original Payback Amount</p>
                      <p className="text-blue-400 font-bold">
                        ${parseFloat(selectedLoan.amount_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-700">
                    <span className="text-zinc-400 text-sm">Already Paid:</span>
                    <span className="text-green-400 font-medium">
                      ${parseFloat(selectedLoan.total_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-zinc-400 text-sm">Current Balance Owed:</span>
                    <span className="text-red-400 font-bold">
                      ${parseFloat(selectedLoan.remaining_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                
                {/* Payment Before Rollover */}
                <div className="mb-4">
                  <label className="block text-sm text-zinc-400 mb-2">
                    Payment Received Before Rollover
                    <span className="text-zinc-500 text-xs ml-2">(Optional - if they paid some before rolling over)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 text-lg">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={rolloverForm.paymentBeforeRollover}
                      onChange={(e) => {
                        const payment = parseFloat(e.target.value || 0);
                        const newRollover = Math.max(0, (selectedLoan.remaining_balance || 0) - payment);
                        setRolloverForm({
                          ...rolloverForm, 
                          paymentBeforeRollover: e.target.value,
                          rolloverAmount: String(newRollover)
                        });
                      }}
                      max={selectedLoan.remaining_balance}
                      placeholder="0.00"
                      className="w-full bg-zinc-900 border border-green-500/50 rounded-lg pl-10 pr-4 py-3 text-white text-lg font-bold focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                    />
                  </div>
                  {parseFloat(rolloverForm.paymentBeforeRollover || 0) > 0 && (
                    <p className="text-xs text-green-400 mt-1">
                      This payment will be recorded on the original loan before rollover
                    </p>
                  )}
                </div>

                {/* Calculation Display */}
                <div className="bg-zinc-900/50 rounded-lg p-3 mb-4 border border-zinc-700">
                  <p className="text-xs text-zinc-500 mb-2">Calculation:</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Current Balance:</span>
                      <span className="text-white">${parseFloat(selectedLoan.remaining_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">- Payment Received:</span>
                      <span className="text-green-400">-${parseFloat(rolloverForm.paymentBeforeRollover || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-zinc-600">
                      <span className="text-zinc-300 font-medium">= Amount to Rollover:</span>
                      <span className="text-purple-400 font-bold">
                        ${((selectedLoan.remaining_balance || 0) - parseFloat(rolloverForm.paymentBeforeRollover || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Rollover Amount Input */}
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Amount to Roll Over *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 text-lg">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={rolloverForm.rolloverAmount}
                      onChange={(e) => setRolloverForm({...rolloverForm, rolloverAmount: e.target.value})}
                      max={(selectedLoan.remaining_balance || 0) - parseFloat(rolloverForm.paymentBeforeRollover || 0)}
                      className="w-full bg-zinc-900 border border-purple-500/50 rounded-lg pl-10 pr-4 py-3 text-white text-xl font-bold focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </div>
                  <div className="flex justify-between mt-2">
                    <button
                      onClick={() => {
                        const maxRollover = (selectedLoan.remaining_balance || 0) - parseFloat(rolloverForm.paymentBeforeRollover || 0);
                        setRolloverForm({...rolloverForm, rolloverAmount: String(Math.max(0, maxRollover))});
                      }}
                      className="text-xs text-purple-400 hover:text-purple-300"
                    >
                      Use Full Remaining (${((selectedLoan.remaining_balance || 0) - parseFloat(rolloverForm.paymentBeforeRollover || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })})
                    </button>
                  </div>
                </div>
              </div>

              {/* Step 2: New Loan Setup */}
              <div className="bg-zinc-800 rounded-lg p-4 mb-6 border border-zinc-700">
                <h4 className="text-blue-400 font-medium mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-xs text-white">2</span>
                  New Loan Details
                </h4>
                
                <div className="space-y-4">
                  {/* Borrower Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Borrower Name *</label>
                      <input
                        type="text"
                        value={rolloverForm.newBorrowerName}
                        onChange={(e) => setRolloverForm({...rolloverForm, newBorrowerName: e.target.value})}
                        className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Borrower Email *</label>
                      <input
                        type="email"
                        value={rolloverForm.newBorrowerEmail}
                        onChange={(e) => setRolloverForm({...rolloverForm, newBorrowerEmail: e.target.value})}
                        placeholder="Required for contract"
                        className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Borrower Phone (Optional)</label>
                    <input
                      type="tel"
                      value={rolloverForm.newBorrowerPhone}
                      onChange={(e) => setRolloverForm({...rolloverForm, newBorrowerPhone: e.target.value})}
                      placeholder="(555) 123-4567"
                      className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
                    />
                  </div>

                  {/* Amounts */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Additional Funds to Add</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={rolloverForm.additionalAmount}
                          onChange={(e) => setRolloverForm({...rolloverForm, additionalAmount: e.target.value})}
                          placeholder="0.00"
                          className="w-full bg-zinc-900 border border-zinc-600 rounded-lg pl-8 pr-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
                        />
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        New loan total: <span className="text-green-400 font-medium">
                          ${(parseFloat(rolloverForm.rolloverAmount || 0) + parseFloat(rolloverForm.additionalAmount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Payback Amount *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={rolloverForm.newAmountReturn}
                          onChange={(e) => setRolloverForm({...rolloverForm, newAmountReturn: e.target.value})}
                          placeholder="0.00"
                          className="w-full bg-zinc-900 border border-zinc-600 rounded-lg pl-8 pr-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
                        />
                      </div>
                      {rolloverForm.newAmountReturn && parseFloat(rolloverForm.rolloverAmount || 0) > 0 && (
                        <p className="text-xs text-zinc-500 mt-1">
                          Interest: <span className="text-amber-400 font-medium">
                            ${(parseFloat(rolloverForm.newAmountReturn || 0) - parseFloat(rolloverForm.rolloverAmount || 0) - parseFloat(rolloverForm.additionalAmount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Date of New Loan</label>
                      <input
                        type="date"
                        value={rolloverForm.newDateLent}
                        onChange={(e) => setRolloverForm({...rolloverForm, newDateLent: e.target.value})}
                        className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Due Date *</label>
                      <input
                        type="date"
                        value={rolloverForm.newDateReturn}
                        onChange={(e) => setRolloverForm({...rolloverForm, newDateReturn: e.target.value})}
                        className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Notes (will appear on contract)</label>
                    <textarea
                      value={rolloverForm.notes}
                      onChange={(e) => setRolloverForm({...rolloverForm, notes: e.target.value})}
                      placeholder="Reason for rollover, special terms, etc..."
                      rows={2}
                      className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Send Contract Option */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rolloverForm.sendContract}
                    onChange={(e) => setRolloverForm({...rolloverForm, sendContract: e.target.checked})}
                    className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-white font-medium">Send Contract to Borrower</p>
                    <p className="text-xs text-zinc-400">
                      Email the promissory note to {rolloverForm.newBorrowerEmail || 'borrower'} for signature
                    </p>
                  </div>
                </label>
              </div>

              {/* Summary */}
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <h4 className="text-purple-400 font-medium mb-3">Rollover Summary</h4>
                <div className="space-y-2 text-sm">
                  {/* Original Loan Section */}
                  <div className="pb-2 border-b border-purple-500/30">
                    <p className="text-xs text-zinc-500 mb-1">Original Loan #{selectedLoan.loan_number}:</p>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Balance Owed:</span>
                      <span className="text-white">${parseFloat(selectedLoan.remaining_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {parseFloat(rolloverForm.paymentBeforeRollover || 0) > 0 && (
                      <div className="flex justify-between text-green-400">
                        <span>- Payment Received:</span>
                        <span>-${parseFloat(rolloverForm.paymentBeforeRollover || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* New Loan Section */}
                  <div className="pt-1">
                    <p className="text-xs text-zinc-500 mb-1">New Loan:</p>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Rollover Amount:</span>
                      <span className="text-purple-400">${parseFloat(rolloverForm.rolloverAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {parseFloat(rolloverForm.additionalAmount || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-400">+ Additional Funds:</span>
                        <span className="text-green-400">+${parseFloat(rolloverForm.additionalAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-1 mt-1 border-t border-purple-500/20">
                      <span className="text-zinc-300">New Loan Principal:</span>
                      <span className="text-white font-bold">
                        ${(parseFloat(rolloverForm.rolloverAmount || 0) + parseFloat(rolloverForm.additionalAmount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Payback Amount:</span>
                      <span className="text-blue-400 font-bold">${parseFloat(rolloverForm.newAmountReturn || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Due Date:</span>
                      <span className="text-amber-400">{rolloverForm.newDateReturn || 'Not set'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowRolloverModal(false);
                    setSelectedLoan(null);
                  }}
                  className="flex-1 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRolloverLoan}
                  disabled={isProcessingRollover || !rolloverForm.newAmountReturn || !rolloverForm.newDateReturn || !rolloverForm.rolloverAmount || parseFloat(rolloverForm.rolloverAmount) <= 0}
                  className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {isProcessingRollover ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Creating New Loan...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Create Loan & Send Contract
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Confirmation Modal */}
      {showPaymentConfirm && paymentConfirmData && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-4">
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Confirm Payment
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Payment Summary */}
              <div className="bg-zinc-800 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Client:</span>
                  <span className="text-white font-medium">{paymentConfirmData.plan.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Payment Amount:</span>
                  <span className="text-green-400 font-bold text-lg">${paymentConfirmData.amount.toLocaleString()}</span>
                </div>
                <div className="border-t border-zinc-700 pt-3 flex justify-between">
                  <span className="text-zinc-400">Previous Balance:</span>
                  <span className="text-amber-400">${paymentConfirmData.plan.remaining_balance?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">New Balance:</span>
                  <span className={`font-bold ${paymentConfirmData.newRemaining === 0 ? 'text-green-400' : 'text-white'}`}>
                    ${paymentConfirmData.newRemaining.toLocaleString()}
                    {paymentConfirmData.newRemaining === 0 && ' (PAID IN FULL!)'}
                  </span>
                </div>
              </div>

              {/* Next Payment Date (Optional) */}
              {paymentConfirmData.newRemaining > 0 && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">
                    Next Payment Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={nextPaymentDate}
                    onChange={(e) => setNextPaymentDate(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white focus:border-green-500 outline-none"
                    placeholder="Set next payment date"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    If set, this will update the next payment reminder date
                  </p>
                </div>
              )}

              {/* Receipt Notice */}
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                <p className="text-sm text-blue-300 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  A payment receipt will be sent to {paymentConfirmData.plan.client_email}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowPaymentConfirm(null);
                    setPaymentConfirmData(null);
                    setNextPaymentDate('');
                  }}
                  className="flex-1 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleConfirmPayment(false)}
                  disabled={sendingReceipt}
                  className="flex-1 px-4 py-3 bg-zinc-600 hover:bg-zinc-500 rounded-lg text-white transition-colors"
                >
                  Log Only
                </button>
                <button
                  onClick={() => handleConfirmPayment(true)}
                  disabled={sendingReceipt}
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {sendingReceipt ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Log & Send Receipt
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Contract Modal - Send contracts from Quick Reminder */}
      {showQuickContractModal && contractModalClient && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            {/* Header */}
            <div className="p-6 border-b border-zinc-700 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Send Contract</h2>
                  <p className="text-sm text-zinc-400">Credit Repair Service Agreement</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowQuickContractModal(false);
                  setContractModalClient(null);
                }}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Client Info (Read-only) */}
              <div className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-500/30 rounded-xl p-4">
                <h3 className="text-indigo-300 font-semibold mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Sending Contract To
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Client Name</label>
                    <input
                      type="text"
                      value={quickContractForm.clientName}
                      onChange={(e) => setQuickContractForm(prev => ({...prev, clientName: e.target.value}))}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-indigo-500 focus:outline-none"
                      data-testid="quick-contract-client-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Client Email</label>
                    <input
                      type="email"
                      value={quickContractForm.clientEmail}
                      onChange={(e) => setQuickContractForm(prev => ({...prev, clientEmail: e.target.value}))}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-indigo-500 focus:outline-none"
                      data-testid="quick-contract-client-email"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="space-y-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  Payment Details
                </h3>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Down Payment *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={quickContractForm.downPayment}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          const remaining = parseInt(quickContractForm.remainingBalance) || 0;
                          const total = (parseInt(val) || 0) + remaining;
                          setQuickContractForm(prev => ({
                            ...prev, 
                            downPayment: val,
                            totalAmount: String(total)
                          }));
                        }}
                        placeholder="0"
                        className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-7 pr-3 py-2 text-white focus:border-green-500 focus:outline-none"
                        data-testid="quick-contract-down-payment"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Remaining Balance</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={quickContractForm.remainingBalance}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          const down = parseInt(quickContractForm.downPayment) || 0;
                          const total = down + (parseInt(val) || 0);
                          setQuickContractForm(prev => ({
                            ...prev, 
                            remainingBalance: val,
                            totalAmount: String(total)
                          }));
                        }}
                        placeholder="0"
                        className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-7 pr-3 py-2 text-white focus:border-green-500 focus:outline-none"
                        data-testid="quick-contract-remaining"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Total Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="text"
                        value={quickContractForm.totalAmount}
                        readOnly
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-lg pl-7 pr-3 py-2 text-green-400 font-bold cursor-not-allowed"
                        data-testid="quick-contract-total"
                      />
                    </div>
                  </div>
                </div>

                {parseInt(quickContractForm.remainingBalance) > 0 && (
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Balance Due Date (MM/DD/YYYY)</label>
                    <input
                      type="text"
                      value={quickContractForm.paymentDueDate}
                      onChange={(e) => {
                        let val = e.target.value.replace(/\D/g, '');
                        if (val.length > 2) val = val.slice(0, 2) + '/' + val.slice(2);
                        if (val.length > 5) val = val.slice(0, 5) + '/' + val.slice(5, 9);
                        setQuickContractForm(prev => ({...prev, paymentDueDate: val}));
                      }}
                      placeholder="MM/DD/YYYY"
                      maxLength={10}
                      className="w-full md:w-1/2 bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2 text-white focus:border-purple-500 focus:outline-none"
                      data-testid="quick-contract-due-date"
                    />
                  </div>
                )}
              </div>

              {/* Additional Terms */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Additional Terms (Optional)</label>
                <textarea
                  value={quickContractForm.additionalTerms}
                  onChange={(e) => setQuickContractForm(prev => ({...prev, additionalTerms: e.target.value}))}
                  placeholder="Any additional terms or conditions specific to this client..."
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-indigo-500 focus:outline-none"
                  data-testid="quick-contract-additional-terms"
                />
              </div>

              {/* Contract Preview Note */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h4 className="text-blue-300 font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  What the client will receive:
                </h4>
                <ol className="text-sm text-blue-200/80 space-y-1 list-decimal list-inside">
                  <li>Email with the Credit Repair Service Agreement</li>
                  <li>Link to review all terms and conditions</li>
                  <li>Option to sign with finger or typed signature</li>
                  <li>Both parties receive a signed copy via email</li>
                </ol>
              </div>

              {/* Warning Box */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h4 className="text-amber-300 font-medium mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Contract Includes Forfeiture Clause
                </h4>
                <p className="text-sm text-amber-200/80">
                  If the client fails to follow instructions, respond to communications, or comply with the credit repair process, 
                  their fees are non-refundable and forfeited.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-zinc-700 flex gap-3 sticky bottom-0 bg-zinc-900">
              <button
                onClick={() => {
                  setShowQuickContractModal(false);
                  setContractModalClient(null);
                }}
                className="flex-1 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendQuickContract}
                disabled={sendingContractTo || !quickContractForm.clientName || !quickContractForm.clientEmail || !quickContractForm.downPayment}
                className="flex-1 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-zinc-700 disabled:to-zinc-700 disabled:cursor-not-allowed rounded-lg text-white font-bold transition-all flex items-center justify-center gap-2"
                data-testid="send-quick-contract-btn"
              >
                {sendingContractTo ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Contract for Signature
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SIGNING LINK MODAL - Shows after sending promissory note */}

      {/* Edit Loan Modal */}

      {/* Paid in Full → "Also delete client?" confirmation modal */}

      {/* Balance-update → "Email client?" prompt (per user request 2026-03-04) */}
      {balanceUpdateEmailPrompt && (() => {
        const { reminder, oldBalance, newBalance } = balanceUpdateEmailPrompt;
        const delta = oldBalance - newBalance;
        const direction = delta > 0 ? 'decreased' : delta < 0 ? 'increased' : 'unchanged';
        const accentColor = delta > 0 ? 'emerald' : delta < 0 ? 'amber' : 'sky';
        const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        return (
          <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[210] p-3" data-testid="balance-update-email-modal">
            <div className={`bg-zinc-900 border border-${accentColor}-500/50 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden`}>
              <div className={`bg-gradient-to-r from-${accentColor}-600 to-${accentColor}-500 px-5 py-4 flex items-center gap-3`}>
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-white font-bold text-base">Balance {direction}</h3>
                  <p className={`text-${accentColor}-100 text-xs truncate`}>{reminder.client_name}</p>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-3 text-sm">
                  <div className="flex justify-between mb-1">
                    <span className="text-zinc-400">Previous</span>
                    <span className="text-zinc-200 font-semibold">{fmt(oldBalance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">New</span>
                    <span className={`text-${accentColor}-400 font-bold text-base`}>{fmt(newBalance)}</span>
                  </div>
                </div>
                <p className="text-zinc-200 text-sm">
                  Email the updated balance to <span className="text-cyan-300 break-all">{reminder.client_email}</span>?
                </p>
                <p className="text-zinc-500 text-[11px] italic">
                  {delta > 0 ? "Includes a friendly \"Payment Received\" banner with the amount applied." : delta < 0 ? "Notifies the client an additional amount was added to the balance." : "Sends a quick \"current balance\" summary."}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 p-4 border-t border-zinc-700 bg-zinc-900/60">
                <button
                  onClick={() => setBalanceUpdateEmailPrompt(null)}
                  disabled={sendingBalanceUpdateEmail}
                  className="px-4 py-2.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                  data-testid="balance-update-email-skip"
                >
                  No, just save
                </button>
                <button
                  onClick={handleConfirmSendBalanceUpdateEmail}
                  disabled={sendingBalanceUpdateEmail}
                  className={`px-4 py-2.5 bg-${accentColor}-600 hover:bg-${accentColor}-500 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50`}
                  data-testid="balance-update-email-send"
                >
                  {sendingBalanceUpdateEmail ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Yes, email it
                </button>
              </div>
            </div>
          </div>
        );
      })()}


      {paidInFullConfirmDelete && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[210] p-3" data-testid="paid-in-full-delete-modal">
          <div className="bg-zinc-900 border border-emerald-500/50 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-white font-bold text-base">Marked Paid in Full</h3>
                <p className="text-emerald-100 text-xs truncate">{paidInFullConfirmDelete.client_name}</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-zinc-200 text-sm">
                The balance is now <span className="text-emerald-400 font-bold">$0.00</span>
                {paidInFullConfirmDelete.client_email ? (
                  <> and a paid-in-full receipt was emailed to <span className="text-cyan-300 break-all">{paidInFullConfirmDelete.client_email}</span>.</>
                ) : (
                  <> (no email on file — receipt not sent).</>
                )}
              </p>
              <p className="text-zinc-300 text-sm">
                Would you like to <span className="font-semibold text-red-300">remove this client</span> from the active list now? They'll be moved to Trash and can be restored anytime.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 p-4 border-t border-zinc-700 bg-zinc-900/60">
              <button
                onClick={() => setPaidInFullConfirmDelete(null)}
                className="px-4 py-2.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white text-sm font-semibold"
                data-testid="paid-in-full-keep"
              >
                Keep on List
              </button>
              <button
                onClick={confirmDeletePaidInFullClient}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-500 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-1.5"
                data-testid="paid-in-full-delete"
              >
                <Trash2 className="w-4 h-4" />
                Move to Trash
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditLoanModal && editLoanForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[200] p-2 sm:p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg flex flex-col shadow-2xl overflow-hidden" style={{ maxHeight: 'calc(100dvh - 1rem)' }}>
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <Edit2 className="w-5 h-5 text-white flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-white truncate">Edit Loan #{editLoanForm.loan_number}</h3>
                  <p className="text-cyan-100 text-xs">Update due date, amounts, or borrower info</p>
                </div>
              </div>
              <button onClick={() => { setShowEditLoanModal(false); setEditLoanForm(null); }} className="w-10 h-10 flex items-center justify-center hover:bg-white/20 rounded-full flex-shrink-0" data-testid="edit-loan-close" aria-label="Close">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-3 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Due Date</label>
                  <input type="date" value={editLoanForm.date_return} onChange={(e) => setEditLoanForm({...editLoanForm, date_return: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm" data-testid="edit-loan-due-date" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Date Lent</label>
                  <input type="date" value={editLoanForm.date_lent} onChange={(e) => setEditLoanForm({...editLoanForm, date_lent: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm" />
                </div>
              </div>
              {/* Expected Payment Date — populated when negotiating a new pay
                  date with a borrower in penalty. The loan will be sorted by
                  this date if set; penalty math keeps using the ORIGINAL due
                  date so the running tally is preserved. Per user 2026-02-24. */}
              <div className="bg-cyan-950/30 border border-cyan-700/40 rounded-lg p-3">
                <label className="text-cyan-300 text-xs mb-1 block font-semibold uppercase tracking-wide">
                  Expected Payment Date (for borrowers in penalty)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={editLoanForm.expected_payment_date || ''}
                    onChange={(e) => setEditLoanForm({...editLoanForm, expected_payment_date: e.target.value})}
                    className="flex-1 px-3 py-2 bg-zinc-900 border border-cyan-700/60 rounded-lg text-white text-sm"
                    data-testid="edit-loan-expected-date"
                  />
                  {editLoanForm.expected_payment_date && (
                    <button
                      type="button"
                      onClick={() => setEditLoanForm({...editLoanForm, expected_payment_date: ''})}
                      className="text-cyan-300 hover:text-white text-xs px-2 py-1 rounded border border-cyan-700/60 hover:bg-cyan-900/40"
                      data-testid="edit-loan-expected-clear"
                    >Clear</button>
                  )}
                </div>
                <p className="text-[10px] text-cyan-400/80 mt-1.5 leading-snug">When set, the loan will sort by THIS date instead of the original due date — penalty keeps accruing from the original due date.</p>
                {/* Optional "reason for shift" input — only shown if the
                    expected_payment_date differs from what's on file. The
                    backend stamps it into the loan's payment_shifts history. */}
                {(editLoanForm.expected_payment_date || '') !== (editLoanForm._original_expected_payment_date || '') && (
                  <div className="mt-2">
                    <label className="text-cyan-300 text-[10px] mb-1 block uppercase tracking-wide">Reason for shift <span className="text-cyan-400/60 normal-case">(optional)</span></label>
                    <input
                      type="text"
                      value={editLoanForm.shift_reason || ''}
                      onChange={(e) => setEditLoanForm({...editLoanForm, shift_reason: e.target.value})}
                      placeholder="e.g. Borrower requested · Partial payment received · Broken promise"
                      className="w-full px-3 py-2 bg-zinc-900 border border-cyan-700/60 rounded-lg text-white text-xs"
                      data-testid="edit-loan-shift-reason"
                    />
                    <p className="text-[10px] text-cyan-400/70 mt-1">A history entry will be logged on the loan card.</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Amount Lent</label>
                  <input type="number" step="0.01" value={editLoanForm.amount_lent} onChange={(e) => setEditLoanForm({...editLoanForm, amount_lent: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Amount Return</label>
                  <input type="number" step="0.01" value={editLoanForm.amount_return} onChange={(e) => setEditLoanForm({...editLoanForm, amount_return: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm" data-testid="edit-loan-amount-return" />
                </div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Borrower Name</label>
                <input value={editLoanForm.borrower_name} onChange={(e) => setEditLoanForm({...editLoanForm, borrower_name: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Borrower Email</label>
                  <input type="email" value={editLoanForm.borrower_email} onChange={(e) => setEditLoanForm({...editLoanForm, borrower_email: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Borrower Phone</label>
                  <input value={editLoanForm.borrower_phone} onChange={(e) => setEditLoanForm({...editLoanForm, borrower_phone: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm" />
                </div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Notes</label>
                <textarea rows="2" value={editLoanForm.notes} onChange={(e) => setEditLoanForm({...editLoanForm, notes: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-zinc-400 text-xs flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-amber-400" />
                    Additional Terms <span className="text-zinc-600">(late fees, penalties, special clauses)</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setEditLoanForm({...editLoanForm, additional_terms: appendLatePenalty(editLoanForm.additional_terms)})}
                    className="px-2 py-1 rounded-md text-[11px] font-semibold bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 transition-colors flex items-center gap-1"
                    data-testid="edit-loan-add-late-penalty"
                    title="Append the $200/day late payment penalty clause"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    Add $200/day Late Penalty
                  </button>
                </div>
                <textarea rows="4" value={editLoanForm.additional_terms}
                  onChange={(e) => setEditLoanForm({...editLoanForm, additional_terms: e.target.value})}
                  placeholder="e.g. Late payment penalty of $50 per day after due date"
                  data-testid="edit-loan-additional-terms"
                  className="w-full px-3 py-2 bg-zinc-800 border border-amber-700/40 rounded-lg text-white text-sm" />
              </div>

              {/* ── Split Payments (Feb 2026) ─────────────────────────────
                  Some loans are repaid on multiple dates (half on Aug 15,
                  half on Sep 1).  Each split gets its own day-before
                  reminder emailed to both the borrower and you. */}
              <SplitPaymentsSection
                splits={editLoanForm.split_payments || []}
                amountReturn={parseFloat(editLoanForm.amount_return) || 0}
                defaultDueDate={editLoanForm.date_return}
                onChange={(next) => setEditLoanForm({ ...editLoanForm, split_payments: next })}
                loanNumber={editLoanForm.loan_number}
                onToggleServerSide={async (splitId, targetPaid) => {
                  // Best-effort persist the paid toggle so the server-side
                  // reminder job de-dupes correctly.  Save Changes still
                  // does a full sync at close.
                  try {
                    await axios.post(`${API}/api/loans/${editLoanForm.loan_number}/split-payments/${splitId}/toggle-paid?paid=${targetPaid}`);
                  } catch (_) { /* silent — full save syncs anyway */ }
                }}
              />
            </div>
            {/* Sticky footer — Save & Cancel always visible even with mobile keyboard open */}
            <div className="flex gap-2 p-4 sm:p-5 border-t border-zinc-700 bg-zinc-900 flex-shrink-0">
              <button onClick={handleSaveLoanEdit} disabled={savingEditLoan}
                className="flex-1 px-4 py-3 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-2"
                data-testid="edit-loan-save">
                {savingEditLoan ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Save Changes
              </button>
              <button onClick={() => { setShowEditLoanModal(false); setEditLoanForm(null); }}
                className="px-4 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {showSigningLinkModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg mx-4 overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Promissory Note Sent!</h3>
                    <p className="text-green-100 text-sm">Loan #{currentLoanInfo.loanNumber}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSigningLinkModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-zinc-300 text-sm">
                Email sent to <strong className="text-white">{currentLoanInfo.borrowerName}</strong>. 
                A copy was also sent to you.
              </p>

              {/* Signing Link Box */}
              <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-4 space-y-3">
                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide">
                  Signing Link (Click to Copy)
                </label>
                <div 
                  onClick={copySigningLinkToClipboard}
                  className="bg-zinc-900 border border-zinc-600 rounded-lg p-3 cursor-pointer hover:border-green-500 hover:bg-zinc-800 transition-all group"
                >
                  <p className="text-green-400 text-sm break-all font-mono group-hover:text-green-300">
                    {currentSigningLink}
                  </p>
                </div>
                <button
                  onClick={copySigningLinkToClipboard}
                  className="w-full py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 rounded-lg text-white font-bold transition-all flex items-center justify-center gap-2"
                  data-testid="copy-signing-link-btn"
                >
                  <Copy className="w-5 h-5" />
                  Copy Link to Clipboard
                </button>
              </div>

              <p className="text-xs text-zinc-500 text-center">
                Share this link with the client if they didn't receive the email
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6">
              <button
                onClick={() => setShowSigningLinkModal(false)}
                className="w-full py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Step 3 Friday Email Preview Modal */}
      {showStep3Preview && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Step 3 Friday Email Preview</h3>
                    <p className="text-emerald-100 text-sm">Sent every Friday at 9:30 AM Pacific</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowStep3Preview(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Email Content Preview */}
            <div className="flex-1 overflow-y-auto p-6 bg-zinc-800">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-lg mx-auto">
                {/* Email Header */}
                <div style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', padding: '25px', textAlign: 'center' }}>
                  <h2 style={{ color: 'white', margin: 0, fontSize: '22px', fontWeight: 'bold' }}>📞 Friday Dispute Check Reminder</h2>
                  <p style={{ color: '#a7f3d0', marginTop: '10px', fontSize: '14px' }}>Step 3: Call & Check Your Dispute Status</p>
                </div>
                
                {/* Email Body */}
                <div style={{ padding: '20px 25px', fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#333' }}>
                  <p style={{ margin: '0' }}>Hi <strong>[Client Name]</strong>,</p>
                  <p style={{ marginTop: '15px', color: '#555' }}>It's <strong style={{ color: '#059669' }}>FRIDAY</strong> — time to call each credit bureau and check the status of your disputes!</p>
                </div>
                
                {/* Main Card */}
                <div style={{ margin: '0 20px 20px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <div style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', padding: '15px 20px', color: 'white' }}>
                    <span style={{ background: 'rgba(255,255,255,0.25)', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', marginRight: '10px' }}>STEP 3</span>
                    <span style={{ fontSize: '20px', marginRight: '8px' }}>📞</span>
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>CALL EVERY FRIDAY</span>
                  </div>
                  <div style={{ padding: '20px', fontSize: '14px', color: '#444' }}>
                    <p style={{ margin: '0 0 15px' }}><strong>Call each bureau to check your dispute status.</strong> Always mention <strong style={{ color: '#dc2626' }}>IDENTITY THEFT</strong> as the reason!</p>
                    
                    {/* TransUnion */}
                    <div style={{ background: '#ecfdf5', padding: '15px', borderRadius: '8px', marginBottom: '12px', borderLeft: '4px solid #10b981' }}>
                      <p style={{ margin: '0 0 8px' }}><strong style={{ color: '#059669' }}>📞 TRANSUNION: 1-800-680-7289</strong></p>
                      <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>Press 1 for English, then say "disputes"</p>
                    </div>
                    
                    {/* Equifax */}
                    <div style={{ background: '#fef2f2', padding: '15px', borderRadius: '8px', marginBottom: '12px', borderLeft: '4px solid #ef4444' }}>
                      <p style={{ margin: '0 0 8px' }}><strong style={{ color: '#dc2626' }}>📞 EQUIFAX: 1-866-349-5191</strong></p>
                      <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>Press 4, then 0# until connected</p>
                    </div>
                    
                    {/* Experian */}
                    <div style={{ background: '#eff6ff', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                      <p style={{ margin: '0 0 8px' }}><strong style={{ color: '#2563eb' }}>📞 EXPERIAN: 1-800-493-1058</strong></p>
                      <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>Follow prompts and inform them about your dispute</p>
                    </div>
                    
                    {/* Important Note */}
                    <div style={{ background: '#dc2626', color: 'white', padding: '15px', borderRadius: '8px', marginTop: '15px', textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: '14px' }}><strong>🚨 IMPORTANT!</strong></p>
                      <p style={{ margin: '8px 0 0', fontSize: '13px' }}>Tell them: &ldquo;I found out from TransUnion and Equifax that my info was involved in <strong>DATA BREACHES</strong>&rdquo;</p>
                    </div>
                  </div>
                </div>
                
                {/* Tips Section */}
                <div style={{ padding: '20px 25px', fontFamily: 'Arial, sans-serif' }}>
                  <h3 style={{ color: '#059669', margin: '0 0 15px', fontSize: '16px' }}>💡 Pro Tips:</h3>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#555', fontSize: '14px' }}>
                    <li style={{ marginBottom: '8px' }}>Re-dispute remaining items <strong>every 15 days</strong></li>
                    <li style={{ marginBottom: '8px' }}>If not satisfied with overseas reps, <strong>call again</strong></li>
                    <li style={{ marginBottom: '8px' }}>Keep a <strong>log</strong> of each call (date, time, rep name, confirmation #)</li>
                    <li>Be <strong>polite but persistent</strong> — you have rights under the FCRA!</li>
                  </ul>
                </div>
                
                {/* Footer */}
                <div style={{ background: '#1e3a5f', padding: '20px', textAlign: 'center' }}>
                  <p style={{ color: '#a5d6ff', margin: 0, fontSize: '12px' }}>© 2026 Clear All Credit Repair</p>
                  <p style={{ margin: '10px 0 0' }}><a href="https://www.clearallcreditrepairs.com" style={{ color: '#60a5fa', textDecoration: 'none', fontSize: '14px', fontWeight: 'bold' }}>www.clearallcreditrepairs.com</a></p>
                  <p style={{ color: '#64748b', margin: '10px 0 0', fontSize: '11px' }}>You&apos;re receiving this because you&apos;re enrolled in our credit repair program.</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-zinc-900 border-t border-zinc-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-400">
                  <BellRing className="w-4 h-4 inline mr-1 text-emerald-400" />
                  All <strong>{quickReminders.length}</strong> active clients receive this email every Friday
                </p>
                <button
                  onClick={() => setShowStep3Preview(false)}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white font-medium transition-colors"
                >
                  Got It
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Thursday Night Heads-Up Email Preview Modal */}
      {showThursdayPreview && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-violet-600 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🌙</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Thursday Night Heads-Up Email</h3>
                    <p className="text-purple-100 text-sm">Sent every Thursday at 7:00 PM Pacific</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowThursdayPreview(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Email Content Preview */}
            <div className="flex-1 overflow-y-auto p-6 bg-zinc-800">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-lg mx-auto">
                {/* Email Header */}
                <div style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)', padding: '25px', textAlign: 'center' }}>
                  <h2 style={{ color: 'white', margin: 0, fontSize: '22px', fontWeight: 'bold' }}>🌙 Heads Up for Tomorrow!</h2>
                  <p style={{ color: '#e9d5ff', marginTop: '10px', fontSize: '14px' }}>Step 3 needs to be completed TOMORROW</p>
                </div>
                
                {/* Email Body */}
                <div style={{ padding: '20px 25px', fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#333' }}>
                  <p style={{ margin: '0' }}>Hi <strong>[Client Name]</strong>,</p>
                  <p style={{ marginTop: '15px', color: '#555' }}>Just a friendly reminder that <strong style={{ color: '#7c3aed' }}>TOMORROW IS FRIDAY</strong> — which means it&apos;s time to complete <strong>Step 3</strong> of your credit repair process!</p>
                </div>
                
                {/* Reminder Card */}
                <div style={{ margin: '0 20px 20px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <div style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)', padding: '15px 20px', color: 'white' }}>
                    <span style={{ background: 'rgba(255,255,255,0.25)', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', marginRight: '10px' }}>REMINDER</span>
                    <span style={{ fontSize: '20px', marginRight: '8px' }}>📅</span>
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>PREPARE FOR TOMORROW</span>
                  </div>
                  <div style={{ padding: '20px', fontSize: '14px', color: '#444' }}>
                    <p style={{ margin: '0 0 15px' }}><strong>Tomorrow morning, you&apos;ll need to call all three credit bureaus</strong> to check the status of your disputes.</p>
                    
                    <div style={{ background: '#f3f4f6', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                      <p style={{ margin: '0', fontSize: '13px', color: '#555' }}><strong>📋 What to have ready:</strong></p>
                      <ul style={{ margin: '10px 0 0', paddingLeft: '20px', color: '#666' }}>
                        <li>Your confirmation numbers from previous calls</li>
                        <li>A notepad to write down new information</li>
                        <li>About 30-45 minutes of quiet time</li>
                      </ul>
                    </div>
                    
                    <div style={{ background: '#fef3c7', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                      <p style={{ margin: '0', fontSize: '13px', color: '#92400e' }}><strong>💡 Pro Tip:</strong> The best time to call is usually between <strong>9 AM - 11 AM</strong> when wait times are shorter!</p>
                    </div>
                  </div>
                </div>
                
                {/* Phone Numbers Preview */}
                <div style={{ padding: '20px 25px', fontFamily: 'Arial, sans-serif' }}>
                  <h3 style={{ color: '#7c3aed', margin: '0 0 15px', fontSize: '16px' }}>📞 Numbers you&apos;ll call tomorrow:</h3>
                  <div style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px' }}>
                    <p style={{ margin: '0 0 8px', color: '#059669' }}><strong>TransUnion:</strong> 1-800-680-7289</p>
                    <p style={{ margin: '0 0 8px', color: '#dc2626' }}><strong>Equifax:</strong> 1-866-349-5191</p>
                    <p style={{ margin: '0', color: '#2563eb' }}><strong>Experian:</strong> 1-800-493-1058</p>
                  </div>
                </div>
                
                {/* Footer */}
                <div style={{ background: '#1e3a5f', padding: '20px', textAlign: 'center' }}>
                  <p style={{ color: '#fcd34d', margin: '0', fontSize: '14px', fontWeight: 'bold' }}>We&apos;ll send you another reminder tomorrow morning! 🌅</p>
                  <p style={{ color: '#a5d6ff', margin: '15px 0 0', fontSize: '12px' }}>© 2026 Clear All Credit Repair</p>
                  <p style={{ margin: '10px 0 0' }}><a href="https://www.clearallcreditrepairs.com" style={{ color: '#60a5fa', textDecoration: 'none', fontSize: '14px', fontWeight: 'bold' }}>www.clearallcreditrepairs.com</a></p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-zinc-900 border-t border-zinc-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-400">
                  <BellRing className="w-4 h-4 inline mr-1 text-purple-400" />
                  All <strong>{quickReminders.length}</strong> active clients receive this email every Thursday night
                </p>
                <button
                  onClick={() => setShowThursdayPreview(false)}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-medium transition-colors"
                >
                  Got It
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Split Payments Section (Feb 2026) ─────────────────────────────────────
// A collapsible editor that lets the user break a single loan into multiple
// scheduled payment dates (e.g. half on Aug 15, half on Sep 1).  The server-
// side reminder job fires ONE day-before reminder per unpaid split to BOTH
// the borrower and the lender.  Total of all splits should ~equal the
// amount_return on the loan — a soft warning is shown if it doesn't.
function SplitPaymentsSection({ splits, amountReturn, defaultDueDate, onChange, loanNumber, onToggleServerSide }) {
  const enabled = Array.isArray(splits) && splits.length > 0;
  const total = (splits || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const remaining = Math.max(0, (amountReturn || 0) - total);
  const diff = Math.round(((amountReturn || 0) - total) * 100) / 100;
  const overBy = Math.round((total - (amountReturn || 0)) * 100) / 100;

  const uid = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `sp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const enableSplits = () => {
    // Seed with 2 splits equally divided on default due date + 15 days.
    const base = defaultDueDate || new Date().toISOString().slice(0, 10);
    const nextDate = (() => {
      try {
        const d = new Date(`${base}T00:00:00`);
        d.setDate(d.getDate() + 15);
        return d.toISOString().slice(0, 10);
      } catch {
        return base;
      }
    })();
    const half = amountReturn ? Math.round((amountReturn / 2) * 100) / 100 : 0;
    onChange([
      { id: uid(), due_date: base, amount: half, note: "", paid: false },
      { id: uid(), due_date: nextDate, amount: (amountReturn || 0) - half, note: "", paid: false },
    ]);
  };

  const disableSplits = () => {
    if (splits.length && !window.confirm("Remove ALL split payments and go back to a single lump-sum due date? This can't be undone.")) return;
    onChange([]);
  };

  const addRow = () => {
    // New split defaults to the LAST split's due date + 15 days
    const last = splits[splits.length - 1];
    const seed = last?.due_date || defaultDueDate || new Date().toISOString().slice(0, 10);
    let d;
    try {
      const parsed = new Date(`${seed}T00:00:00`);
      parsed.setDate(parsed.getDate() + 15);
      d = parsed.toISOString().slice(0, 10);
    } catch {
      d = seed;
    }
    onChange([
      ...splits,
      { id: uid(), due_date: d, amount: Math.max(0, remaining), note: "", paid: false },
    ]);
  };

  const updateRow = (idx, patch) => {
    const next = splits.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange(next);
  };

  const removeRow = (idx) => {
    const next = splits.filter((_, i) => i !== idx);
    onChange(next);
  };

  const togglePaid = async (idx) => {
    const target = !splits[idx].paid;
    updateRow(idx, { paid: target, paid_at: target ? new Date().toISOString() : null });
    // Fire-and-forget server toggle so the reminder job is aware immediately.
    // The full form save on "Save Changes" will reconcile anything else.
    if (loanNumber && splits[idx].id && onToggleServerSide) {
      onToggleServerSide(splits[idx].id, target);
    }
  };

  return (
    <div className="rounded-xl border border-violet-700/40 bg-gradient-to-br from-violet-950/30 to-fuchsia-950/20 p-3 sm:p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-violet-500/20 border border-violet-500/40 flex items-center justify-center flex-shrink-0">
            <span className="text-violet-300 text-sm font-bold">⇄</span>
          </div>
          <div className="min-w-0">
            <h4 className="text-white text-sm font-bold flex items-center gap-1.5">
              Split Payments
              {enabled && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-violet-500/25 text-violet-200 border border-violet-500/40">
                  {splits.length} split{splits.length === 1 ? "" : "s"}
                </span>
              )}
            </h4>
            <p className="text-[11px] text-violet-300/80 leading-snug">
              {enabled
                ? "Each split gets its own day-before reminder to you + the borrower."
                : "Turn this on if the loan is paid in multiple installments."}
            </p>
          </div>
        </div>
        {!enabled ? (
          <button
            type="button"
            onClick={enableSplits}
            className="px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold flex-shrink-0 flex items-center gap-1"
            data-testid="split-payments-enable"
          >
            + Enable
          </button>
        ) : (
          <button
            type="button"
            onClick={disableSplits}
            className="px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[11px] font-semibold flex-shrink-0"
            data-testid="split-payments-disable"
          >
            Disable
          </button>
        )}
      </div>

      {enabled && (
        <>
          <div className="space-y-2 mt-2" data-testid="split-payments-list">
            {splits.map((s, i) => (
              <div
                key={s.id || i}
                className={`rounded-lg border p-2.5 flex items-center gap-2 flex-wrap ${
                  s.paid
                    ? "border-emerald-500/40 bg-emerald-500/10"
                    : "border-zinc-700 bg-zinc-900/60"
                }`}
                data-testid={`split-row-${i}`}
              >
                <button
                  type="button"
                  onClick={() => togglePaid(i)}
                  className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all active:scale-90 ${
                    s.paid
                      ? "border-emerald-400 bg-emerald-500 text-white shadow-md shadow-emerald-500/30"
                      : "border-zinc-500 hover:border-emerald-400 hover:bg-emerald-500/10"
                  }`}
                  title={s.paid ? "Mark unpaid" : "Mark this split paid"}
                  aria-label={s.paid ? "Mark unpaid" : "Mark paid"}
                  data-testid={`split-toggle-${i}`}
                >
                  {s.paid ? <span className="text-[13px] font-bold">✓</span> : null}
                </button>
                <div className="flex flex-col flex-1 min-w-[110px]">
                  <label className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-0.5">Due</label>
                  <input
                    type="date"
                    value={s.due_date || ""}
                    onChange={(e) => updateRow(i, { due_date: e.target.value })}
                    className={`px-2 py-1.5 bg-zinc-800 border rounded-md text-white text-xs ${s.paid ? "border-emerald-700/40" : "border-zinc-600"}`}
                    data-testid={`split-date-${i}`}
                  />
                </div>
                <div className="flex flex-col w-24">
                  <label className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-0.5">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={s.amount ?? ""}
                    onChange={(e) => updateRow(i, { amount: parseFloat(e.target.value) || 0 })}
                    className={`px-2 py-1.5 bg-zinc-800 border rounded-md text-white text-xs tabular-nums ${s.paid ? "border-emerald-700/40" : "border-zinc-600"}`}
                    data-testid={`split-amount-${i}`}
                  />
                </div>
                <div className="flex flex-col flex-1 min-w-[120px]">
                  <label className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-0.5">Note (optional)</label>
                  <input
                    type="text"
                    value={s.note || ""}
                    onChange={(e) => updateRow(i, { note: e.target.value })}
                    placeholder="e.g. First half"
                    maxLength={280}
                    className="px-2 py-1.5 bg-zinc-800 border border-zinc-600 rounded-md text-white text-xs"
                    data-testid={`split-note-${i}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="w-8 h-8 rounded-md bg-red-500/15 hover:bg-red-500/30 border border-red-500/40 text-red-300 hover:text-red-100 flex items-center justify-center flex-shrink-0"
                  title="Remove this split"
                  aria-label="Remove split"
                  data-testid={`split-remove-${i}`}
                >
                  <span className="text-sm">×</span>
                </button>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
            <button
              type="button"
              onClick={addRow}
              className="px-3 py-1.5 rounded-lg bg-violet-500/20 hover:bg-violet-500/30 text-violet-200 border border-violet-500/40 text-xs font-semibold"
              data-testid="split-add"
            >
              + Add Split
            </button>
            <div className="text-[11px] text-right">
              <div className="text-zinc-500">
                Total scheduled:{" "}
                <span className="text-white font-mono font-bold">
                  ${total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                {" of "}
                <span className="text-zinc-300 font-mono">
                  ${(amountReturn || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              {Math.abs(diff) > 0.01 && (
                <div className={`mt-0.5 font-semibold ${diff > 0 ? "text-amber-300" : "text-red-300"}`}>
                  {diff > 0
                    ? `Short by $${diff.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : `Over by $${overBy.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </div>
              )}
              {Math.abs(diff) <= 0.01 && (
                <div className="mt-0.5 text-emerald-300 font-semibold">✓ Splits total the full return amount</div>
              )}
            </div>
          </div>
          <p className="text-[10px] text-violet-300/70 mt-2 leading-snug">
            📧 A reminder email fires to <strong>you and the borrower</strong> the day before each split's due date at 9 PM Pacific
            (and a day-of at 9 AM Pacific).
          </p>
        </>
      )}
    </div>
  );
}

// ─── LoanSplitsInline (Feb 2026) ────────────────────────────────────────────
// Compact schedule preview shown INLINE on the loan card, right under the
// progress bar.  Highlights the next-up split (unpaid + earliest date),
// shows a per-row paid checkbox, and calls onToggle(splitId, targetPaid).
function LoanSplitsInline({ loan, onToggle }) {
  const splits = Array.isArray(loan.split_payments) ? loan.split_payments : [];
  if (!splits.length) return null;
  const totalCount = splits.length;
  const paidCount = splits.filter((s) => s.paid).length;
  const nextUp = splits.find((s) => !s.paid);
  const scheduledSum = splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const paidSum = splits.filter((s) => s.paid).reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const percent = scheduledSum > 0 ? Math.round((paidSum / scheduledSum) * 100) : 0;

  return (
    <div className="mt-4 rounded-xl border border-violet-500/40 bg-gradient-to-br from-violet-950/25 to-fuchsia-950/15 p-3" data-testid={`loan-splits-inline-${loan.loan_number}`}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-violet-500/20 border border-violet-500/40 flex items-center justify-center flex-shrink-0">
            <span className="text-violet-300 text-xs font-bold">⇄</span>
          </div>
          <div className="min-w-0">
            <p className="text-violet-200 text-xs font-bold uppercase tracking-wide leading-none">
              Split Payments · {paidCount}/{totalCount} paid
            </p>
            {nextUp && (
              <p className="text-violet-300/80 text-[11px] mt-0.5 truncate">
                Next up: <strong className="text-white">${(parseFloat(nextUp.amount) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> on <strong className="text-white">{nextUp.due_date}</strong>
              </p>
            )}
          </div>
        </div>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-violet-500/25 text-violet-200 border border-violet-500/40">
          {percent}%
        </span>
      </div>
      <div className="space-y-1.5">
        {splits.map((s, i) => (
          <div
            key={s.id || i}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${
              s.paid
                ? "border-emerald-500/40 bg-emerald-500/10"
                : (nextUp && s.id === nextUp.id
                    ? "border-amber-500/40 bg-amber-500/10"
                    : "border-zinc-700/60 bg-zinc-900/40")
            }`}
            data-testid={`inline-split-row-${loan.loan_number}-${i}`}
          >
            <button
              type="button"
              onClick={() => onToggle && onToggle(s.id, !s.paid)}
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all active:scale-90 ${
                s.paid
                  ? "border-emerald-400 bg-emerald-500 text-white"
                  : "border-zinc-500 hover:border-emerald-400 hover:bg-emerald-500/15"
              }`}
              aria-label={s.paid ? "Mark unpaid" : "Mark paid"}
              data-testid={`inline-split-toggle-${loan.loan_number}-${i}`}
            >
              {s.paid ? <span className="text-[11px] font-bold">✓</span> : null}
            </button>
            <span className={`text-xs font-mono ${s.paid ? "text-emerald-300 line-through" : "text-zinc-300"}`}>
              {s.due_date}
            </span>
            <span className={`text-xs font-mono font-bold tabular-nums ml-auto ${s.paid ? "text-emerald-300 line-through" : "text-white"}`}>
              ${(parseFloat(s.amount) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            {s.note ? (
              <span className={`text-[10px] italic truncate max-w-[110px] ${s.paid ? "text-emerald-400/70" : "text-zinc-500"}`}>
                {s.note}
              </span>
            ) : null}
            {nextUp && s.id === nextUp.id && !s.paid && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-200 border border-amber-500/50 uppercase tracking-wider">
                Next
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-violet-300/60 mt-2 leading-snug">
        📧 Reminder emails fire to you + the borrower the day before each unpaid split.
      </p>
    </div>
  );
}

/**
 * SignedNotesChip — small "📎 N notes" pill that appears on every loan row.
 * Clicking it stops the row-click propagation and opens an inline manager
 * for uploading / viewing / downloading / emailing / deleting signed
 * promissory notes attached to that loan.
 */
function SignedNotesChip({ loan, onChanged }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [open, setOpen] = React.useState(false);
  const [notes, setNotes] = React.useState(loan.signed_notes || []);
  const [uploading, setUploading] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null);
  const fileRef = React.useRef(null);
  const count = notes.length;

  const refresh = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/loans/${loan.loan_number}/signed-notes`);
      setNotes(res.data.notes || []);
    } catch { /* ignore */ }
  }, [API, loan.loan_number]);

  const upload = async (e) => {
    e.stopPropagation();
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/api/loans/${loan.loan_number}/signed-notes/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      await refresh();
      if (onChanged) onChanged();
      toast.success(`${files.length} note${files.length !== 1 ? 's' : ''} attached`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (noteId, e) => {
    e.stopPropagation();
    if (!window.confirm("Remove this signed note? This can't be undone.")) return;
    setBusyId(noteId);
    try {
      await axios.delete(`${API}/api/loans/${loan.loan_number}/signed-notes/${noteId}`);
      await refresh();
      if (onChanged) onChanged();
      toast.success("Removed");
    } catch (err) {
      toast.error("Delete failed");
    } finally { setBusyId(null); }
  };

  const sendEmail = async (noteId, e) => {
    e.stopPropagation();
    const to = window.prompt("Email address to send this signed note to:");
    if (!to || !to.includes("@")) return;
    setBusyId(noteId);
    try {
      await axios.post(`${API}/api/loans/${loan.loan_number}/signed-notes/${noteId}/email`, { to_email: to });
      toast.success(`Emailed to ${to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email failed");
    } finally { setBusyId(null); }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); refresh(); }}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold border transition ${
          count > 0
            ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30"
            : "bg-zinc-800/60 border-zinc-700 text-zinc-400 hover:border-zinc-500"
        }`}
        title={count > 0 ? "View signed promissory notes" : "Attach a signed promissory note"}
        data-testid={`signed-notes-chip-${loan.loan_number}`}
      >
        📎 {count > 0 ? `${count} signed note${count !== 1 ? 's' : ''}` : "Attach signed note"}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          data-testid={`signed-notes-modal-${loan.loan_number}`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-xl w-full max-h-[90vh] overflow-y-auto rounded-2xl border border-emerald-500/40 bg-zinc-950 p-5 shadow-2xl"
          >
            <div className="flex items-center justify-between gap-2 mb-3">
              <div>
                <h3 className="text-lg font-black text-white">Signed Promissory Notes</h3>
                <p className="text-xs text-zinc-400">
                  Loan #{loan.loan_number} · {loan.borrower_name || 'Unknown borrower'}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Upload */}
            <div className="rounded-xl border-2 border-dashed border-emerald-500/40 bg-emerald-500/5 p-4 mb-4">
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/*"
                multiple
                onChange={upload}
                className="hidden"
                data-testid={`signed-notes-upload-input-${loan.loan_number}`}
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                disabled={uploading}
                className="w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-bold disabled:opacity-60"
                data-testid={`signed-notes-upload-btn-${loan.loan_number}`}
              >
                {uploading ? "Uploading…" : "📎 Upload signed promissory note (PDF or photo)"}
              </button>
              <p className="text-[10px] text-zinc-500 text-center mt-2">
                Accepts PDF or camera photo · Multiple files supported
              </p>
            </div>

            {/* List */}
            {notes.length === 0 ? (
              <p className="text-center text-sm text-zinc-500 italic py-6">
                No signed notes on file yet. Upload one above.
              </p>
            ) : (
              <div className="space-y-2">
                {notes.map((n) => (
                  <div key={n.id}
                    className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-3"
                    data-testid={`signed-note-row-${n.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-white truncate">
                          {n.filename}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          {(n.size / 1024).toFixed(1)} KB · Uploaded {n.uploaded_at ? new Date(n.uploaded_at).toLocaleString() : '—'}
                        </div>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        <a
                          href={`${API}/api/loans/${loan.loan_number}/signed-notes/${n.id}/download`}
                          target="_blank" rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-cyan-500 hover:bg-cyan-400 text-white text-[11px] font-bold"
                          data-testid={`signed-note-view-${n.id}`}
                        >View</a>
                        <a
                          href={`${API}/api/loans/${loan.loan_number}/signed-notes/${n.id}/download`}
                          download={n.filename}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white text-[11px] font-bold"
                          data-testid={`signed-note-download-${n.id}`}
                        >Download</a>
                        <button
                          onClick={(e) => sendEmail(n.id, e)}
                          disabled={busyId === n.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-orange-500 hover:bg-orange-400 text-white text-[11px] font-bold disabled:opacity-60"
                          data-testid={`signed-note-email-${n.id}`}
                        >Email</button>
                        <button
                          onClick={(e) => del(n.id, e)}
                          disabled={busyId === n.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/70 hover:bg-red-500 text-white text-[11px] font-bold disabled:opacity-60"
                          data-testid={`signed-note-delete-${n.id}`}
                        >✕</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}



